/**
 * Dashboard Component
 * Extracted from App.jsx - Phase 1 Refactoring
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import MuiButton from "@mui/material/Button";
import MuiIconButton from "@mui/material/IconButton";
import MuiDialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import MuiAlert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import { FONT, MONO } from "../theme/tokens";
import { api, v, getToken, getSseToken, formatDate, formatDuration, parseODataDate, toSuiteUrl, monitoringLink } from "../utils/helpers";
import { erkenneTyp, formatiere, faerbe } from "../utils/payloadView";
import { StatusBadge, Btn, Card } from "../components/ui";
import IntegrationContent from "../IntegrationContent";
import PipelineAssessment from "./PipelineAssessment";
import useMonitorRunPages from "../hooks/useMonitorRunPages";
import useErrorAnalysis from "../hooks/useErrorAnalysis";
import { ErrorAnalysisMenu, ErrorAnalysisPanel } from "../components/ErrorAnalysis";

// Backward-compatible aliases
const fD = formatDate;
const fDur = formatDuration;

// Number formatting helper - k/M suffixes. Der Dezimaltrenner folgt der Sprache:
// "66,3k" ist im Englischen die falsche ZAHL, nicht nur die falsche Schreibweise.
const fmtZahl = (n, komma) => {
  if (n == null || isNaN(n)) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1).replace('.', komma) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1).replace('.', komma) + 'k';
  return n.toString();
};

// Konzept-B Route-Renderer: Sender, ArrowRightAlt, Receiver
const RouteLink = ({from, to}) => (<>
  <span className="iio-route-from">{from || "-"}</span>
  <span className="iio-route-link"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"/></svg></span>
  <span className="iio-route-to">{to || "-"}</span>
</>);

// Chevron-Flow (CI aus Transport-Schleuse). static = dichte Liste (Performance), wave = arme Kontexte.
const ChevFlow = ({variant="static", small=false}) => (
  <span className={`iio-mon2-chevflow iio-mon2-chevflow--${variant==="wave"?"wave":"static"}${small?" iio-mon2-chevflow--sm":""}`} aria-hidden="true">
    <i/><i/>{variant==="wave"?<i/>:null}
  </span>
);


// Run-Step CPI-Status -> Timeline-Modifier
const stepStatusMod=(st)=>{
  const s=(st||"").toUpperCase();
  if(s==="FAILED"||s==="ERROR") return "err";
  if(s==="COMPLETED"||s==="OK") return "ok";
  if(s==="RETRY"||s==="ESCALATED"||s==="PROCESSING") return "active";
  // Unbekannter Status darf NICHT als Erfolg erscheinen - neutral statt gruen.
  return "idle";
};

// relative Zeit "vor X" aus Timestamp (ms oder OData-String)
// Datenalter als Dauer - der Entwickler braucht "wie alt", nicht "wann".
// Die Mehrzahl kommt aus dem Katalog, nicht aus einer Bedingung im Code.
const relAge=(ms,t)=>{
  if(ms==null) return t("zeit.unbekannt");
  const s=Math.max(0,Math.round(ms/1000));
  if(s<10) return t("gemeinsam:zeit.geradeEben");
  if(s<60) return t("zeit.vorSek",{count:s});
  const m=Math.round(s/60);
  if(m<60) return t("zeit.vorMin",{count:m});
  const h=Math.floor(m/60);
  return t("zeit.vorStdMin",{std:h,min:m%60});
};
const relTime=(ts,t)=>{
  if(!ts) return "-";
  const ms=typeof ts==="number"?ts:(parseODataDate(ts)||0);
  if(!ms) return "-";
  const sec=Math.max(0,Math.floor((Date.now()-ms)/1000));
  if(sec<60) return t("zeit.vorSek",{count:sec});
  if(sec<3600) return t("zeit.vorMin",{count:Math.floor(sec/60)});
  if(sec<86400) return t("zeit.vorStd",{count:Math.floor(sec/3600)});
  return t("zeit.vorTag",{count:Math.floor(sec/86400)});
};

// Konzept-B Status -> Mrow-Bar-Modifier
const mrowBarMod = (st) => {
  if (st === "FAILED" || st === "ERROR") return "iio-mon-mrow-bar--crit";
  if (st === "RETRY" || st === "ESCALATED") return "iio-mon-mrow-bar--warn";
  if (st === "COMPLETED") return "iio-mon-mrow-bar--ok";
  return "iio-mon-mrow-bar--idle";
};

// Konzept-B Status -> Code-Anzeige.
// WICHTIG: Der Status-WERT (FAILED, COMPLETED, ESCALATED, RETRY) kommt aus der
// SAP-Schnittstelle und bleibt woertlich. Diese Funktion liefert deshalb nur noch
// die Zuordnung: CSS-Klasse + Katalog-Schluessel. Den sichtbaren Text setzt die
// Anzeige ueber t("status.<schluessel>").
const mrowCodeMod = (st) => {
  if (st === "FAILED" || st === "ERROR") return {cls: "iio-mon-mrow-code--crit", schluessel: "fehler"};
  // ESCALATED nicht mehr als "RETRY" beschriften: seit die Ansicht ESCALATED als eigene
  // Rubrik kennt, waere derselbe Lauf im ESCALATED-Reiter mit RETRY gestempelt.
  if (st === "ESCALATED") return {cls: "iio-mon-mrow-code--warn", schluessel: "eskaliert"};
  if (st === "RETRY") return {cls: "iio-mon-mrow-code--warn", schluessel: "wiederholung"};
  if (st === "COMPLETED") return {cls: "iio-mon-mrow-code--ok", schluessel: "ok"};
  if (st === "PROCESSING") return {cls: "iio-mon-mrow-code--warn", schluessel: "verarbeitung"};
  // Unbekannter Status: der rohe Wert des Tenants, unuebersetzt.
  return {cls: "iio-mon-mrow-code--idle", schluessel: null, roh: st || "-"};
};

// Pipeline message detection
const isPipelineMsg = (msg) => {
  const name = (msg.IntegrationFlowName || msg.iFlowName || msg.name || '').toLowerCase();
  const sender = (msg.Sender || '').toUpperCase();
  const receiver = (msg.Receiver || '').toUpperCase();
  return name.includes('pipeline') ||
         /PIPQ\d+|PIPX\d+/.test(sender) ||
         /PIPQ\d+|PIPX\d+/.test(receiver);
};

// Button styles as module-level constants - stable references, no re-creation on each render
const btnPrimary={display:"inline-flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:8,background:"var(--accent)",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",border:"none",fontFamily:FONT,textDecoration:"none"};
const btnOutline={display:"inline-flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:8,background:"transparent",color:"var(--accent)",fontSize:12,fontWeight:600,cursor:"pointer",border:"1px solid rgba(75,127,250,.4)",fontFamily:FONT,textDecoration:"none"};
const btnGhost={display:"inline-flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:8,background:"var(--surface-2)",color:"var(--text-1)",fontSize:12,fontWeight:600,cursor:"pointer",border:"1px solid var(--border)",fontFamily:FONT,textDecoration:"none"};
const btnAi={display:"inline-flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:8,background:"linear-gradient(135deg,var(--accent-light),var(--accent))",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",border:"none",fontFamily:FONT};
const parseOD = parseODataDate;
const SB = StatusBadge;

const Dashboard=({instance,onBack,targetFlowName:initialTargetFlow,onClearTarget,targetTab,onClearTargetTab})=>{
  const {t,i18n}=useTranslation(["monitor","gemeinsam"]);
  // Zahlen, Datum und Uhrzeit folgen der gewaehlten Sprache. Ein fest eingestelltes
  // "de-DE" verfaelscht im Englischen die Zahl selbst: 16.044 liest sich dort als
  // sechzehn Komma null vier vier.
  const gebiet=i18n.language==="en"?"en-GB":"de-DE";
  const zahl=(x)=>(x??0).toLocaleString(gebiet);
  const fmt=(n)=>fmtZahl(n,i18n.language==="en"?".":",");
  // Anzeige zum Status-Wert. Der Wert selbst wird nirgends angefasst.
  const codeLabel=(c)=>(c&&c.schluessel?t(`status.${c.schluessel}`):(c&&c.roh)||"-");
  // Meldungen aus Callbacks: der Verweis bleibt stabil, damit weder ein
  // Abhaengigkeits-Array noch ein Ladevorgang von der Sprache abhaengt.
  const tRef=useRef(t);tRef.current=t;
  // Read URL on mount to support direct deep-linking to content tab / specific iFlow
  const _urlP=window.location.pathname.split("/").filter(Boolean);
  const _urlQ=new URLSearchParams(window.location.search);
  const _flowFromUrl=_urlQ.get("flow")||(_urlP[4]?decodeURIComponent(_urlP[4]):null);
  const[tab,setTab]=useState((initialTargetFlow||_flowFromUrl)?"content":(_urlP[3]==="content"?"content":"monitor"));
  const[localTargetFlow,setLocalTargetFlow]=useState(initialTargetFlow||_flowFromUrl);
  useEffect(()=>{if(initialTargetFlow){setLocalTargetFlow(initialTargetFlow);setTab("content");}},[initialTargetFlow]);
  useEffect(()=>{if(targetTab?.tab){setTab(targetTab.tab);onClearTargetTab&&onClearTargetTab();}},[targetTab?.nonce]);
  // KI-Features (Analyse/Bulk/Chat) erfordern aktivierte KI-Capabilities auf der Instanz.
  const aiEnabled=!!instance?.aiCapabilities?.enabled;
  const[view,setView]=useState("overview");const[iflows,setIflows]=useState([]);const[sel,setSel]=useState(null);
  const[logs,setLogs]=useState([]);const[sf,setSf]=useState("ALL");const[gb,setGb]=useState("none");const[search,setSearch]=useState("");
  const[exp,setExp]=useState(null);const[loading,setLoading]=useState(true);const[error,setError]=useState(null);const[hdrs,setHdrs]=useState({});const hdrsRef=useRef({});
  const[refreshInterval,setRefreshInterval]=useState(0);const[lastRefresh,setLastRefresh]=useState(null);
  const[hiddenFlows,setHiddenFlows]=useState([]);const[showFilter,setShowFilter]=useState(false);
  const selRef=useRef(sel);selRef.current=sel;const viewRef=useRef(view);viewRef.current=view;const tabRef=useRef(tab);tabRef.current=tab;

  // === Message Monitor state ===
  const[monitorData,setMonitorData]=useState(null);const[monitorLoading,setMonitorLoading]=useState(false);
  const[monitorSort,setMonitorSort]=useState("failed_desc");
  // Correlation-Ketten: globale Suche per Correlation-ID + "Abhängigkeiten"-Popup
  const[corrSearch,setCorrSearch]=useState(null); // {cid,loading,error,flows:[],byFlow:{},count,capped}
  const[corrDeps,setCorrDeps]=useState(null);     // {cid,loading,error,data}
  const corrActiveRef=useRef(false);
  const[monitorSearch,setMonitorSearch]=useState("");
  // Laufzeit-Umschalter im Monitor-Kopf: "cloud" oder die Id eines Edge-Monitoring-Eintrags
  // der Instanz (Einstellungen, Reiter Edge). Die Datenanbindung der Edges folgt in einem
  // spaeteren Schritt - bis dahin zeigt der Monitor bei gewaehlter Edge bewusst keine Zahlen,
  // denn Cloud-Werte unter einem Edge-Etikett waeren eine falsche Aussage.
  const[monitorRuntime,setMonitorRuntime]=useState("cloud");
  // Die Laufzeit-Wahl zeigt NUR aufgesetzte Monitoring-Zugaenge (Einstellungen > Edge >
  // Edge Monitoring). Die Deploy-Ziele (runtimeLocations) gehoeren bewusst NICHT hinein
  // (Davids Ansage 18.08.): sie sind Transportziele ohne Monitoring-Zugang - ein Eintrag
  // im Dropdown wuerde eine Edge behaupten, die hier nie Daten liefern kann.
  const edgeRuntimes=(Array.isArray(instance?.edgeMonitoring)?instance.edgeMonitoring:[]).filter(e=>e&&e.id&&e.name);
  // Faellt automatisch auf die Cloud zurueck, wenn der gewaehlte Eintrag geloescht wurde.
  const aktiveEdge=monitorRuntime!=="cloud"?(edgeRuntimes.find(e=>e.id===monitorRuntime)||null):null;
  useEffect(()=>{setMonitorRuntime("cloud");},[instance?.id]);
  const[monitorDeepSearch,setMonitorDeepSearch]=useState("");
  // Suchindex erst auf Anforderung: Der Aufbau (bis 5000 Zeilen + Header je GUID) lief bisher
  // bei JEDEM iFlow-Aufklappen - jetzt erst, wenn die Tiefensuche fokussiert wird.
  const[iflowIndexWanted,setIflowIndexWanted]=useState(false);
  // Serverseitige In-iFlow-Suche (durchsucht auch Custom Header, die nicht in der Liste stehen)
  // In-iFlow Such-INDEX: Runs des Fensters + Header-Map (guid->[{n,v}]). Wird beim Oeffnen einmal
  // im Hintergrund aufgebaut + serverseitig gecacht. Suche laeuft danach in-memory, kein Call beim Tippen.
  const[iflowIndex,setIflowIndex]=useState({runs:[],headers:{},loading:false,building:false,indexed:0,total:0,error:null,runsCapped:false,flow:null});
  const[monitorSearchMode,setMonitorSearchMode]=useState("iflow"); // "iflow" | "content"
  const[contentSearchData,setContentSearchData]=useState({messages:[],count:0,loading:false,error:null});
  const[statFilter,setStatFilter]=useState(null);
  // Aufriss der Erfolgs-KPI: Klick auf die Zahl klappt die Zusammensetzung nach Status auf
  // (Completed/Fehler/Retry/... wie die All-Artifacts-Zeile im SAP-Monitor).
  const[kpiAufriss,setKpiAufriss]=useState(false);
  // Angeklickte Aufriss-Zelle: ihre Erklaerung erscheint als eigene Zeile unter der Tabelle.
  // Bewusst per Klick statt Hover (Davids Wunsch 17.08.) und als eigene Zeile statt Popup:
  // ein schwebendes Popup wuerde der Scroll-Kontext der Tabelle clippen.
  const[aufrissSel,setAufrissSel]=useState(null);
  const[monitorExpanded,setMonitorExpanded]=useState(null);
  const[monitorLogs,setMonitorLogs]=useState([]);const[monitorLogsLoading,setMonitorLogsLoading]=useState(false);const[monitorLogCount,setMonitorLogCount]=useState(0);
  const[monitorLogFilter,setMonitorLogFilter]=useState("ALL");
  const[monitorErrorDetail,setMonitorErrorDetail]=useState(null);const[monitorLogHeaders,setMonitorLogHeaders]=useState({});
  const errorAnalysis = useErrorAnalysis(instance.id, aiEnabled);
  const[retryConfirm,setRetryConfirm]=useState(false);
  const[refreshSpin,setRefreshSpin]=useState(false);
  const[mdetailTab,setMdetailTab]=useState({});// guid -> "overview"|"headers"|"error"|"attachments"|"adapter"
  const[attachmentModal,setAttachmentModal]=useState(null);
  const[permissions,setPermissions]=useState({transport:true,content:true,alerting:true,isOwner:true});
  // Grouping & Pagination (PI/PO Monitor style)
  const[groupBy,setGroupBy]=useState("iflow"); // "iflow"|"status"
  const[msgPage,setMsgPage]=useState(1);
  const MSG_PAGE_SIZE=50;
  // Pipeline retry state
  const[retryLoading,setRetryLoading]=useState(null);
  const[retryResult,setRetryResult]=useState({});
  // Modul 4: Pipeline Monitor Modus
  const[pipelineMode,setPipelineMode]=useState(false);
  const[pipelineExpanded,setPipelineExpanded]=useState(null);
  const[pipelineHealthOpen,setPipelineHealthOpen]=useState(false);

  // === NEU: Pins (pro User + Instanz), Run-Steps-Timeline, Kebab, Drawer-Tab ===
  const[monitorPins,setMonitorPins]=useState([]); // Array von iFlow-Namen
  // Anpassbare Monitor-Ansicht: Umgang mit dem SAP-Status ESCALATED.
  // "aus" (Vorgabe) | "spalte" (eigene Spalte, abhakbar) | "fehler" (in die Fehlerzahl)
  const[eskModus,setEskModus]=useState("aus");
  const[anpOffen,setAnpOffen]=useState(false);
  const[pinsHidden,setPinsHidden]=useState(false);
  const[pinHintDismissed,setPinHintDismissed]=useState(false);
  const pinsSaveTimer=useRef(null);
  const[runSteps,setRunSteps]=useState({}); // guid -> {runs,steps,loading}
  const[tlExpanded,setTlExpanded]=useState({}); // guid -> bool
  const[drawerTab,setDrawerTab]=useState({}); // guid -> "uebersicht"|"headers"|"payload"|"fehler"|"adapter"|"attachments"
  const[kebabMenu,setKebabMenu]=useState(null); // {flowName,x,y} | null

  // Konzept-B Bulk-Selection (Set of GUIDs)
  const[selectedGuids,setSelectedGuids]=useState(()=>new Set());
  const toggleSelect=(guid)=>setSelectedGuids(p=>{const n=new Set(p);n.has(guid)?n.delete(guid):n.add(guid);return n;});
  const clearSelection=()=>setSelectedGuids(new Set());

  // "Bearbeitet"-Markierungen (Acknowledge): rein webapp-seitig, pro Instanz+MessageGuid.
  // Der Tenant wird nicht beruehrt; die Markierung traegt die Message-Zeit und zaehlt nur,
  // solange der Run im gewaehlten Zeit-Scope liegt.
  const[acks,setAcks]=useState({}); // guid -> {flow,time,at,by}
  const loadAcks=useCallback(async()=>{
    try{const d=await api(`/api/${instance.id}/message-acks`);const m={};(d.acks||[]).forEach(a=>{m[a.guid]=a;});setAcks(m);}catch{}
  },[instance.id]);
  const ackMessages=async(items)=>{
    if(!items.length)return;
    try{await api(`/api/${instance.id}/message-acks`,{method:"POST",body:JSON.stringify({messages:items})});await loadAcks();clearSelection();}
    catch(e){setError(e.message);}
  };
  const unackMessages=async(guids)=>{
    if(!guids.length)return;
    try{await api(`/api/${instance.id}/message-acks`,{method:"DELETE",body:JSON.stringify({guids})});await loadAcks();clearSelection();}
    catch(e){setError(e.message);}
  };

  // Konzept-B Bulk-AI-Analyse mit Cluster-Pre-Check
  const[bulkAnalysis,setBulkAnalysis]=useState(null); // {status,phase,preCheck,analysisText,error,msgCount,flowCount}
  const bulkAbortRef=useRef(null);
  const runBulkAiAnalysis=async()=>{
    if(!aiEnabled)return;
    const guids=Array.from(selectedGuids);
    if(!guids.length)return;
    if(bulkAbortRef.current){try{bulkAbortRef.current.abort();}catch{}}
    const ctl=new AbortController();
    bulkAbortRef.current=ctl;
    setBulkAnalysis({status:"loading",stage:"preload",msgCount:guids.length});
    try{
      const tok=getToken();
      const resp=await fetch(`/api/${instance.id}/ai-bulk-analysis`,{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${tok}`},
        body:JSON.stringify({messageGuids:guids, sprache: i18n.language === "en" ? "en" : "de"}),
        signal:ctl.signal,
      });
      if(!resp.ok){throw new Error(`HTTP ${resp.status}`);}
      const reader=resp.body.getReader();
      const decoder=new TextDecoder();
      let buf="";
      while(true){
        const{value,done}=await reader.read();
        if(done)break;
        buf+=decoder.decode(value,{stream:true});
        const lines=buf.split("\n\n");
        buf=lines.pop()||"";
        for(const line of lines){
          if(!line.startsWith("data:"))continue;
          try{
            const ev=JSON.parse(line.slice(5).trim());
            if(ev.type==="preload"&&ev.stage==="start")setBulkAnalysis(p=>({...p,status:"loading",stage:"preload",msgCount:ev.count}));
            else if(ev.type==="preload"&&ev.stage==="done")setBulkAnalysis(p=>({...p,stage:"precheck",loaded:ev.loaded,failedCount:ev.failed,skipped:ev.skipped,flowCount:ev.flows}));
            else if(ev.type==="precheck"&&ev.stage==="start")setBulkAnalysis(p=>({...p,stage:"precheck"}));
            else if(ev.type==="precheck"&&ev.stage==="done")setBulkAnalysis(p=>({...p,stage:"analysis",preCheck:{hasCorrelation:ev.hasCorrelation,reasoning:ev.reasoning,clusters:ev.clusters,orphans:ev.orphans}}));
            else if(ev.type==="analysis"&&ev.stage==="done")setBulkAnalysis(p=>({...p,status:"done",stage:"done",phase:ev.phase,analysisText:ev.text,msgCount:ev.msgCount,flowCount:ev.flowCount,sampled:ev.sampled,toolCalls:ev.toolCalls}));
            else if(ev.type==="error")setBulkAnalysis(p=>({...(p||{}),status:"error",error:ev.error}));
            else if(ev.type==="done"){/* end */}
          }catch{}
        }
      }
    }catch(e){
      if(e.name==="AbortError")return;
      setBulkAnalysis(p=>({...(p||{}),status:"error",error:e.message}));
    }
  };

  // Sub-tabs within Message Monitor
  const[monitorSubTab,setMonitorSubTab]=useState("monitor"); // "monitor" | "pipeline" | "analytics"
  const[statusFilter,setStatusFilter]=useState("ALL"); // "ALL"|"COMPLETED"|"FAILED"|"PROCESSING"
  const[refreshCountdown,setRefreshCountdown]=useState(0);

  // Time scope for monitor
  const[scopeMode,setScopeMode]=useState("24h"); // "1h"|"6h"|"24h"|"7d"|"30d"|"custom"
  const[customFrom,setCustomFrom]=useState("");const[customTo,setCustomTo]=useState("");
  // Scoped Counts fuer Fenster > Worker-Fenster (7d/30d/custom): separat vom Worker-Snapshot geladen.
  const[scopedData,setScopedData]=useState(null);
  const[scopedLoading,setScopedLoading]=useState(false);
  const[scopedNonce,setScopedNonce]=useState(0);
  const getScopeParams=useCallback(()=>{
    if(scopeMode==="1h") return "hours=1";
    if(scopeMode==="6h") return "hours=6";
    if(scopeMode==="24h") return "hours=24";
    if(scopeMode==="7d") return "hours=168";
    if(scopeMode==="30d") return "hours=720";
    if(scopeMode==="custom"&&customFrom&&customTo) return `since=${new Date(customFrom).toISOString()}&until=${new Date(customTo).toISOString()}`;
    return "hours=24";
  },[scopeMode,customFrom,customTo]);
  const getScopeLogParams=useCallback(()=>{
    if(scopeMode==="1h") return `startDate=${new Date(Date.now()-1*3600000).toISOString().replace("Z","")}`;
    if(scopeMode==="6h") return `startDate=${new Date(Date.now()-6*3600000).toISOString().replace("Z","")}`;
    if(scopeMode==="24h") return `startDate=${new Date(Date.now()-24*3600000).toISOString().replace("Z","")}`;
    if(scopeMode==="7d") return `startDate=${new Date(Date.now()-168*3600000).toISOString().replace("Z","")}`;
    if(scopeMode==="30d") return `startDate=${new Date(Date.now()-720*3600000).toISOString().replace("Z","")}`;
    if(scopeMode==="custom"&&customFrom&&customTo) return `startDate=${new Date(customFrom).toISOString().replace("Z","")}&endDate=${new Date(customTo).toISOString().replace("Z","")}`;
    return `startDate=${new Date(Date.now()-24*3600000).toISOString().replace("Z","")}`;
  },[scopeMode,customFrom,customTo]);

  const saveFilters=async(hf)=>{try{await api(`/api/instances/${instance.id}/filters`,{method:"PUT",body:JSON.stringify({hiddenFlows:hf})});}catch{}};
  const loadIflows=useCallback(async()=>{try{const d=await api(`/api/${instance.id}/iflows`);setIflows(d);setLastRefresh(new Date());}catch(e){setError(e.message);}},[instance.id]);
  const loadLogs=useCallback(async(flowName)=>{try{const d=await api(`/api/${instance.id}/logs?iflowName=${encodeURIComponent(flowName)}&top=200`);setLogs(d.results||[]);setLastRefresh(new Date());}catch(e){setError(e.message);}},[instance.id]);

  // Message Monitor V2 - Snapshot Pattern statt Synchron Load
  // Snapshot kommt sofort aus Backend Cache (oder mit null Counts wenn noch kein Cache).
  // Live Updates kommen via SSE Stream, Frontend ersetzt Tabelle bei neuer Version.
  const mplVersionRef=useRef(0);
  const mplEventSourceRef=useRef(null);
  // Fix 7: SSE Reconnect Backoff - Counter und Timeout-Handle als Refs damit Cleanup korrekt funktioniert
  const sseReconnectAttemptsRef=useRef(0);
  const sseReconnectTimeoutRef=useRef(null);
  const [sseLost,setSseLost]=useState(false);
  // Frische je Prioritätsklasse (nur High-Volume-Instanzen liefern das). Kommt aus dem
  // Snapshot-Feld _hv und wird zwischen zwei Snapshots per SSE-Ereignis "stand" nachgezogen.
  const [monitorStand,setMonitorStand]=useState(null);
  // Fix 5: Trigger State um SSE Effect manuell neu laufen zu lassen wenn Visibility wieder true ist
  const [sseTrigger,setSseTrigger]=useState(0);

  const loadMonitor=useCallback(async()=>{
    setMonitorLoading(true);setError(null);
    try{
      const d=await api(`/api/${instance.id}/mpl-counts/snapshot`,{timeout:20000});
      setMonitorData({
        iflows:d.iflows||[],
        hours:d.hours,
        // Tenant-wahre Gesamtzahlen pro Zeitfenster. Ohne sie fiel der Kopf auf die
        // Summe der sichtbaren Zeilen zurueck - auf grossen Tenants weicht die von der
        // Wahrheit ab (Rotation/High-Volume-Backfill deckt Fenster unterschiedlich ab)
        // und wich damit von der Startseite ab, die /stats und dieselben Totals nutzt.
        totals:d.totals||null,
        // Frische je Klasse. MUSS hier mit übernommen werden - genau dieses
        // Vergessen war die Ursache des Schiefstands beim Kopf: gelesen, aber nie gesetzt.
        _hv:d._hv||null,
        // Cap-Zustand des Fehler-Stores: ein abgeschnittener Bestand muss sichtbar sein.
        fehlerCap:d.fehlerCap||null,
        groupBy:d.groupBy||"iflow",
        totalLogs:d.totalLogs,
        deployedCount:d.deployedCount,
        allMessages:[],
        _meta:{
          version:d.version||0,
          computedAt:d.computedAt,
          ageMs:d.ageMs,
          computing:d.computing,
          durationMs:d.durationMs,
          lastError:d.lastError,
          nextRunAt:d.nextRunAt||null,
          pollDelayMs:d.pollDelayMs||null,
        },
      });
      mplVersionRef.current=d.version||0;
      setLastRefresh(new Date());
      // Wenn der Backend Snapshot einen lastError mitliefert, im UI anzeigen
      if(d.lastError){
        const le=d.lastError;
        let msg=tRef.current("meldung.workerLetzterLauf");
        if(le.httpStatus) msg += ` (HTTP ${le.httpStatus})`;
        if(le.hint) msg += `. ${le.hint}`;
        if(le.tenantMessage) msg += ` ${tRef.current("meldung.tenantAntwort",{text:le.tenantMessage})}`;
        setError(msg);
      }
    }catch(e){
      setError(e.message||tRef.current("meldung.snapshotFehlt"));
    }
    setMonitorLoading(false);
  },[instance.id]);

  // Bearbeitet-Markierungen beim Oeffnen des Monitors (und bei Instanzwechsel) laden
  useEffect(()=>{if(tab==="monitor")loadAcks();},[tab,loadAcks]);

  // Scoped Counts laden, wenn das gewaehlte Fenster groesser als das Worker-Fenster ist
  // (7d/30d/custom). Kleinere Fenster (1h/6h/24h) kommen direkt aus den Snapshot-Buckets.
  useEffect(()=>{
    if(tab!=="monitor"){return;}
    const SH={"1h":1,"6h":6,"24h":24,"7d":168,"30d":720};
    const BH=[1,6,24];
    const workerHours=monitorData?.hours||24;
    const wantHours=scopeMode==="custom"?null:SH[scopeMode];
    let needFetch;
    if(scopeMode==="custom"){ needFetch=!!customFrom&&!!customTo; }
    else { const canLocal=wantHours<=workerHours&&(wantHours===workerHours||BH.includes(wantHours)); needFetch=!canLocal; }
    if(!needFetch){ setScopedData(null); setScopedLoading(false); return; }
    let cancelled=false;
    setScopedLoading(true);
    (async()=>{
      try{
        let qs;
        if(scopeMode==="custom"){ qs=`since=${encodeURIComponent(new Date(customFrom).toISOString())}&until=${encodeURIComponent(new Date(customTo).toISOString())}`; }
        else { qs=`hours=${wantHours}`; }
        if(scopedNonce>0) qs+=`&refresh=1`;
        const d=await api(`/api/${instance.id}/mpl-counts/scoped?${qs}`,{timeout:180000});
        if(!cancelled) setScopedData(d);
      }catch(e){ if(!cancelled) setScopedData({iflows:[],error:e.message||tRef.current("meldung.auswertungFehlgeschlagen")}); }
      if(!cancelled) setScopedLoading(false);
    })();
    return ()=>{cancelled=true;};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[instance.id,tab,scopeMode,customFrom,customTo,monitorData?.hours,scopedNonce]);

  // Selbstkorrektur: war ein groesserer Zeitraum gewaehlt als der Worker verwaltet
  // (z.B. nach Absenken von monitorHours), auf den groessten erlaubten zuruecksetzen.
  useEffect(()=>{
    if(scopeMode==="custom") return;
    const SH={"1h":1,"6h":6,"24h":24,"7d":168,"30d":720};
    const wh=monitorData?.hours||24;
    if(SH[scopeMode]>wh){
      const allowed=[["30d",720],["7d",168],["24h",24],["6h",6],["1h",1]].find(([,h])=>h<=wh);
      setScopeMode(allowed?allowed[0]:"1h");
    }
  },[monitorData?.hours,scopeMode]);

  // SSE Subscribe fuer Live Updates (Versions Push vom Backend)
  // Fix 7: Manueller Reconnect mit exponentiellem Backoff statt EventSource Auto-Reconnect
  useEffect(()=>{
    if(!instance?.id) return;
    if(tab!=="monitor") return; // nur wenn Tab aktiv
    if(!getToken()) return;

    // Reset Attempts wenn Effect neu startet (Tab/Instance Wechsel)
    sseReconnectAttemptsRef.current=0;
    setSseLost(false);

    let cancelled=false;
    let currentEs=null;

    const connect=async()=>{
      if(cancelled) return;
      // Kurzlebiges SSE-Token je Verbindungsaufbau holen, damit das 7-Tage-Sitzungstoken
      // nicht in der URL (und damit in Server-Logs/Proxys) landet.
      const sseTok=await getSseToken();
      if(cancelled||!sseTok) return;
      const url=`/api/${instance.id}/mpl-counts/stream?token=${encodeURIComponent(sseTok)}`;
      const es=new EventSource(url);
      currentEs=es;
      mplEventSourceRef.current=es;
      es.addEventListener("update",(e)=>{
        try{
          const data=JSON.parse(e.data);
          if((data.version||0)>mplVersionRef.current){
            // Neuer Stand verfuegbar, alten verwerfen und Snapshot neu laden
            loadMonitor();
            loadAcks(); // Bearbeitet-Markierungen anderer User mitziehen
          }
        }catch{}
      });
      // Leichtes Frische-Ereignis: kommt ohne Versions-Bump, weil sich am Tenant nichts
      // geaendert hat. Ohne diesen Weg wuerde der Kopf im Sättigungsfall nie erfahren, wie
      // alt welche Klasse ist - genau dann, wenn es zaehlt. Kein Nachladen des Snapshots.
      es.addEventListener("stand",(e)=>{
        try{ setMonitorStand(JSON.parse(e.data)); }catch{}
      });
      es.addEventListener("worker-error",(e)=>{
        try{
          const data=JSON.parse(e.data);
          let msg=tRef.current("meldung.workerFehlgeschlagen");
          if(data.httpStatus) msg += ` (HTTP ${data.httpStatus})`;
          if(data.hint) msg += `. ${data.hint}`;
          if(data.tenantMessage && data.tenantMessage.length<200) msg += ` ${tRef.current("meldung.tenantAntwort",{text:data.tenantMessage})}`;
          if(data.filterExcerpt) msg += ` ${tRef.current("meldung.filterAuszug",{text:data.filterExcerpt.substring(0,120)})}`;
          setError(msg);
        }catch{}
      });
      es.onopen=()=>{
        // Verbindung wieder da, Counter zuruecksetzen und Hinweis verbergen
        sseReconnectAttemptsRef.current=0;
        setSseLost(false);
      };
      es.onerror=()=>{
        if(cancelled) return;
        try{es.close();}catch{}
        if(mplEventSourceRef.current===es) mplEventSourceRef.current=null;
        const attempts=sseReconnectAttemptsRef.current+1;
        sseReconnectAttemptsRef.current=attempts;
        const delay=Math.min(1000*Math.pow(2,attempts-1),30000);
        if(attempts>5) setSseLost(true);
        if(sseReconnectTimeoutRef.current){clearTimeout(sseReconnectTimeoutRef.current);}
        sseReconnectTimeoutRef.current=setTimeout(()=>{
          sseReconnectTimeoutRef.current=null;
          connect();
        },delay);
      };
    };

    connect();

    return ()=>{
      cancelled=true;
      if(sseReconnectTimeoutRef.current){clearTimeout(sseReconnectTimeoutRef.current);sseReconnectTimeoutRef.current=null;}
      try{if(currentEs)currentEs.close();}catch{}
      mplEventSourceRef.current=null;
    };
  },[instance?.id,tab,loadMonitor,sseTrigger]);

  // Fix 5: visibilitychange - bei Tab Rueckkehr stale Daten neu laden und SSE bei Bedarf neu aufbauen
  // Refs auf aktuelle Werte um stale Closures im Listener zu vermeiden
  const loadMonitorRef=useRef(loadMonitor);loadMonitorRef.current=loadMonitor;
  const monitorDataRef=useRef(monitorData);monitorDataRef.current=monitorData;
  const monitorLoadingRef=useRef(monitorLoading);monitorLoadingRef.current=monitorLoading;
  useEffect(()=>{
    const handler=()=>{
      if(document.visibilityState!=="visible") return;
      if(tabRef.current!=="monitor") return;
      const ts=monitorDataRef.current?._meta?.computedAt;
      if(ts && (Date.now()-ts)>60000 && !monitorLoadingRef.current){
        loadMonitorRef.current && loadMonitorRef.current();
      }
      const es=mplEventSourceRef.current;
      if(!es || es.readyState===2){
        // SSE ist closed oder weg - SSE-useEffect via Trigger neu starten
        if(sseReconnectTimeoutRef.current){clearTimeout(sseReconnectTimeoutRef.current);sseReconnectTimeoutRef.current=null;}
        sseReconnectAttemptsRef.current=0;
        setSseTrigger(t=>t+1);
      }
    };
    document.addEventListener("visibilitychange",handler);
    return ()=>document.removeEventListener("visibilitychange",handler);
  },[]);

  // Fix 6: Auto-Refresh wenn Daten stale (>60s). Pruefung alle 30s, kein API-Call wenn frisch.
  useEffect(()=>{
    const timer=setInterval(()=>{
      if(tabRef.current!=="monitor") return;
      if(monitorLoadingRef.current) return;
      const ts=monitorDataRef.current?._meta?.computedAt;
      if(ts && (Date.now()-ts)>60000){
        loadMonitorRef.current && loadMonitorRef.current();
      }
    },30000);
    return ()=>clearInterval(timer);
  },[]);

  // Fix 8: Stand-Anzeige soll sich selbst rerendern - Tick alle 15s erzwingt Re-Render der ageMs Berechnung
  const [headerNow,setHeaderNow]=useState(()=>Date.now());
  useEffect(()=>{
    const timer=setInterval(()=>setHeaderNow(Date.now()),15000);
    return ()=>clearInterval(timer);
  },[]);

  // Volltextsuche serverseitig (Debounce 400 ms, mind. 2 Zeichen)
  useEffect(()=>{
    if(monitorSearchMode!=="content"||monitorSearch.length<2){
      setContentSearchData({messages:[],count:0,loading:false,error:null});
      return;
    }
    setContentSearchData(p=>({...p,loading:true,error:null}));
    const t=setTimeout(async()=>{
      try{
        const d=await api(`/api/${instance.id}/mpl-search?q=${encodeURIComponent(monitorSearch)}&${getScopeParams()}&top=200`);
        setContentSearchData({messages:d.messages||[],count:d.count||0,loading:false,error:null});
      }catch(e){setContentSearchData({messages:[],count:0,loading:false,error:e.message||tRef.current("meldung.sucheFehlgeschlagen")});}
    },400);
    return ()=>clearTimeout(t);
  },[monitorSearch,monitorSearchMode,instance.id,getScopeParams]);

  const ackPageKey=JSON.stringify(Object.values(acks).filter(a=>a.flow===monitorExpanded).map(a=>[a.guid,a.at,a.by]).sort());
  const liveFailureFlow=monitorData?.iflows?.find(f=>f.name===monitorExpanded);
  const liveFailureKey=JSON.stringify([liveFailureFlow?.failed,liveFailureFlow?.escalated,liveFailureFlow?.lastFailed]);
  const {data:monitorFilterReload,loadMore:loadMoreMonitorRuns,refresh:refreshMonitorRuns,updateLogs:updateMonitorRunLogs,active:filteredRunsActive}=useMonitorRunPages({
    instanceId:instance.id,flow:monitorExpanded,status:monitorLogFilter,mode:eskModus,
    scope:getScopeLogParams,ackKey:ackPageKey,refreshKey:scopedNonce,liveKey:liveFailureKey,
  });
  useEffect(()=>{setMsgPage(1);},[monitorExpanded,monitorLogFilter,getScopeLogParams,ackPageKey]);

  // Load iFlow logs for monitor expand - filtered by same scope.
  // AbortController bricht vorherigen Drilldown ab, damit schnelles Klicken durch iFlows keine Race produziert.
  const drilldownAbortRef=useRef(null);
  const iflowSearchAbortRef=useRef(null);
  const loadMonitorLogs=useCallback(async(flowName,opts={})=>{
    if(monitorExpanded===flowName){
      if(drilldownAbortRef.current){try{drilldownAbortRef.current.abort();}catch{}drilldownAbortRef.current=null;}
      setMonitorExpanded(null);setMonitorLogs([]);setMonitorLogCount(0);setMonitorErrorDetail(null);return;
    }
    if(drilldownAbortRef.current){try{drilldownAbortRef.current.abort();}catch{}}
    const ctrl=new AbortController();
    drilldownAbortRef.current=ctrl;
    // Fehler zuerst: hat der iFlow Fehler, direkt auf dem Fehler-Tab landen. Die Zeilen kommen
    // aus dem Count-Worker-Store (Fast Path im /logs-Endpoint) und sind sofort da, waehrend
    // die 500 Default-Runs und der Suchindex im Hintergrund weiterladen.
    setMonitorExpanded(flowName);setMonitorLogsLoading(true);setMonitorLogs([]);setMonitorErrorDetail(null);setMonitorLogFilter((opts.failed||0)>0?"FAILED":"ALL");setMonitorDeepSearch("");setIflowIndexWanted(false);setMsgPage(1);
    try{
      // light=1: schlanke Liste (8 Felder, kein $inlinecount) - der volle Datensatz eines
      // Laufs kommt erst beim Klick auf die Zeile (hydrateRun). Erfolgs-Runs sind fuer den
      // Nutzer nur Zeitstempel; alles Weitere waere Datenlast auf dem Tenant ohne Leser.
      const d=await api(`/api/${instance.id}/logs?iflowName=${encodeURIComponent(flowName)}&${getScopeLogParams()}&top=500&light=1`,{signal:ctrl.signal});
      if(ctrl.signal.aborted) return;
      const rows=(d.results||[]).map(r=>r.IntegrationFlowName?r:{...r,IntegrationFlowName:flowName});
      setMonitorLogs(rows);setMonitorLogCount(d.light?0:(d.count||rows.length));
    }catch(e){
      if(e.name!=="AbortError") setError(e.message);
    }
    if(!ctrl.signal.aborted) setMonitorLogsLoading(false);
  },[instance.id,getScopeLogParams,monitorExpanded]);

  // Such-Index NUR auf Anforderung aufbauen (Fokus in der Tiefensuche setzt iflowIndexWanted).
  // Er lief frueher bei jedem iFlow-Aufklappen: bis 5000 volle Zeilen plus ein Header-Call je
  // GUID - tausende Tenant-Abfragen fuer einen Klick, den die meisten nie zum Suchen nutzten.
  // Erster Call mit withRuns=1, danach Polling withRuns=0 bis building=false (Header werden
  // batchweise serverseitig gecacht). Die Suche selbst laeuft rein in-memory ueber den Index.
  useEffect(()=>{
    const empty={runs:[],headers:{},loading:false,building:false,indexed:0,total:0,error:null,runsCapped:false,flow:null};
    if(!monitorExpanded||!iflowIndexWanted||corrActiveRef.current){ if(iflowSearchAbortRef.current){try{iflowSearchAbortRef.current.abort();}catch{}iflowSearchAbortRef.current=null;} setIflowIndex(empty); return; }
    const flow=monitorExpanded;
    if(iflowSearchAbortRef.current){try{iflowSearchAbortRef.current.abort();}catch{}}
    const ctrl=new AbortController(); iflowSearchAbortRef.current=ctrl;
    let cancelled=false; let withRuns=true; let timer=null;
    setIflowIndex({...empty,loading:true,flow});
    const poll=async()=>{
      try{
        const d=await api(`/api/${instance.id}/mpl-search-index?iflowName=${encodeURIComponent(flow)}&${getScopeParams()}&withRuns=${withRuns?1:0}`,{timeout:60000,signal:ctrl.signal});
        if(cancelled) return;
        setIflowIndex(prev=>{
          const runs=d.runs||prev.runs;
          return {runs,headers:{...prev.headers,...(d.headers||{})},loading:false,building:!!d.building,indexed:d.indexed||0,total:d.total||runs.length,error:null,runsCapped:!!d.runsCapped,flow};
        });
        if(d.runs) withRuns=false; // erst aufhoeren Runs anzufordern, wenn wir sie wirklich haben
        if(d.building && !cancelled) timer=setTimeout(poll,1200);
      }catch(e){ if(!cancelled && e.name!=="AbortError") setIflowIndex(prev=>({...prev,loading:false,building:false,error:e.message||tRef.current("meldung.indexFehlgeschlagen")})); }
    };
    poll();
    return ()=>{cancelled=true; if(timer) clearTimeout(timer);};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[instance.id,monitorExpanded,iflowIndexWanted,scopeMode,customFrom,customTo]);

  // Pins vorwaermen: beim Oeffnen des Monitor-Tabs den Header-Index der angepinnten iFlows
  // im Hintergrund leicht anstossen (max 2 Pins, eine kleine Batch), damit ein spaeteres
  // Oeffnen schneller sucht. Bewusst NICHT an scopeMode gekoppelt (sonst Last bei jedem Wechsel).
  // Dep ist der stabile pinsKey, damit es nicht bei jeder Pin-Array-Neureferenz feuert.
  const monitorPinsKey=(monitorPins||[]).join("|");
  useEffect(()=>{
    if(tab!=="monitor"||!monitorPins||monitorPins.length===0) return;
    let cancelled=false; const ctrl=new AbortController();
    (async()=>{
      for(const flow of monitorPins.slice(0,2)){
        if(cancelled) break;
        try{ await api(`/api/${instance.id}/mpl-search-index?iflowName=${encodeURIComponent(flow)}&${getScopeParams()}&withRuns=0&batch=150`,{timeout:60000,signal:ctrl.signal}); }catch{}
      }
    })();
    return ()=>{cancelled=true; try{ctrl.abort();}catch{}};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[instance.id,tab,monitorPinsKey]);

  // Klick auf einen Lauf: erst jetzt den vollstaendigen Datensatz dieses EINEN Laufs laden
  // (die Liste ist leicht, 8 Felder). Fehlschlag ist unkritisch - der Drawer arbeitet mit
  // den vorhandenen Feldern weiter, es geht nur um Zusatzfelder wie ApplicationMessageId.
  const hydrateRun=async(guid)=>{
    try{
      const full=await api(`/api/${instance.id}/run/${encodeURIComponent(guid)}`,{timeout:15000});
      if(full&&full.MessageGuid){
        const merge=l=>l.MessageGuid===guid?{...l,...full}:l;
        setMonitorLogs(p=>p.map(merge));
        updateMonitorRunLogs(merge);
      }
    }catch{}
  };
  const loadMonitorLogError=async(guid)=>{
    if(monitorErrorDetail?.guid===guid){setMonitorErrorDetail(null);return;}
    setMonitorErrorDetail({guid,loading:true});
    try{
      // Erfolg und Fehler werden UNTERSCHIEDEN: eine leere Liste heisst "wirklich nichts da",
      // ein Fehler heisst "nicht lesbar". Frueher wurde beides zu [] und die UI behauptete "0".
      const hole=(pfad)=>api(`/api/${instance.id}/logs/${encodeURIComponent(guid)}/${pfad}`)
        .then(v=>({ok:true,v})).catch(err=>({ok:false,v:null,grund:err&&err.reason?err.reason:null}));
      const[hdrsRes,errInfo,attachRes,adapterRes]=await Promise.all([
        hole("headers"), hole("error"), hole("attachments"), hole("adapter-attributes"),
      ]);
      setMonitorErrorDetail({guid,loading:false,
        headers:Array.isArray(hdrsRes.v)?hdrsRes.v:[],
        error:errInfo.ok?errInfo.v:null,
        attachments:Array.isArray(attachRes.v)?attachRes.v:[],
        adapterAttributes:Array.isArray(adapterRes.v)?adapterRes.v:[],
        nichtLesbar:[
          hdrsRes.ok?null:{bereich:t("detail.reiterHeaders"),grund:hdrsRes.grund},
          attachRes.ok?null:{bereich:t("detail.reiterAttachments"),grund:attachRes.grund},
          adapterRes.ok?null:{bereich:t("detail.reiterAdapter"),grund:adapterRes.grund},
        ].filter(Boolean),
      });
    }catch{setMonitorErrorDetail({guid,headers:[],error:null,attachments:[],adapterAttributes:[],nichtLesbar:[],loading:false});}
  };
  const loadAttachmentContent=async(aid,name)=>{
    try{const res=await fetch(`/api/${instance.id}/logs/${encodeURIComponent(monitorErrorDetail?.guid)}/attachments/${encodeURIComponent(aid)}`,{headers:{Authorization:`Bearer ${getToken()}`}});
    const text=await res.text();setAttachmentModal({content:text,name});}catch(e){setAttachmentModal({content:t("meldung.fehlerMit",{text:e.message}),name});}
  };
  // Markdown-Renderer fuer KI-Analyse + Chat: Ueberschriften, Listen, Codebloecke,
  // Bold, Inline-Code, Trennlinien. HTML wird zuerst escaped (kein XSS).
  const renderChatMd=(text)=>{
    if(!text) return "";
    const esc=(s)=>String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    const inline=(s)=>esc(s)
      .replace(/\*\*([^*]+)\*\*/g,'<strong>$1</strong>')
      .replace(/`([^`]+)`/g,'<code class="iio-md-code">$1</code>');
    const lines=String(text).replace(/\r/g,"").split("\n");
    let html="",i=0,listType=null;
    const closeList=()=>{ if(listType){ html+=`</${listType}>`; listType=null; } };
    while(i<lines.length){
      const line=lines[i];
      if(/^```/.test(line)){ closeList(); const buf=[]; i++; while(i<lines.length&&!/^```/.test(lines[i])){ buf.push(lines[i]); i++; } i++; html+=`<pre class="iio-md-pre"><code>${esc(buf.join("\n"))}</code></pre>`; continue; }
      if(/^---+\s*$/.test(line)){ closeList(); html+='<hr class="iio-md-hr"/>'; i++; continue; }
      let hm;
      if((hm=line.match(/^(#{1,4})\s+(.*)$/))){ closeList(); html+=`<div class="iio-md-h iio-md-h${hm[1].length}">${inline(hm[2])}</div>`; i++; continue; }
      if(/^\s*\d+\.\s+/.test(line)){ if(listType!=="ol"){ closeList(); html+='<ol class="iio-md-ol">'; listType="ol"; } html+=`<li>${inline(line.replace(/^\s*\d+\.\s+/,""))}</li>`; i++; continue; }
      if(/^\s*[-*]\s+/.test(line)){ if(listType!=="ul"){ closeList(); html+='<ul class="iio-md-ul">'; listType="ul"; } html+=`<li>${inline(line.replace(/^\s*[-*]\s+/,""))}</li>`; i++; continue; }
      if(/^\s*$/.test(line)){ closeList(); i++; continue; }
      closeList(); html+=`<div class="iio-md-p">${inline(line)}</div>`; i++;
    }
    closeList();
    return html;
  };
  // Resolve package for design links
  const[resolvedPkgs,setResolvedPkgs]=useState({});
  const resolvePackage=async(flowName)=>{
    if(resolvedPkgs[flowName])return resolvedPkgs[flowName];
    try{const r=await api(`/api/${instance.id}/resolve-package/${encodeURIComponent(flowName)}`);setResolvedPkgs(p=>({...p,[flowName]:r}));return r;}catch{return{packageId:null};}
  };
  const openDesignLink=async(flowName)=>{
    const suiteBase=toSuiteUrl(instance.tenantUrl,instance.suiteUrl);
    const w=window.open("about:blank","_blank");
    try{const pkg=await resolvePackage(flowName);
      if(pkg?.packageId)w.location.href=`${suiteBase}/shell/design/contentpackage/${pkg.packageId}/integrationflows/${pkg.flowId||flowName}`;
      else w.location.href=`${suiteBase}/shell/design`;
    }catch{w.location.href=`${suiteBase}/shell/design`;}
  };

  // Pipeline retry
  const triggerRetry=async(msg)=>{
    const key=msg.MessageGuid;
    setRetryLoading(key);
    try{
      const r=await api(`/api/${instance.id}/pipeline/retry`,{method:"POST",body:JSON.stringify({messageGuid:msg.MessageGuid,correlationId:msg.CorrelationId,queueName:msg.Receiver||""})});
      setRetryResult(p=>({...p,[key]:r}));
    }catch(e){setRetryResult(p=>({...p,[key]:{success:false,message:e.message}}));}
    setRetryLoading(null);
  };

  // === NEU: Pins laden/speichern (pro User+Instanz) ===
  const savePins=useCallback((pins)=>{
    if(pinsSaveTimer.current)clearTimeout(pinsSaveTimer.current);
    pinsSaveTimer.current=setTimeout(()=>{
      api(`/api/monitor/pins/${instance.id}`,{method:"PUT",body:JSON.stringify({pins})}).catch(()=>{});
    },400);
  },[instance.id]);
  const togglePin=useCallback((flowName)=>{
    setMonitorPins(prev=>{
      let next;
      if(prev.includes(flowName))next=prev.filter(x=>x!==flowName);
      else{ if(prev.length>=12)return prev; next=[...prev,flowName]; }
      savePins(next);
      return next;
    });
  },[savePins]);

  // === NEU: Run-Steps (iFlow-Schritt-Timeline) laden ===
  const loadRunSteps=useCallback(async(guid,iflowName)=>{
    if(!guid||runSteps[guid])return;
    setRunSteps(p=>({...p,[guid]:{loading:true,runs:[],steps:[]}}));
    try{
      const q=iflowName?`?iflow=${encodeURIComponent(iflowName)}`:"";
      const d=await api(`/api/${instance.id}/logs/${encodeURIComponent(guid)}/run-steps${q}`);
      setRunSteps(p=>({...p,[guid]:{loading:false,runs:d.runs||[],steps:d.steps||[],namesAvailable:d.namesAvailable,contentRestricted:d.contentRestricted,
        // Deterministische Fehlerstelle + Aufloesungsquote fuer die Verortung
        errorStep:d.errorStep||null,resolution:d.resolution||null,runCount:d.runCount||null}}));
    }catch{
      setRunSteps(p=>({...p,[guid]:{loading:false,runs:[],steps:[],namesAvailable:false,contentRestricted:false,errorStep:null,resolution:null}}));
    }
  },[instance.id,runSteps]);

  // Initial load - iFlows, filters, permissions, pins (once per instance)
  useEffect(()=>{setLoading(true);loadIflows().finally(()=>setLoading(false));
    api(`/api/instances/${instance.id}/filters`).then(f=>setHiddenFlows(f.hiddenFlows||[])).catch(()=>{});
    api(`/api/instances/${instance.id}/permissions`).then(setPermissions).catch(()=>{});
    api(`/api/monitor/pins/${instance.id}`).then(d=>setMonitorPins(Array.isArray(d.pins)?d.pins:[])).catch(()=>{});
    // Ansicht liegt am BENUTZER, nicht an der Instanz - dieselbe Ablage wie die Pins, damit die
    // Einstellung dem Nutzer folgt und nicht am Browser klebt.
    api("/api/monitor/ansicht").then(d=>setEskModus(d&&d.eskalation?d.eskalation:"aus")).catch(()=>{});
  },[loadIflows,instance.id]);

  const setzeEskModus=useCallback((m)=>{
    setEskModus(m);
    api("/api/monitor/ansicht",{method:"PUT",body:JSON.stringify({eskalation:m})}).catch(()=>{});
  },[]);

  // Kebab-Menü schließen bei Klick außerhalb / Scroll
  useEffect(()=>{
    if(!kebabMenu)return;
    const close=()=>setKebabMenu(null);
    window.addEventListener("click",close);
    window.addEventListener("scroll",close,true);
    window.addEventListener("resize",close);
    return()=>{window.removeEventListener("click",close);window.removeEventListener("scroll",close,true);window.removeEventListener("resize",close);};
  },[kebabMenu]);

  // Reload monitor whenever scope changes (loadMonitor is recreated via useCallback when getScopeParams changes)
  // This also handles the initial load and auto-refresh without stale closures
  useEffect(()=>{loadMonitor();},[loadMonitor]);

  // Sync tab/flow with browser back/forward within dashboard
  useEffect(()=>{
    const onPop=()=>{
      const p=window.location.pathname.split("/").filter(Boolean);
      const isContent=p[3]==="content";
      setTab(isContent?"content":"monitor");
      if(isContent&&p[4])setLocalTargetFlow(decodeURIComponent(p[4]));
      else if(!isContent)setLocalTargetFlow(null);
    };
    window.addEventListener("popstate",onPop);
    return()=>window.removeEventListener("popstate",onPop);
  },[]);

  // Auto-refresh
  useEffect(()=>{
    if(!refreshInterval)return;
    const timer=setInterval(()=>{
      if(tabRef.current==="monitor") loadMonitor();
      else if(tabRef.current==="logs"){
        if(viewRef.current==="iflow"&&selRef.current) loadLogs(selRef.current.Name);
        else loadIflows();
      }
    },refreshInterval*1000);
    return()=>clearInterval(timer);
  },[refreshInterval,loadIflows,loadLogs,loadMonitor]);

  // Refresh countdown
  useEffect(()=>{
    if(refreshInterval<=0){setRefreshCountdown(0);return;}
    setRefreshCountdown(refreshInterval);
    const timer=setInterval(()=>{
      setRefreshCountdown(prev=>{
        if(prev<=1) return refreshInterval;
        return prev-1;
      });
    },1000);
    return()=>clearInterval(timer);
  },[refreshInterval]);

  const pickFlow=useCallback(f=>{setLoading(true);setSel(f);setView("iflow");setExp(null);setHdrs({});hdrsRef.current={};loadLogs(f.Name).finally(()=>setLoading(false));},[loadLogs]);
  // hdrsRef mirrors hdrs state to avoid stale closure in useCallback without hdrs in dep array
  const loadH=useCallback(async g=>{if(hdrsRef.current[g])return;try{const h=await api(`/api/${instance.id}/logs/${encodeURIComponent(g)}/headers`);hdrsRef.current={...hdrsRef.current,[g]:h};setHdrs(p=>({...p,[g]:h}));}catch{}},[instance.id]);
  const toggle=g=>{if(exp===g){setExp(null);return;}setExp(g);loadH(g);};
  // Manueller Refresh: bei Monitor-Tab Worker neu starten und User klar Feedback geben
  const [refreshInfo,setRefreshInfo]=useState(null);
  const manualRefresh=async()=>{
    if(tab==="monitor"){
      try{
        const r=await api(`/api/${instance.id}/mpl-counts/refresh`,{method:"POST"});
        setRefreshInfo({type:"ok",text:r.message||t("meldung.aktualisierungGestartet")});
        loadMonitor();
        loadAcks();
        setScopedNonce(n=>n+1); // erzwingt frisches Nachladen bei 7d/30d/custom
      }catch(e){
        // 202 wird vom api() als Fehler geworfen weil !r.ok. Wir parsen die Meldung.
        // ACHTUNG: Diese Pruefung haengt am deutschen Wortlaut des Servers. Der Server
        // antwortet unabhaengig von der Oberflaechen-Sprache deutsch, die Pruefung traegt
        // also weiterhin - sie gehoert aber auf eine Kennung umgestellt (siehe Bericht).
        if(e.message&&e.message.includes("läuft bereits")) setRefreshInfo({type:"info",text:e.message});
        else setRefreshInfo({type:"error",text:e.message||t("meldung.aktualisierungFehlgeschlagen")});
      }
      setTimeout(()=>setRefreshInfo(null),3500);
      return;
    }
    if(view==="iflow"&&sel) loadLogs(sel.Name);
    else loadIflows();
  };
  const fl=logs.filter(l=>{if(sf!=="ALL"&&l.Status!==sf)return false;if(search){const s=search.toLowerCase();return[l.MessageGuid,l.CorrelationId,l.Sender,l.Receiver].some(v=>v?.toLowerCase().includes(s));}return true;});
  const gr=gb==="none"?{"Alle":fl}:fl.reduce((a,l)=>{const k=gb==="status"?l.Status:gb==="sender"?(l.Sender||"?"):(l.Receiver||"?");(a[k]=a[k]||[]).push(l);return a;},{});
  const st={total:logs.length,ok:logs.filter(l=>l.Status==="COMPLETED").length,fail:logs.filter(l=>l.Status==="FAILED").length,proc:logs.filter(l=>l.Status==="PROCESSING").length,retry:logs.filter(l=>l.Status==="RETRY"||l.Status==="ESCALATED").length};

  // Zeitfenster-Projektion: 1h/6h aus Snapshot-Buckets, 24h aus den flachen Worker-Werten,
  // 7d/30d/custom aus den separat geladenen scoped Counts. So bedient der Selektor die Zahlen.
  const SCOPE_HOURS={"1h":1,"6h":6,"24h":24,"7d":168,"30d":720};
  const MON_BUCKET_HOURS=[1,6,24]; // muss zu backend MM_BUCKET_HOURS passen
  const workerHours=monitorData?.hours||24;
  const scopeHoursNum=scopeMode==="custom"
    ?((customFrom&&customTo)?Math.max(1,Math.round((new Date(customTo)-new Date(customFrom))/3600000)):24)
    :SCOPE_HOURS[scopeMode];
  // Lokal projizierbar nur, wenn das Fenster <= Worker-Fenster ist UND entweder exakt das
  // Worker-Fenster (flache Werte) oder ein berechneter Bucket (1/6/24) ist. Sonst scoped-Fetch.
  const canProjectLocally=scopeMode!=="custom"&&scopeHoursNum<=workerHours
    &&(scopeHoursNum===workerHours||MON_BUCKET_HOURS.includes(scopeHoursNum));
  const useScopedFetch=!canProjectLocally;
  const scopedIflows=(()=>{
    if(useScopedFetch) return scopedData?.iflows||[];
    const base=monitorData?.iflows||[];
    if(scopeHoursNum===workerHours) return base; // flaches Worker-Fenster == gewaehltes Fenster
    const bh=scopeHoursNum; // 1, 6 oder 24
    return base.map(f=>{
      const b=f.buckets&&f.buckets[bh];
      // ALLE Statusklassen projizieren - eine vergessene Klasse behaelt sonst beim
      // Fenster-Wechsel still ihren 24h-Wert (so geschehen mit escalated/discarded).
      if(!b) return {...f,total:0,failed:0,completed:0,processing:0,retry:0,escalated:0,discarded:0,other:0};
      return {...f,total:b.total||0,failed:b.failed||0,completed:b.completed||0,processing:b.processing||0,retry:b.retry||0,escalated:b.escalated||0,discarded:b.discarded||0,other:b.other||0};
    });
  })();

  // Sprechende Namen: MPL liefert als "IntegrationFlowName" die TECHNISCHE Id des Artefakts.
  // displayName kommt aus dem Deploy-Join des Workers. Die Map loest Ids in Message-Zeilen
  // auf, die selbst nur die technische Id kennen. Gruppierung/Drilldown bleiben auf f.name.
  const flowDisplayMap={};
  for(const f of [...(monitorData?.iflows||[]),...(scopedData?.iflows||[])]){
    if(f.displayName&&f.displayName!==f.name) flowDisplayMap[f.name]=f.displayName;
  }
  const flowLabel=(n)=>(n&&flowDisplayMap[n])||n;

  // Bearbeitet-Verrechnung: ein Ack zaehlt nur, wenn die MESSAGE-Zeit im gewaehlten Scope
  // liegt - faellt der Run aus dem Zeitfenster, verschwindet er auch aus "Bearbeitet".
  const ackScopeWin=scopeMode==="custom"&&customFrom&&customTo
    ?{from:new Date(customFrom).getTime(),to:new Date(customTo).getTime()}
    :{from:Date.now()-scopeHoursNum*3600000,to:Infinity};
  const ackInScope=(a)=>!!a&&(a.time||0)>=ackScopeWin.from&&(a.time||0)<=ackScopeWin.to;
  const isAcked=(guid)=>ackInScope(acks[guid]);
  const ackCountByFlow={};
  // Getrennt nach Art, damit ein abgehakter eskalierter Lauf von der Eskalations-Zahl abgeht und
  // nicht von der Fehlerzahl. Acks ohne art stammen aus der Zeit vor der Erweiterung und gelten
  // als Fehler - genau wie sie sich vorher verhalten haben.
  const ackFehlerByFlow={},ackEskByFlow={};
  for(const a of Object.values(acks)){
    if(!ackInScope(a)||!a.flow)continue;
    ackCountByFlow[a.flow]=(ackCountByFlow[a.flow]||0)+1;
    if(a.art==="eskaliert")ackEskByFlow[a.flow]=(ackEskByFlow[a.flow]||0)+1;
    else ackFehlerByFlow[a.flow]=(ackFehlerByFlow[a.flow]||0)+1;
  }
  const failureCountsForFlow=f=>{
    if(f.name===monitorExpanded&&monitorFilterReload?.summary&&!monitorFilterReload.stale)return monitorFilterReload.summary;
    const raw=f.failed||0,esc=eskModus==="aus"?0:(f.escalated||0);
    const ackFail=Math.min(raw,ackFehlerByFlow[f.name]||0),ackEsc=Math.min(esc,ackEskByFlow[f.name]||0);
    return {failed:raw-ackFail+(eskModus==="fehler"?esc-ackEsc:0),acknowledged:ackFail+ackEsc,escalated:esc-ackEsc};
  };

  // Correlation-Suche im "iFlow filtern"-Feld: Sieht die Eingabe wie eine Correlation-ID aus
  // (>=12 Zeichen, kein Leerzeichen) und trifft lokal keinen iFlow-Namen, wird die Kette
  // per OData gesucht (1 eq-Call serverseitig, ~100ms).
  const corrCandidate=(()=>{
    if(monitorSearchMode!=="iflow"||tab!=="monitor")return null;
    const q=monitorSearch.trim();
    if(q.length<12||/\s/.test(q))return null;
    const ql=q.toLowerCase();
    const hasLocal=(scopedIflows||[]).some(f=>f.name.toLowerCase().includes(ql)||(f.displayName||"").toLowerCase().includes(ql));
    return hasLocal?null:q;
  })();
  useEffect(()=>{
    if(!corrCandidate){setCorrSearch(null);return;}
    let cancelled=false;
    setCorrSearch({cid:corrCandidate,loading:true,flows:[],byFlow:{}});
    const t=setTimeout(async()=>{
      try{
        const d=await api(`/api/${instance.id}/mpl-correlation?cid=${encodeURIComponent(corrCandidate)}`);
        if(cancelled)return;
        const byFlow={};(d.iflows||[]).forEach(x=>{byFlow[x.name]=x;});
        setCorrSearch({cid:corrCandidate,loading:false,flows:(d.iflows||[]).map(x=>x.name),byFlow,count:d.count,capped:d.capped});
      }catch(e){if(!cancelled)setCorrSearch({cid:corrCandidate,loading:false,error:e.message,flows:[],byFlow:{}});}
    },600);
    return()=>{cancelled=true;clearTimeout(t);};
  },[corrCandidate,instance.id]);
  const corrActive=!!(corrSearch&&!corrSearch.loading&&!corrSearch.error&&corrSearch.flows.length>0&&monitorSearchMode==="iflow"&&monitorSearch.trim()===corrSearch.cid);
  corrActiveRef.current=corrActive;
  // Correlation-Filter komplett zuruecksetzen (Banner-Button + nach Bedarf)
  const corrReset=()=>{setMonitorSearch("");setCorrSearch(null);setMonitorExpanded(null);setMonitorErrorDetail(null);};
  // "Lade Abhängigkeiten": Kette dieser Correlation-ID fuers Popup holen
  const openCorrDeps=async(cid)=>{
    setCorrDeps({cid,loading:true});
    try{const d=await api(`/api/${instance.id}/mpl-correlation?cid=${encodeURIComponent(cid)}`);setCorrDeps({cid,loading:false,data:d});}
    catch(e){setCorrDeps({cid,loading:false,error:e.message});}
  };

  // Monitor sort + filter
  const monitorIflows=(scopedIflows||[]).filter(f=>{
    if(monitorSearchMode==="iflow"&&monitorSearch){
      if(corrActive){
        if(!corrSearch.flows.includes(f.name))return false;
      }else{
        const q=monitorSearch.toLowerCase();
        if(!f.name.toLowerCase().includes(q)&&!(f.displayName||"").toLowerCase().includes(q))return false;
      }
    }
    const effFailed=failureCountsForFlow(f).failed;
    if(statFilter==="fail"&&effFailed===0)return false;
    if(statusFilter!=="ALL"){
      if(statusFilter==="COMPLETED"&&f.completed===0)return false;
      if(statusFilter==="FAILED"&&effFailed===0)return false;
      if(statusFilter==="PROCESSING"&&(f.processing||0)===0)return false;
    }
    return true;
  });
  // Content search: serverseitig per /mpl-search (siehe useEffect oben)
  const contentSearchResults=monitorSearchMode==="content"&&monitorSearch.length>=2?contentSearchData.messages:[];
  // Gewicht fuer "Fehler zuerst": zeigt die Ansicht Eskalationen, gehoeren sie zum
  // Handlungsbedarf und muessen die Sortierung mitbestimmen. Sonst versinkt ein iFlow mit
  // eskalierten Laeufen unter lauter fehlerfreien, obwohl die Spalte daneben rot leuchtet.
  const fehlerGewicht=f=>failureCountsForFlow(f).failed+(eskModus==="spalte"?failureCountsForFlow(f).escalated:0);
  const sortedMonitorIflows=[...monitorIflows].sort((a,b)=>{
    if(monitorSort==="failed_desc") return (fehlerGewicht(b)-fehlerGewicht(a))||(b.total-a.total);
    if(monitorSort==="total_desc") return b.total-a.total;
    if(monitorSort==="name_asc") return (a.displayName||a.name).localeCompare(b.displayName||b.name);
    if(monitorSort==="last_failed") return (b.lastFailed?parseOD(b.lastFailed):0)-(a.lastFailed?parseOD(a.lastFailed):0);
    if(monitorSort==="completed_desc") return (b.completed-a.completed)||(b.total-a.total);
    return 0;
  });
  // Kopf-KPIs: bevorzugt die tenant-wahren Gesamtzahlen aus dem Count-Worker-Snapshot
  // (stabil, kein Rotations-Lag). Bei aktiver Suche/Filterung oder klassischem Worker:
  // Summe der sichtbaren Zeilen wie bisher.
  const globalScopeTotals=(!monitorSearch&&monitorData?.totals&&monitorData.totals[scopeHoursNum])||null;
  // Eskalationen im Scope - Grundlage fuer den Modus "zu Fehler zaehlen" und fuer den Zusatz
  // "inkl. n ESCALATED" am Kopf. Die Zahl kommt aus derselben Menge wie die Fehlerzahl, damit
  // Kopf und Tabelle nicht auseinanderlaufen.
  const eskImScope=(globalScopeTotals?(scopedIflows||[]):monitorIflows).reduce((s,f)=>s+(f.escalated||0),0);
  const monitorTotalsRoh=globalScopeTotals
    ?{completed:globalScopeTotals.completed||0,failed:globalScopeTotals.failed||0,total:globalScopeTotals.total||0,discarded:globalScopeTotals.discarded||0}
    :monitorIflows.reduce((a,f)=>({completed:a.completed+f.completed,failed:a.failed+f.failed,total:a.total+f.total,discarded:a.discarded+(f.discarded||0)}),{completed:0,failed:0,total:0,discarded:0});
  const monitorTotals=eskModus==="fehler"
    ?{...monitorTotalsRoh,failed:monitorTotalsRoh.failed+eskImScope}
    :monitorTotalsRoh;
  // Bearbeitete Fehler fuer die Fehler-KPI: dieselbe Basis wie monitorTotals verwenden
  // (globale Totals -> alle iFlows im Scope; gefilterte Ansicht -> nur sichtbare iFlows),
  // sonst wuerden Acks unsichtbarer iFlows die KPI der Suchtreffer unterlaufen.
  const ackedVisible=(globalScopeTotals?(scopedIflows||[]):monitorIflows).reduce((s,f)=>{
    const basis=(f.failed||0)+(eskModus==="fehler"?(f.escalated||0):0);
    const acks=(ackFehlerByFlow[f.name]||0)+(eskModus==="fehler"?(ackEskByFlow[f.name]||0):0);
    return s+Math.min(acks,basis);
  },0);
  const failureCountAdjustment=(globalScopeTotals?(scopedIflows||[]):monitorIflows).reduce((sum,f)=>{
    const basis=(f.failed||0)+(eskModus==="fehler"?(f.escalated||0):0);
    const acknowledged=(ackFehlerByFlow[f.name]||0)+(eskModus==="fehler"?(ackEskByFlow[f.name]||0):0);
    return sum+failureCountsForFlow(f).failed-Math.max(0,basis-acknowledged);
  },0);
  // Fehlerseiten sind bereits serverseitig nach Status und Bearbeitet-Markierung gefiltert.
  // Ein fehlgeschlagener Abruf darf nicht auf die ungefilterte Vorschau zurueckfallen.
  const useFilterReload = filteredRunsActive;
  const baseLogsForFilter = useFilterReload ? (monitorFilterReload?.logs||[]) : monitorLogs;
  const filteredMonitorLogs=baseLogsForFilter.filter(l=>{
    // Lokale Statusfilter gelten nur fuer die ungefilterte Vorschau.
    if(!useFilterReload&&monitorLogFilter==="ESCALATED")return l.Status==="ESCALATED"&&!isAcked(l.MessageGuid);
    if(!useFilterReload&&(monitorLogFilter==="FAILED"||monitorLogFilter==="ACKED")){
      const istFehler=l.Status==="FAILED"||l.Status==="ERROR";
      // Bearbeitet umfasst auch abgehakte eskalierte Laeufe, sobald die Ansicht sie zeigt.
      const istEsk=eskModus!=="aus"&&l.Status==="ESCALATED";
      if(!useFilterReload&&!istFehler&&!istEsk)return false;
      if(monitorLogFilter==="FAILED"&&istEsk)return false;
      const a=isAcked(l.MessageGuid);
      if(monitorLogFilter==="FAILED"&&a)return false;
      if(monitorLogFilter==="ACKED"&&!a)return false;
    }
    // Uebrige Filter: klientseitig auf den Default-Logs (Retry/Escalated laufen unter "Andere")
    else if(!useFilterReload && monitorLogFilter!=="ALL"){
      if(monitorLogFilter==="OTHER"&&["COMPLETED","FAILED","ERROR","PROCESSING"].includes(l.Status))return false;
      if(monitorLogFilter==="OTHER"&&eskModus==="spalte"&&l.Status==="ESCALATED")return false;
      else if(monitorLogFilter!=="OTHER"&&l.Status!==monitorLogFilter)return false;
    }
    if(!monitorDeepSearch) return true;
    const q=monitorDeepSearch.toLowerCase();
    // Fuzzy search across all message fields
    const fields=[l.MessageGuid,l.CorrelationId,l.Sender,l.Receiver,l.ApplicationMessageId,l.ApplicationMessageType,l.CustomStatus,l.TransactionId,l.IntegrationFlowName,flowLabel(l.IntegrationFlowName)].filter(Boolean);
    return fields.some(f=>f.toLowerCase().includes(q));
  });
  // Bei aktiver Suche (>=2 Zeichen) rein IN-MEMORY ueber den Index suchen: Standard-Felder UND
  // Custom Header (Name+Wert) - kein OData-Call beim Tippen. Danach auf den Status-Tab filtern.
  const iflowSearchActive=monitorDeepSearch.trim().length>=2;
  const statusMatchesTab=(l)=>{
    if(monitorLogFilter==="ALL") return true;
    if(monitorLogFilter==="FAILED") return (l.Status==="FAILED"||l.Status==="ERROR"||(eskModus==="fehler"&&l.Status==="ESCALATED"))&&!isAcked(l.MessageGuid);
    if(monitorLogFilter==="ACKED") return (l.Status==="FAILED"||l.Status==="ERROR"||(eskModus!=="aus"&&l.Status==="ESCALATED"))&&isAcked(l.MessageGuid);
    if(monitorLogFilter==="ESCALATED") return l.Status==="ESCALATED"&&!isAcked(l.MessageGuid);
    if(monitorLogFilter==="OTHER") return !["COMPLETED","FAILED","ERROR","PROCESSING"].includes(l.Status);
    return l.Status===monitorLogFilter;
  };
  const iflowSearchHits=(()=>{
    if(!iflowSearchActive) return [];
    const q=monitorDeepSearch.trim().toLowerCase();
    const hdrs=iflowIndex.headers||{};
    return (iflowIndex.runs||[]).filter(l=>{
      if([l.MessageGuid,l.CorrelationId,l.Sender,l.Receiver,l.ApplicationMessageId,l.ApplicationMessageType,l.CustomStatus,l.TransactionId].some(f=>f&&String(f).toLowerCase().includes(q))) return true;
      const hs=hdrs[l.MessageGuid]; return hs?hs.some(h=>String(h.n??"").toLowerCase().includes(q)||String(h.v??"").toLowerCase().includes(q)):false;
    }).map(l=>{
      const hs=hdrs[l.MessageGuid];
      const hit=hs&&hs.find(h=>String(h.n??"").toLowerCase().includes(q)||String(h.v??"").toLowerCase().includes(q));
      return hit?{...l,_matchHeader:`${hit.n}: ${hit.v}`}:l;
    }).filter(statusMatchesTab);
  })();
  // Bei aktiver Correlation-Suche zeigt der aufgeklappte iFlow NUR die Runs der Kette
  // (kommen aus der Correlation-Abfrage, kein Drilldown-Fetch noetig).
  const corrRuns=(()=>{
    if(!corrActive||!monitorExpanded)return null;
    let runs=[...(corrSearch.byFlow[monitorExpanded]?.messages||[])]
      .sort((a,b)=>(parseOD(b.LogEnd||b.LogStart)||0)-(parseOD(a.LogEnd||a.LogStart)||0))
      .filter(statusMatchesTab);
    if(monitorDeepSearch){
      const q=monitorDeepSearch.toLowerCase();
      runs=runs.filter(l=>[l.MessageGuid,l.CorrelationId,l.Sender,l.Receiver,l.ApplicationMessageId,l.CustomStatus].some(x=>x&&String(x).toLowerCase().includes(q)));
    }
    return runs;
  })();
  const displayLogs=corrRuns??(iflowSearchActive?iflowSearchHits:filteredMonitorLogs);
  // Bearbeiter ist nur bei bearbeiteten Eintraegen ein Wert - sonst entfaellt die Spalte.
  const zeigeBearbeiter=(displayLogs||[]).some(m=>m.isAcknowledged??isAcked(m.MessageGuid));

  // btnPrimary / btnOutline / btnGhost / btnAi are module-level constants (above component)

  // PI/PO grouping: Wenn Backend bereits gruppiert hat (groupBy != iflow), Buckets direkt verwenden.
  // Fallback: aus allMessages klientseitig bauen (z.B. bei alten Cache Daten).
  const buildGroups=()=>{
    if(groupBy!=="iflow"&&monitorData?.groupBy===groupBy&&Array.isArray(monitorData?.iflows)){
      return monitorData.iflows.map(b=>({
        key:b.name,
        total:b.total||0,
        failed:b.failed||0,
        completed:b.completed||0,
        processing:b.processing||0,
        lastRun:b.lastRun?parseOD(b.lastRun):null,
        messages:[],
      })).sort((a,b)=>(b.failed-a.failed)||(b.total-a.total));
    }
    const msgs=monitorData?.allMessages||[];
    const groups={};
    msgs.forEach(m=>{
      let key;
      // Fehlt der Wert, traegt die Gruppe eine Beschriftung statt eines Wertes. Der
      // Schluessel ist rein klientseitig und wird nie an den Server gegeben.
      if(groupBy==="sender") key=m.Sender||t("gruppen.ohneSender");
      else if(groupBy==="receiver") key=m.Receiver||t("gruppen.ohneReceiver");
      // Status bleibt der WERT der Schnittstelle, auch wenn er hier als Gruppe erscheint.
      else if(groupBy==="status") key=m.Status||"UNKNOWN";
      else if(groupBy==="channel") key=`${m.Sender||"?"}→${m.Receiver||"?"}`;
      else key=m.IntegrationFlowName||"?";
      if(!groups[key]) groups[key]={key,total:0,failed:0,completed:0,processing:0,lastRun:null,messages:[]};
      groups[key].total++;
      if(m.Status==="COMPLETED")groups[key].completed++;
      else if(m.Status==="FAILED"||m.Status==="ERROR")groups[key].failed++;
      else if(m.Status==="PROCESSING")groups[key].processing++;
      if(m.LogStart){const t=parseOD(m.LogStart);if(!groups[key].lastRun||t>groups[key].lastRun)groups[key].lastRun=t;}
      groups[key].messages.push(m);
    });
    return Object.values(groups).sort((a,b)=>(b.failed-a.failed)||(b.total-a.total));
  };

  return(
    <div style={{animation:"fadeIn 0.3s ease"}}>
      {/* Tabs - top level */}
      <div style={{display:"flex",gap:0,borderBottom:`1px solid ${v("border")}`,marginBottom:20,flexWrap:"wrap"}}>
        {[{k:"monitor",l:t("kopf.reiterMonitor")},{k:"content",l:t("kopf.reiterContent")}].map(x=>(
          <button key={x.k} onClick={()=>{
            setTab(x.k);setView("overview");setSel(null);
            if(x.k==="monitor")loadMonitor();
            const base="/"+window.location.pathname.split("/").filter(Boolean).slice(0,3).join("/");
            window.history.pushState({},document.title,x.k==="content"?base+"/content":base);
          }} style={{
            padding:"10px 20px",border:"none",background:"none",fontSize:13,cursor:"pointer",fontFamily:FONT,
            fontWeight:tab===x.k?600:400,color:tab===x.k?v("text-1"):v("text-3"),
            borderBottom:`2px solid ${tab===x.k?v("accent"):"transparent"}`,transition:"all .15s"
          }}>{x.l}</button>
        ))}
      </div>

      {error&&<div style={{padding:"10px 16px",borderRadius:8,background:`${v("danger")}08`,border:`1px solid ${v("danger")}20`,color:v("danger"),fontSize:12,marginBottom:16}}>{t("meldung.fehlerMit",{text:error})}</div>}
      {refreshInfo&&(
        <div style={{
          padding:"10px 16px",borderRadius:8,fontSize:12,marginBottom:16,
          background:refreshInfo.type==="error"?`${v("danger")}10`:refreshInfo.type==="info"?`${v("warning")}12`:`${v("success")}10`,
          border:`1px solid ${refreshInfo.type==="error"?v("danger"):refreshInfo.type==="info"?v("warning"):v("success")}40`,
          color:refreshInfo.type==="error"?v("danger"):refreshInfo.type==="info"?v("warning"):v("success"),
        }}>{refreshInfo.text}</div>
      )}

      {/* === MESSAGE MONITOR TAB (PI Monitor style) === */}
      {tab==="monitor"&&<>


        {/* === R1 Stale/Worker-Banner === */}
        {!aktiveEdge && monitorData?._meta?.ageMs > 180000 && (
          <div className="iio-mon2-banner">
            <span className="bar"/>
            <span className="grow">
              {t("banner.datenAlt",{count:Math.floor(monitorData._meta.ageMs/60000)})}{" "}
              {monitorData._meta.computing
                ? <em>{t("banner.wirdBerechnet")}</em>
                : t("banner.pausiert")}
            </span>
            <button className="btn btn--signal-text btn--sm" onClick={manualRefresh}>{t("gemeinsam:aktion.aktualisieren")}</button>
          </div>
        )}

        {/* === Z2 Kontroll-Block: KPIs + Haarlinie + Kontroll-Bank (oder Bulk-Bar) === */}
        {(()=>{
          // Identitaet + Frischezustand sind Teil derselben Kommandoleiste - frueher
          // waren es zwei Container mit 56px Abstand, der Blick musste springen.
          const ts=monitorData?._meta?.computedAt;
          const ageMs=ts?headerNow-ts:null;
          // High-Volume-Instanzen liefern eine Frische JE KLASSE. Dann trägt das Fehler-Delta
          // den Zustand, denn das ist die Klasse, die in Echtzeit laufen muss. Ein alterndes
          // Zahlenwerk darf den Kopf nicht auf "Veraltet" ziehen, solange die Fehler frisch
          // sind, und ein frischer Snapshot darf keine Frische für die Fehler behaupten.
          // Der Snapshot kann neuer sein als das letzte leichte Ereignis und umgekehrt -
          // deshalb den jüngeren von beiden nehmen, nicht einen von beiden bevorzugen.
          const hvA=monitorStand,hvB=monitorData?._hv;
          const hvF=!hvA?hvB||null:!hvB?hvA:((hvA.fehlerDeltaAt||0)>=(hvB.fehlerDeltaAt||0)?hvA:hvB);
          const fehlerAlter=hvF?.fehlerDeltaAt?headerNow-hvF.fehlerDeltaAt:null;
          const zahlenAlter=hvF?.globalAt?headerNow-hvF.globalAt:null;
          const leitAlter=fehlerAlter!=null?fehlerAlter:ageMs;
          // Zweites Alter nur nennen, wenn es sich wirklich unterscheidet - sonst Rauschen.
          const zahlenZusatz=(zahlenAlter!=null&&fehlerAlter!=null&&zahlenAlter-fehlerAlter>60000)
            ?` · ${t("frische.zahlenAlter",{alter:relAge(zahlenAlter,t)})}`:"";
          // Der Grund gehoert dazu: ein nacktes "Verzoegert" laesst den Nutzer raten, ob die
          // Webapp klemmt oder der Tenant. satKlasse kommt aus der Fehlerklassifikation.
          const drosselZusatz=hvF?.saettigung
            ?` · ${hvF?.satKlasse==="saettigung"?t("frische.poolKnapp"):hvF?.satKlasse==="zu_teuer"?t("frische.zaeh"):t("frische.begrenzt")}`:"";
          // Paritaetswaechter hat eine Abweichung gemessen: der vorgezogene Vollabgleich laeuft.
          const paritaetZusatz=hvF?.paritaet?` · ${t("frische.abgleich")}`:"";
          const liveState=leitAlter==null?"warn":leitAlter<90000?"ok":leitAlter<180000?"warn":"crit";
          // Dauer bis zum naechsten Pull: bevorzugt die Server-Angabe, sonst der
          // gemeldete Takt, sonst gar keine Leiste (lieber nichts als etwas Falsches).
          const naechsterLauf=monitorData?._meta?.nextRunAt||null;
          const zyklusMs=(naechsterLauf&&ts&&naechsterLauf>ts)?(naechsterLauf-ts)
            :(monitorData?._meta?.pollDelayMs||0);
          // Der Worker taktet adaptiv - im aktiven Count-Modus alle 5s. Ein Countdown
          // waere dort reines Flackern: unter 20s Zyklus tragen Zustand und "gerade
          // eben" die Aussage allein, die Leiste bleibt weg.
          const pullDauerMs=zyklusMs>=20000?zyklusMs:0;
          const zusatz=zahlenZusatz+drosselZusatz+paritaetZusatz;
          const zustand = !ts ? {mod:"idle",label:t("frische.verbinde"),sub:t("frische.wirdGeladen")}
            : sseLost ? {mod:"crit",label:t("frische.unterbrochen"),sub:t("frische.letzterStand",{alter:relAge(leitAlter,t)})}
            : liveState==="ok" ? {mod:"ok",label:t("frische.live"),sub:relAge(leitAlter,t)+zusatz}
            : liveState==="warn" ? {mod:"warn",label:t("frische.verzoegert"),sub:relAge(leitAlter,t)+zusatz}
            : {mod:"crit",label:t("frische.veraltet"),sub:relAge(leitAlter,t)+zusatz};
          // Identitaet und Laufzeit-Wahl getrennt von der Frische-Anzeige: bei gewaehlter
          // Edge traegt nur die Identitaet - die Frische beschreibt den Cloud-Worker und
          // wuerde dort einen Live-Zustand behaupten, den es fuer die Edge nicht gibt.
          const identCore=<div className="iio-mon2-kontext">
            <div className="iio-mon2-ident">
              {/* title: der Titel kuerzt seit dem Umbau per Ellipsis, sobald die Kennzahlen
                  rechts den Platz nehmen - der volle Instanzname darf dabei nicht
                  unerreichbar werden. */}
              <h1 title={instance.name||instance.id}>{instance.name||instance.id}</h1>
              <span className="id-meta">{[instance.region,instance.suiteStack||"Integration Suite"].filter(Boolean).join(" · ")}</span>
            </div>
            <span className="iio-mon2-hair"/>
            {/* Laufzeit-Wahl wie in der Integration Suite - immer sichtbar, Cloud ist der
                Standard (Davids Ansage 18.08.: das Dropdown gehoert fest in den Monitor).
                Steht NEBEN der Identitaet, nicht darunter: es beantwortet dieselbe Frage
                ("welches System sehe ich gerade"), und unter dem Titel brach sein
                Innenpolster die Schrift-Fluchtlinie der Karte. */}
            <select className="iio-mon2-runtime" aria-label={t("runtime.wahl")}
              value={aktiveEdge?monitorRuntime:"cloud"} onChange={e=>setMonitorRuntime(e.target.value)}>
              <option value="cloud">{t("runtime.cloud")}</option>
              {edgeRuntimes.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>;
          if(aktiveEdge) return <div className="iio-mon2-cmd">
            <div className="iio-mon2-cmd-a">{identCore}</div>
            <div className="iio-mon2-edgeleer" role="status">
              <span className="ttl">{t("runtime.edgeLeerTitel")}</span>
              <span className="txt">{t("runtime.edgeLeerText",{name:aktiveEdge.name})}</span>
            </div>
          </div>;
          const identBlock=<>
            {identCore}
            <span className="iio-mon2-hair"/>
            <div className={`iio-mon2-fresh iio-mon2-fresh--${zustand.mod}`} title={ts?t("gemeinsam:zeit.stand",{zeit:new Intl.DateTimeFormat(gebiet,{dateStyle:"medium",timeStyle:"medium"}).format(new Date(ts))}):t("frische.wirdGeladenLang")}>
              <span className="bar"/>
              <div className="txt">
                <span className="l">{zustand.label}</span>
                <span className="s">{refreshInfo&&refreshInfo.type!=="error"?refreshInfo.text:zustand.sub}</span>
                {/* Laeuft exakt bis zum naechsten geplanten Worker-Lauf: der Server
                    meldet nextRunAt, der Takt ist adaptiv (5s bis 300s) und darf nicht
                    geraten werden. Neustart nur bei neuem Datenstand -> keine Spruenge. */}
                {(zustand.mod==="ok"||zustand.mod==="warn")&&pullDauerMs>0&&
                  <span key={ts||0} className="iio-mon2-fresh-progress" aria-hidden="true"
                    style={{animationDuration:`${pullDauerMs}ms`}}/>}
              </div>
            </div>
          </>;
          // scopeMode bleibt die KENNUNG ("1h", "7d", …), nur die Beschriftung wird gesetzt.
          const scopeLabel=scopeMode==="1h"?t("zeitfenster.1h"):scopeMode==="6h"?t("zeitfenster.6h"):scopeMode==="24h"?t("zeitfenster.24h"):scopeMode==="7d"?t("zeitfenster.7d"):scopeMode==="30d"?t("zeitfenster.30d"):t("zeitfenster.eigen");
          const queueCount=instance.hasPipelineConcept?(monitorData?.allMessages||[]).filter(m=>{
            const sender=(m.Sender||"").toUpperCase();const receiver=(m.Receiver||"").toUpperCase();
            return (/PIPQ|PIPX/.test(sender)||/PIPQ|PIPX/.test(receiver))&&m.Status!=="COMPLETED";
          }).length:0;
          const failedFlows=(scopedIflows||[]).filter(f=>failureCountsForFlow(f).failed>0).length;
          // Ehrliche Erfolgszahl: echtes COMPLETED (tenant-wahrer $count bzw. Zeilensumme).
          // "total - failed" entsprach SAPs TOTAL und zaehlte Verworfene/Laufende/Retries mit.
          const okTotal=Math.max(0,monitorTotals.completed||0);
          const effFailedTotal=Math.max(0,(monitorTotals.failed||0)-ackedVisible+failureCountAdjustment);
          const kpis=[
            // Klick auf die Erfolgs-KPI oeffnet den Aufriss (ersetzt den frueheren Filter-Toggle
            // dieser Kachel - ein Ziel, ein Bedienelement).
            // Subzeile: die EXAKTE Nachrichtenzahl zur gerundeten Grosszahl - iFlow-Anzahl und
            // Verworfen-Zusatz stehen im Aufriss, nicht mehr hier (Davids Wunsch 17.08.).
            {key:"total",label:t("kpi.erfolgreich"),scope:scopeLabel,val:okTotal,sub:scopedLoading?t("frische.wirdGeladen"):t("kpi.nachrichten",{anzahl:zahl(okTotal)}),crit:false,aufriss:true},
            // Die Fehler-KPI ist eine reine Anzeige (Davids Wunsch 17.08.): der Klick-Filter
            // bot keinen Mehrwert, "Nur Fehler" gibt es weiterhin im Statusfilter-Dropdown.
            {key:"fail",label:eskModus==="fehler"?t("kpi.fehlerMitEsk"):t("kpi.fehler"),scope:scopeLabel,val:effFailedTotal,sub:(monitorData?.fehlerCap?.capped?`${t("kpi.gedeckelt")} · `:"")+(eskModus==="fehler"&&eskImScope>0?t("kpi.inklEsk",{count:eskImScope}):failedFlows>0?t("kpi.iflows",{count:failedFlows}):ackedVisible>0?t("kpi.bearbeitet",{count:ackedVisible}):t("kpi.keineFehler")),crit:true},
            ...(instance.hasPipelineConcept?[{key:"queue",label:t("kpi.inQueues"),scope:"",val:queueCount,sub:queueCount>0?t("kpi.pipelineAktiv"):t("kpi.keine"),crit:false}]:[]),
          ];
          // Bulk-Bar: welche der selektierten Runs sind (noch nicht) als bearbeitet markierte Fehler?
          const msgByGuid={};
          const collect=m=>{if(m&&m.MessageGuid&&!msgByGuid[m.MessageGuid])msgByGuid[m.MessageGuid]=m;};
          (monitorFilterReload?.logs||[]).forEach(collect);(monitorLogs||[]).forEach(collect);(iflowIndex.runs||[]).forEach(collect);(monitorData?.allMessages||[]).forEach(collect);
          const selMsgs=Array.from(selectedGuids).map(g=>msgByGuid[g]).filter(Boolean);
          // Abhakbar sind Fehler und - sobald die Ansicht sie zeigt - eskalierte Laeufe. Ist ESCALATED
          // ausgeblendet, bleibt es auch nicht abhakbar: eine unsichtbare Zahl abzuhaken waere sinnlos.
          const abhakbar=(m)=>m.Status==="FAILED"||m.Status==="ERROR"||(eskModus!=="aus"&&m.Status==="ESCALATED");
          const ackableSel=selMsgs.filter(m=>abhakbar(m)&&!(m.isAcknowledged??isAcked(m.MessageGuid)));
          const unackableSel=selMsgs.filter(m=>abhakbar(m)&&(m.isAcknowledged??isAcked(m.MessageGuid)));
          return <div className="iio-mon2-cmd">
            <div className="iio-mon2-cmd-a">
              {identBlock}
              {/* Schub: schiebt die Kennzahlen an die rechte Fluchtlinie und legt den
                  freien Raum zwischen Kontext- und Kennzahlenzone. */}
              <span className="iio-mon2-hair iio-mon2-hair--schub"/>
              <div className="iio-mon2-summary">
                {kpis.map(k=>{
                  // Nur die Erfolgs-KPI ist klickbar (Aufriss) - alle anderen sind reine Anzeige.
                  const active=k.aufriss&&kpiAufriss;
                  const El=k.aufriss?"button":"div";
                  return <El key={k.key} className={`iio-mon2-kpi${active?" is-active":""}${k.aufriss?"":" is-static"}`}
                    {...(k.aufriss?{onClick:()=>{setKpiAufriss(v=>!v);setAufrissSel(null);},"aria-expanded":kpiAufriss,title:t("aufriss.hilfe")}:{})}>
                    <div className="iio-mon2-kpi-l">{k.label}{k.aufriss&&<svg className={`iio-mon2-kpi-chev${kpiAufriss?" is-open":""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>}<i>{k.scope}</i></div>
                    <div className="iio-mon2-kpi-row">
                      {/* Rot nur bei echtem Fehlerbestand - eine dauerhaft rote Null macht blind */}
                      <span className={`iio-mon2-kpi-v${k.crit&&k.val>0?" iio-mon2-kpi-v--crit":""}`} title={zahl(k.val)}>{fmt(k.val)}</span>
                      {/* title: die Subzeile kuerzt per Ellipsis - gerade die Deckelungs-
                          Warnung darf dabei nicht unlesbar verloren gehen. */}
                      <span className="iio-mon2-kpi-s" title={k.sub}>{k.sub}</span>
                    </div>
                  </El>;
                })}
              </div>
            </div>
            {selectedGuids.size>0?(
              /* Bulk-Bar ersetzt die Kontroll-Bank innerhalb des Blocks */
              <div className="iio-mon2-bulk">
                <div className="iio-mon2-bulk-l">
                  <span className="iio-mon2-bulk-count">{t("bulk.ausgewaehlt",{count:selectedGuids.size})}</span>
                  {/* Information statt Bedienungsanleitung: erklaert die Zaehler an den Buttons */}
                  <span className="iio-mon2-bulk-class">{[ackableSel.length?t("bulk.offen",{count:ackableSel.length}):null,unackableSel.length?t("bulk.bereitsBearbeitet",{count:unackableSel.length}):null].filter(Boolean).join(" · ")||t("bulk.nichtsAbhakbar")}</span>
                  <button className="btn btn--tertiary btn--sm" onClick={clearSelection}>{t("bulk.auswahlAufheben")}</button>
                </div>
                <div className="iio-mon2-bulk-right">
                  {unackableSel.length>0&&<button className="btn btn--tertiary btn--sm" title={t("bulk.zuruecknehmenHilfe")} onClick={()=>unackMessages(unackableSel.map(m=>m.MessageGuid))}>
                    {t("bulk.zuruecknehmen",{count:unackableSel.length})}
                  </button>}
                  {instance.hasPipelineConcept&&(()=>{
                    const guids=Array.from(selectedGuids);
                    const ziel=(monitorData?.allMessages||monitorLogs).filter(m=>guids.includes(m.MessageGuid)&&isPipelineMsg(m));
                    if(!ziel.length) return null;
                    // Wirkt im Tenant - daher zweistufig statt Ein-Klick.
                    return <button className={`btn btn--secondary btn--sm${retryConfirm?" is-armed":""}`}
                      title={t("bulk.erneutHilfe")}
                      onClick={()=>{ if(!retryConfirm){setRetryConfirm(true);setTimeout(()=>setRetryConfirm(false),5000);return;} setRetryConfirm(false); ziel.forEach(m=>triggerRetry(m)); }}>
                      {retryConfirm?t("bulk.erneutBestaetigen",{count:ziel.length}):t("bulk.erneut",{count:ziel.length})}
                    </button>;
                  })()}
                  <button className="btn btn--ai btn--sm" disabled={!aiEnabled||bulkAnalysis?.status==="loading"} title={aiEnabled?undefined:t("ki.nichtAktiviertKurz")} onClick={runBulkAiAnalysis}>
                    {bulkAnalysis?.status==="loading"
                      ? (bulkAnalysis.stage==="preload"?t("ki.ladeDetails"):bulkAnalysis.stage==="precheck"?t("ki.preCheckLaeuft"):t("ki.analysiere"))
                      : t("ki.starten")}
                  </button>
                  {ackableSel.length>0&&<button className="btn btn--signal btn--sm" title={t("bulk.markierenHilfe")} onClick={()=>ackMessages(ackableSel.map(m=>({guid:m.MessageGuid,flow:m.IntegrationFlowName||"",art:m.Status==="ESCALATED"?"eskaliert":"fehler",time:parseOD(m.LogEnd||m.LogStart)||Date.now()})))}>
                    {t("bulk.markieren",{count:ackableSel.length})}
                  </button>}
                </div>
              </div>
            ):(
              /* Kontroll-Bank: Suche (geschlossene Einheit) + Filterbank (eine Bank) */
              <div className="iio-mon2-bank">
                <div className="iio-mon2-search">
                  <div className="iio-mon2-search-seg">
                    <button className={monitorSearchMode==="iflow"?"is-active":""} onClick={()=>{setMonitorSearchMode("iflow");setMonitorSearch("");}}>{t("suche.modusName")}</button>
                    <button className={monitorSearchMode==="content"?"is-active":""} onClick={()=>{setMonitorSearchMode("content");setMonitorSearch("");}}>{t("suche.modusInhalt")}</button>
                  </div>
                  <div className="iio-mon2-search-field">
                    <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input placeholder={monitorSearchMode==="iflow"?t("suche.platzhalterName"):t("suche.platzhalterInhalt")} value={monitorSearch} onChange={e=>setMonitorSearch(e.target.value)}/>
                    <span className="iio-mon2-search-info" tabIndex={0} aria-label={t("suche.hilfe")}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12" strokeLinecap="round"/><circle cx="12" cy="8" r="1.05" fill="currentColor" stroke="none"/></svg>
                      <span className="iio-mon2-search-tip">{t("suche.hinweis")}</span>
                    </span>
                    {monitorSearchMode==="content"&&monitorSearch.length>0&&monitorSearch.length<2&&<span className="iio-mon2-search-hint">{t("suche.mindestens")}</span>}
                  </div>
                </div>
                <span className="iio-mon2-hair iio-mon2-hair--schub"/>
                <div className="iio-mon2-filterbank">
                  {(()=>{
                    // Nur Zeitraeume anbieten, die der Worker auch verwaltet (monitorHours = workerHours).
                    // v ist die KENNUNG des Zeitfensters und bleibt unveraendert, l die Beschriftung.
                    const ALL=[{v:"1h",l:t("zeitfenster.1h"),h:1},{v:"6h",l:t("zeitfenster.6h"),h:6},{v:"24h",l:t("zeitfenster.24h"),h:24},{v:"7d",l:t("zeitfenster.7d"),h:168},{v:"30d",l:t("zeitfenster.30d"),h:720}];
                    const opts=ALL.filter(s=>s.h<=workerHours);
                    return <div className="iio-mon2-seg" role="radiogroup" aria-label={t("zeitfenster.gruppe")}>
                      {opts.map(o=><button key={o.v} type="button" role="radio" aria-checked={scopeMode===o.v}
                        className={scopeMode===o.v?"is-active":""}
                        onClick={()=>{setScopeMode(o.v);setMonitorExpanded(null);setMonitorLogs([]);}}>{o.l}</button>)}
                    </div>;
                  })()}
                  {/* Auswahl der Ansicht: zwei Listen, die sich beruehren duerfen, weil sie
                      dieselbe Frage beantworten (was sehe ich, in welcher Reihenfolge).
                      Zeitfenster und Aktualisieren stehen bewusst getrennt daneben. */}
                  <div className="iio-mon2-fb-group">
                  <select className="iio-mon2-fb-seg" aria-label={t("statusfilter.aria")} value={statFilter||statusFilter} onChange={e=>{
                    const val=e.target.value;
                    if(val==="fail"){setStatFilter("fail");setStatusFilter("ALL");}
                    else{setStatFilter(null);setStatusFilter(val);}
                  }}>
                    {/* Die Werte ALL, fail, COMPLETED, PROCESSING sind Kennungen bzw. Status-Werte
                        der Schnittstelle und bleiben woertlich - nur die Beschriftung wechselt. */}
                    <option value="ALL">{t("statusfilter.alle")}</option><option value="fail">{t("statusfilter.nurFehler")}</option><option value="COMPLETED">{t("statusfilter.completed")}</option><option value="PROCESSING">{t("statusfilter.processing")}</option>
                  </select>
                  <select className="iio-mon2-fb-seg" aria-label={t("sortierung.aria")} value={monitorSort} onChange={e=>setMonitorSort(e.target.value)}>
                    {/* Sortier-Kennungen (failed_desc, name_asc, …) bleiben unveraendert. */}
                    <option value="failed_desc">{t("sortierung.fehlerZuerst")}</option><option value="last_failed">{t("sortierung.zuletztAktiv")}</option><option value="total_desc">{t("sortierung.volumen")}</option><option value="completed_desc">{t("sortierung.okAbsteigend")}</option><option value="name_asc">{t("sortierung.name")}</option>
                  </select>
                  </div>
                  <button className={`iio-mon2-fb-refresh${refreshSpin?" is-spinning":""}`} title={t("filter.neuAbrufen")} onClick={()=>{setRefreshSpin(true);setTimeout(()=>setRefreshSpin(false),700);manualRefresh();}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
              </div>
            )}
            {/* Aufriss: Zusammensetzung der Gesamtzahl nach Status, Quelle identisch zu den
                KPIs (tenant-wahre Totals; bei Suche/7d/30d die Summe der sichtbaren Zeilen). */}
            {kpiAufriss&&(()=>{
              const basis=globalScopeTotals||(monitorIflows||[]).reduce((a,f)=>({
                completed:a.completed+(f.completed||0),failed:a.failed+(f.failed||0),retry:a.retry+(f.retry||0),
                processing:a.processing+(f.processing||0),escalated:a.escalated+(f.escalated||0),
                discarded:a.discarded+(f.discarded||0),other:a.other+(f.other||0),total:a.total+(f.total||0),
              }),{completed:0,failed:0,retry:0,processing:0,escalated:0,discarded:0,other:0,total:0});
              // "retry" ist die Obermenge RETRY+ESCALATED (cwKind); im Aufriss stehen beide
              // getrennt wie im SAP-Monitor - Retry hier also ohne die Eskalationen.
              const retryRein=Math.max(0,(basis.retry||0)-(basis.escalated||0));
              const sonstige=Math.max(0,(basis.other||0)-(basis.discarded||0));
              const spalten=[
                {l:t("aufriss.fehler"),tip:t("aufriss.tipFehler"),v:basis.failed||0,c:"crit"},
                {l:t("aufriss.retry"),tip:t("aufriss.tipRetry"),v:retryRein,c:"warn"},
                {l:t("aufriss.erfolgreich"),tip:t("aufriss.tipErfolgreich"),v:basis.completed||0,c:"ok"},
                {l:t("aufriss.verarbeitung"),tip:t("aufriss.tipVerarbeitung"),v:basis.processing||0,c:""},
                {l:t("aufriss.eskaliert"),tip:t("aufriss.tipEskaliert"),v:basis.escalated||0,c:"warn"},
                {l:t("aufriss.verworfen"),tip:t("aufriss.tipVerworfen"),v:basis.discarded||0,c:""},
                ...(sonstige>0?[{l:t("aufriss.sonstige"),tip:t("aufriss.tipSonstige"),v:sonstige,c:""}]:[]),
                {l:t("aufriss.gesamt"),tip:t("aufriss.tipGesamt"),v:basis.total||0,c:"sum"},
              ];
              return <div className="iio-mon2-aufriss" role="region" aria-label={t("aufriss.titel")}>
                <div className="iio-mon2-aufriss-head">
                  <span className="ttl">{t("aufriss.titel")}</span>
                  <span className="meta">{scopeLabel}{!globalScopeTotals?` · ${t("aufriss.zeilenbasis")}`:""}</span>
                </div>
                <div className="iio-mon2-aufriss-tab" style={{gridTemplateColumns:`repeat(${spalten.length},minmax(96px,1fr))`}}>
                  {/* Je Status eine klickbare Zelle (Kopf + Wert), Trennstriche ueber die volle
                      Zellhoehe. Ein Klick zeigt die Erklaerung des Status unter der Tabelle,
                      ein zweiter Klick blendet sie wieder aus. */}
                  {spalten.map((s,si)=><button key={s.l} type="button"
                    className={`col${si===spalten.length-1?" is-sum":""}${aufrissSel===s.l?" is-active":""}`}
                    aria-pressed={aufrissSel===s.l} title={t("aufriss.zelleHilfe")}
                    onClick={()=>setAufrissSel(p=>p===s.l?null:s.l)}>
                    <span className="h">{s.l}</span>
                    {/* Farbklasse nur bei echtem Bestand - eine rote Null macht blind */}
                    <span className={`v${s.c&&(s.v>0||s.c==="sum")?" "+s.c:""}`}>{zahl(s.v)}</span>
                  </button>)}
                </div>
                {/* Erklaerzeile der angeklickten Zelle: volle Breite, bricht normal um */}
                {(()=>{const sel=spalten.find(x=>x.l===aufrissSel);return sel
                  ?<div className="iio-mon2-aufriss-erkl" role="status"><b>{sel.l}</b>{sel.tip}</div>
                  :null;})()}
              </div>;
            })()}
          </div>;
        })()}

        {/* Bulk-Analyse Result-Card */}
        {!aktiveEdge&&bulkAnalysis&&(
          <div className="iio-mon2-ai">
            <div className="iio-mon2-ai-head">
              <span className="ttl">{t("ki.bulkTitel")}</span>
              {bulkAnalysis.status==="done"&&<span className="meta">· {t("ki.bulkMeta",{messages:bulkAnalysis.msgCount,flows:bulkAnalysis.flowCount})} · {bulkAnalysis.phase==="clustered"?t("ki.clusterAnzahl",{count:bulkAnalysis.preCheck?.clusters?.length||0}):t("ki.keineCluster")}</span>}
              {bulkAnalysis.status==="loading"&&<span className="meta">· {bulkAnalysis.stage==="preload"?t("ki.stufeDetails"):bulkAnalysis.stage==="precheck"?t("ki.stufePreCheck"):t("ki.stufeTiefe")}</span>}
              <button className="x" onClick={()=>{if(bulkAbortRef.current){try{bulkAbortRef.current.abort();}catch{}}setBulkAnalysis(null);}} title={t("gemeinsam:aktion.schliessen")}>✕</button>
            </div>
            {bulkAnalysis.status==="error"&&<div className="iio-mon2-ai-warn" style={{borderColor:v("danger"),color:v("danger")}}>{t("meldung.fehlerMit",{text:bulkAnalysis.error})}</div>}
            {bulkAnalysis.status==="loading"&&(
              <div className="iio-mon2-ai-stages">
                {[
                  {k:"preload",l:t("ki.stufeDetailsLang"),done:["precheck","analysis","done"].includes(bulkAnalysis.stage)},
                  {k:"precheck",l:t("ki.stufePreCheckLang"),done:["analysis","done"].includes(bulkAnalysis.stage)},
                  {k:"analysis",l:bulkAnalysis.preCheck?.hasCorrelation?t("ki.stufeCluster"):t("ki.stufeEinzelfall"),done:bulkAnalysis.stage==="done"},
                ].map(s=>(
                  <div key={s.k} className={`iio-mon2-ai-stage${s.done?" done":bulkAnalysis.stage===s.k?" active":""}`}>
                    <span className="bar"/>{s.l}{s.done?` ${t("ki.erledigt")}`:bulkAnalysis.stage===s.k?` ${t("ki.laeuft")}`:""}
                  </div>
                ))}
              </div>
            )}
            {bulkAnalysis.preCheck&&bulkAnalysis.stage==="analysis"&&bulkAnalysis.status==="loading"&&(
              <div className="iio-mon2-ai-note">{t("ki.preCheck")}: {bulkAnalysis.preCheck.hasCorrelation?`${t("ki.clusterErkannt",{count:bulkAnalysis.preCheck.clusters.length})} - `:`${t("ki.keinMuster")} - `}{bulkAnalysis.preCheck.reasoning}</div>
            )}
            {bulkAnalysis.status==="done"&&(<>
              {bulkAnalysis.preCheck&&<div className="iio-mon2-ai-note" style={{fontStyle:"normal"}}><b style={{color:v("text-1")}}>{t("ki.preCheck")}: </b>{bulkAnalysis.preCheck.reasoning}</div>}
              {bulkAnalysis.sampled&&<div className="iio-mon2-ai-warn">{t("ki.stichprobe",{teil:bulkAnalysis.sampled,gesamt:bulkAnalysis.msgCount})}</div>}
              <div className="iio-mon2-ai-result" dangerouslySetInnerHTML={{__html:renderChatMd(bulkAnalysis.analysisText||"")}}/>
            </>)}
          </div>
        )}

        {/* === Z3 Sub-Tabs === */}
        {!aktiveEdge&&<div className="iio-mon2-subtabs">
          <button className={`iio-mon2-subtab${monitorSubTab==="monitor"?" is-active":""}`} onClick={()=>setMonitorSubTab("monitor")}>
            {t("unterreiter.monitor")}
          </button>
          {instance.hasPipelineConcept&&<button className={`iio-mon2-subtab${monitorSubTab==="pipeline"?" is-active":""}`} onClick={()=>setMonitorSubTab("pipeline")}>{t("unterreiter.pipeline")}</button>}
          <button className={`iio-mon2-subtab${monitorSubTab==="analytics"?" is-active":""}`} onClick={()=>setMonitorSubTab("analytics")}>{t("unterreiter.analytics")}</button>
          {monitorSubTab==="monitor"&&<span className="iio-mon2-anp">
            {/* Sichtbare Rueckmeldung neben dem Zahnrad: eine abweichende Einstellung aendert die
                Bedeutung der Fehlerzahl. Ohne Beschriftung waere das eine Blackbox - besonders
                fuer einen Kollegen, der nur auf den Bildschirm schaut. */}
            {eskModus!=="aus"&&<button className="lbl" onClick={()=>setAnpOffen(o=>!o)}>
              ESCALATED: {eskModus==="spalte"?t("anpassen.kurzSpalte"):t("anpassen.kurzFehler")}
            </button>}
            <button className={`zr${eskModus!=="aus"?" an":""}`} title={t("anpassen.zahnrad")}
              aria-expanded={anpOffen} onClick={()=>setAnpOffen(o=>!o)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.6.65 1.04 1.27 1.09H21a2 2 0 0 1 0 4h-.09c-.62.05-1.13.49-1.27 1.09z"/>
              </svg>
            </button>
            {anpOffen&&<>
              <span className="iio-mon2-anp-weg" onClick={()=>setAnpOffen(false)}/>
              <div className="iio-mon2-anp-pop">
                <div className="h">{t("anpassen.titel")}</div>
                {/* k ist die Kennung, die gespeichert wird ("aus", "spalte", "fehler") -
                    sie bleibt woertlich, nur Titel und Erklaerung kommen aus dem Katalog. */}
                {[
                  {k:"aus",titel:t("anpassen.ausTitel"),erklaerung:t("anpassen.ausText")},
                  {k:"spalte",titel:t("anpassen.spalteTitel"),erklaerung:t("anpassen.spalteText")},
                  {k:"fehler",titel:t("anpassen.fehlerTitel"),erklaerung:t("anpassen.fehlerText")},
                ].map(o=>(
                  <button key={o.k} className={`opt${eskModus===o.k?" an":""}`} onClick={()=>{setzeEskModus(o.k);setAnpOffen(false);}}>
                    <span className="rd" aria-hidden="true"/>
                    <span className="tx"><b>{o.titel}</b><span>{o.erklaerung}</span></span>
                  </button>
                ))}
                <div className="f">{t("anpassen.fuss")}</div>
              </div>
            </>}
          </span>}
        </div>}

        {/* Predictive & Analytics sub-tab */}
        {!aktiveEdge&&monitorSubTab==="analytics"&&<div className="iio-placeholder">{t("unterreiter.analyticsPlatzhalter")}</div>}

        {/* Pipeline & Queues sub-tab */}
        {!aktiveEdge&&monitorSubTab==="pipeline"&&<div className="iio-placeholder">{t("unterreiter.pipelinePlatzhalter")}</div>}

        {/* Monitor sub-tab content */}
        {!aktiveEdge&&monitorSubTab==="monitor"&&<>

        {monitorLoading&&!monitorData?<div style={{display:"flex",flexDirection:"column",gap:4}}>{[...Array(6)].map((_,i)=><div key={i} className="iio-skeleton iio-mon2-skel"/>)}</div>:

        /* ── Pipeline-Modus: CorrelationId-Gruppierung ── */
        pipelineMode&&instance.hasPipelineConcept?(()=>{
          const allMsgs=monitorData?.allMessages||[];
          // Nur Pipeline-relevante Messages (enthält "pipeline" im iFlow-Namen oder PIPQ Sender/Receiver)
          const pipelineMsgs=allMsgs.filter(m=>isPipelineMsg(m));
          if(!pipelineMsgs.length) return <div style={{padding:40,textAlign:"center",color:v("text-3"),fontSize:13}}>{t("pipeline.keineNachrichten")}</div>;
          // Gruppieren nach CorrelationId
          const groups={};
          for(const m of pipelineMsgs){
            const cid=m.CorrelationId||m.MessageGuid;
            if(!groups[cid])groups[cid]={cid,msgs:[],failed:0,completed:0};
            groups[cid].msgs.push(m);
            if(m.Status==="FAILED"||m.Status==="ERROR")groups[cid].failed++;
            if(m.Status==="COMPLETED")groups[cid].completed++;
          }
          const groupList=Object.values(groups).sort((a,b)=>b.failed-a.failed||(b.msgs.length-a.msgs.length));
          const filtered=monitorSearch?groupList.filter(g=>g.cid.toLowerCase().includes(monitorSearch.toLowerCase())||g.msgs.some(m=>(m.IntegrationFlowName||"").toLowerCase().includes(monitorSearch.toLowerCase())||flowLabel(m.IntegrationFlowName||"").toLowerCase().includes(monitorSearch.toLowerCase()))):groupList;
          return <div style={{borderRadius:8,border:`1px solid ${v("border")}`,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"28px 1fr 200px 80px 80px",padding:"8px 16px",background:v("surface-2"),fontSize:10,fontWeight:600,color:v("text-3"),textTransform:"uppercase",letterSpacing:.6}}>
              <div/><div>{t("pipeline.spalteCid")}</div><div>{t("pipeline.spalteStages")}</div><div>{t("pipeline.spalteNachrichten")}</div><div>{t("pipeline.spalteStatus")}</div>
            </div>
            {filtered.slice(0,100).map(g=>{
              const isExp=pipelineExpanded===g.cid;
              const overallStatus=g.failed>0?"FAILED":g.completed===g.msgs.length?"COMPLETED":"PROCESSING";
              // Sort stages by LogStart
              const sorted=[...g.msgs].sort((a,b)=>(parseOD(a.LogStart)||0)-(parseOD(b.LogStart)||0));
              const stageNames=[...new Set(sorted.map(m=>m.IntegrationFlowName||"?"))];
              return <div key={g.cid}>
                <div onClick={()=>setPipelineExpanded(isExp?null:g.cid)} style={{display:"grid",gridTemplateColumns:"28px 1fr 200px 80px 80px",padding:"10px 16px",borderTop:`1px solid ${v("border-subtle")}`,alignItems:"center",cursor:"pointer",fontSize:11,background:isExp?v("surface-1"):"transparent"}}
                  onMouseEnter={e=>e.currentTarget.style.background=v("surface-1")} onMouseLeave={e=>e.currentTarget.style.background=isExp?v("surface-1"):"transparent"}>
                  <SB status={overallStatus}/>
                  <span style={{fontFamily:MONO,color:v("text-2"),fontSize:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{g.cid?.substring(0,32)}{g.cid?.length>32?"…":""}</span>
                  <div style={{display:"flex",gap:3,alignItems:"center",flexWrap:"nowrap",overflow:"hidden"}}>
                    {stageNames.map((s,si)=>{
                      const stageMsgs=sorted.filter(m=>m.IntegrationFlowName===s);
                      const stageFailed=stageMsgs.some(m=>m.Status==="FAILED"||m.Status==="ERROR");
                      const stageOk=stageMsgs.every(m=>m.Status==="COMPLETED");
                      const sLabel=flowLabel(s);
                      return <span key={si} style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:stageFailed?`${v("danger")}18`:stageOk?`${v("success")}15`:v("surface-2"),color:stageFailed?v("danger"):stageOk?v("success"):v("text-3"),fontFamily:MONO,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:60}} title={sLabel}>
                        {sLabel.split(/[_\-]/).slice(-1)[0]?.substring(0,8)||sLabel.substring(0,8)}
                      </span>;
                    })}
                  </div>
                  <span style={{fontFamily:MONO,color:v("text-2"),fontSize:11}}>{g.msgs.length}</span>
                  <span style={{fontSize:10,color:g.failed>0?v("danger"):g.completed===g.msgs.length?v("success"):v("text-3"),fontWeight:600}}>{g.failed>0?t("pipeline.fehlerAnzahl",{count:g.failed}):g.completed===g.msgs.length?t("status.ok"):t("pipeline.laufend")}</span>
                </div>
                {isExp&&<div style={{background:v("surface-1"),borderTop:`1px solid ${v("border")}`,padding:"8px 16px 12px"}}>
                  <div style={{fontSize:10,color:v("text-3"),marginBottom:6,fontFamily:FONT}}>{t("pipeline.stagesDieserTransaktion")}</div>
                  {sorted.map((m,mi)=>(
                    <div key={mi} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:mi<sorted.length-1?`1px solid ${v("border-subtle")}`:"none",fontSize:11}}>
                      <SB status={m.Status}/>
                      <span style={{flex:1,color:v("text-1"),overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={m.IntegrationFlowName||""}>{flowLabel(m.IntegrationFlowName)||"?"}</span>
                      <span style={{fontFamily:MONO,color:v("text-3"),fontSize:10,flexShrink:0}}>{fD(m.LogStart)}</span>
                      <span style={{fontFamily:MONO,color:v("text-3"),fontSize:10,flexShrink:0}}>{fDur(m.LogStart,m.LogEnd)}</span>
                      <a href={monitoringLink(toSuiteUrl(instance.tenantUrl,instance.suiteUrl),m.MessageGuid)} target="_blank" rel="noopener noreferrer"
                        style={{fontSize:9,padding:"2px 6px",borderRadius:4,background:v("surface-0"),border:`1px solid ${v("border")}`,color:v("accent"),textDecoration:"none",flexShrink:0}}>CPI</a>
                    </div>
                  ))}
                </div>}
              </div>;
            })}
            {filtered.length===0&&<div style={{padding:32,textAlign:"center",color:v("text-3"),fontSize:13}}>{t("pipeline.keineGruppen")}</div>}
          </div>;
        })():

        /* Content Search Results */
        monitorSearchMode==="content"&&monitorSearch.length>=2?(
          <div style={{background:v("surface-1"),border:`1px solid ${v("border")}`,borderRadius:8,overflow:"hidden"}}>
            <div style={{padding:"10px 16px",background:v("surface-0"),borderBottom:`1px solid ${v("border")}`,fontSize:10,color:v("text-3"),fontFamily:FONT,textTransform:"uppercase",letterSpacing:.8,display:"grid",gridTemplateColumns:"28px minmax(150px,2fr) minmax(120px,1fr) 100px 100px 60px",alignItems:"center"}}>
              <div></div><div>iFlow</div><div>{t("treffer.spalteGuid")}</div><div>{t("treffer.spalteSender")}</div><div>{t("treffer.spalteStart")}</div><div>{t("treffer.spalteDauer")}</div>
            </div>
            {contentSearchData.loading?<div style={{padding:32,textAlign:"center",color:v("text-3"),fontSize:13}}>{t("treffer.sucheLaeuft")}</div>:contentSearchData.error?<div style={{padding:32,textAlign:"center",color:v("danger"),fontSize:13}}>{t("meldung.fehlerMit",{text:contentSearchData.error})}</div>:contentSearchResults.length===0?<div style={{padding:32,textAlign:"center",color:v("text-3"),fontSize:13}}>{t("treffer.keine")}{monitorSearch.length<2?` ${t("treffer.mindestensKurz")}`:""}</div>:
            contentSearchResults.map((m,mi)=>(
              <div key={m.MessageGuid||mi}>
                <div className="row-hover" style={{display:"grid",gridTemplateColumns:"28px minmax(150px,2fr) minmax(120px,1fr) 100px 100px 60px",padding:"8px 16px",borderBottom:`1px solid ${v("border-subtle")}`,alignItems:"center",fontSize:11,cursor:"pointer",background:monitorErrorDetail?.guid===m.MessageGuid?v("surface-2"):"transparent"}}
                  onClick={()=>loadMonitorLogError(m.MessageGuid)}>
                  <SB status={m.Status}/>
                  <span style={{fontFamily:FONT,color:v("text-1"),wordBreak:"break-word",fontSize:11}} title={m.IntegrationFlowName||""}>{flowLabel(m.IntegrationFlowName)||"-"}</span>
                  <span style={{fontFamily:FONT,color:v("text-3"),fontSize:10,wordBreak:"break-all"}}>{m.MessageGuid?.substring(0,18)}…</span>
                  <span style={{fontFamily:FONT,color:v("text-3"),fontSize:10}}>{m.Sender||"-"}</span>
                  <span style={{fontFamily:FONT,color:v("text-3"),fontSize:10,whiteSpace:"nowrap"}}>{fD(m.LogStart)}</span>
                  <span style={{fontFamily:FONT,color:v("text-3"),fontSize:10}}>{fDur(m.LogStart,m.LogEnd)}</span>
                </div>
                {monitorErrorDetail?.guid===m.MessageGuid&&(
                  <div style={{padding:"12px 16px",background:v("surface-1"),borderBottom:`1px solid ${v("border")}`,animation:"fadeIn 0.15s ease"}}>
                    {monitorErrorDetail.loading?<div style={{color:v("text-3"),fontSize:12}}>{t("detail.ladeDetails")}</div>:<>
                      {(m.Status==="FAILED"||m.Status==="ERROR")&&monitorErrorDetail.error?.message&&(
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:11,color:v("danger"),fontWeight:600,textTransform:"uppercase",marginBottom:6}}>{t("detail.fehlerdetails")}</div>
                          <pre style={{background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,borderRadius:8,padding:10,fontSize:11,color:v("danger"),fontFamily:FONT,whiteSpace:"pre-wrap",maxHeight:120,overflow:"auto"}}>{monitorErrorDetail.error?.message}</pre>
                        </div>
                      )}
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                        {[["guid","GUID",m.MessageGuid],["corr",t("detail.correlation"),m.CorrelationId],["snd",t("detail.sender"),m.Sender],["rcv",t("detail.receiver"),m.Receiver]].map(([kk,lbl,val])=>(
                          <div key={kk}><div style={{fontSize:10,color:v("text-3"),fontFamily:FONT}}>{lbl}</div><div style={{fontSize:11,color:v("text-2"),fontFamily:FONT,wordBreak:"break-all"}}>{val||"-"}</div></div>
                        ))}
                      </div>
                      {monitorErrorDetail.headers?.length>0&&(
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:11,color:v("accent"),fontWeight:600,textTransform:"uppercase",marginBottom:6,fontFamily:FONT}}>{t("detail.customHeaders")}</div>
                          <div style={{background:v("surface-0"),borderRadius:8,border:`1px solid ${v("border")}`,overflow:"hidden"}}>
                            {monitorErrorDetail.headers.map((h,hi)=>(
                              <div key={hi} style={{display:"flex",borderBottom:`1px solid ${v("border")}`,fontSize:11}}>
                                <span style={{padding:"4px 8px",color:v("accent"),fontFamily:FONT,fontWeight:500,minWidth:140,background:v("surface-1")}}>{h.Name}</span>
                                <span style={{padding:"4px 8px",color:v("text-1"),fontFamily:FONT,flex:1,wordBreak:"break-all"}}>{h.Value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {monitorErrorDetail.attachments?.length>0&&(
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:11,color:v("text-3"),fontWeight:600,textTransform:"uppercase",marginBottom:6,fontFamily:FONT}}>{t("detail.reiterAttachments")}</div>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            {monitorErrorDetail.attachments.map((att,ai)=>(
                              <button key={ai} onClick={()=>loadAttachmentContent(att.Id,att.Name)} style={{padding:"6px 12px",borderRadius:6,background:v("surface-2"),border:`1px solid ${v("border")}`,color:v("text-1"),fontSize:11,cursor:"pointer",fontFamily:FONT,fontWeight:500}}>
                                {att.Name} <span style={{color:v("text-3"),fontSize:10}}>({att.ContentType})</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        <a href={monitoringLink(toSuiteUrl(instance.tenantUrl,instance.suiteUrl),m.MessageGuid)} target="_blank" rel="noopener noreferrer" style={btnOutline}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          {t("aktion.imCpiMonitor")}
                        </a>
                        {m.IntegrationFlowName&&<button onClick={()=>{setLocalTargetFlow(m.IntegrationFlowName);setTab("content");}} style={btnOutline}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                          {t("aktion.iflowEinsehen")}
                        </button>}
                        {m.IntegrationFlowName&&<button onClick={()=>openDesignLink(m.IntegrationFlowName)} style={btnOutline}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          {t("aktion.iflowBearbeiten")}
                        </button>}
                      </div>
                    </>}
                  </div>
                )}
              </div>
            ))}
          </div>
        ):monitorSearchMode==="content"&&monitorSearch.length<2?(
          <div style={{padding:32,textAlign:"center",color:v("text-3"),fontSize:13}}>{t("treffer.mindestensLang")}</div>
        ):

        /* PI Monitor Table */
        groupBy!=="iflow"?(
          /* === Grouped View (Sender / Receiver / Status / Kanal) === */
          <div style={{borderRadius:10,border:`1px solid ${v("border")}`,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 70px 70px 70px 150px 24px",padding:"10px 16px",background:v("surface-2"),fontSize:11,fontWeight:600,color:v("text-3"),textTransform:"uppercase",letterSpacing:".04em",alignItems:"center"}}>
              <span>{groupBy==="sender"?t("detail.sender"):groupBy==="receiver"?t("detail.receiver"):groupBy==="status"?t("pipeline.spalteStatus"):t("gruppen.kanal")}</span>
              <span style={{textAlign:"right"}}>{t("status.ok")}</span><span style={{textAlign:"right"}}>{t("kpi.fehler")}</span><span style={{textAlign:"right"}}>{t("gruppen.gesamt")}</span><span style={{paddingLeft:16}}>{t("gruppen.letzterLauf")}</span><span/>
            </div>
            {buildGroups().filter(g=>{
              if(!monitorSearch)return true;
              const q=monitorSearch.toLowerCase();
              return g.key.toLowerCase().includes(q);
            }).map(g=>{
              const gKey="grp:"+g.key;
              const isOpen=monitorExpanded===gKey;
              const shownMsgs=(isOpen?g.messages.filter(m=>{
                if(monitorLogFilter!=="ALL"){
                  if(monitorLogFilter==="FAILED"&&(!(m.Status==="FAILED"||m.Status==="ERROR")||isAcked(m.MessageGuid)))return false;
                  else if(monitorLogFilter==="ACKED"&&(!(m.Status==="FAILED"||m.Status==="ERROR")||!isAcked(m.MessageGuid)))return false;
                  else if(monitorLogFilter==="OTHER"&&["COMPLETED","FAILED","ERROR","PROCESSING"].includes(m.Status))return false;
                  else if(!["FAILED","ACKED","OTHER"].includes(monitorLogFilter)&&m.Status!==monitorLogFilter)return false;
                }
                if(!monitorDeepSearch)return true;
                const q=monitorDeepSearch.toLowerCase();
                return[m.MessageGuid,m.CorrelationId,m.Sender,m.Receiver,m.IntegrationFlowName,flowLabel(m.IntegrationFlowName)].filter(Boolean).some(f=>f.toLowerCase().includes(q));
              }):[]).slice(0,msgPage*MSG_PAGE_SIZE);
              return(
              <div key={g.key}>
                <div className="row-hover" onClick={()=>{if(isOpen){setMonitorExpanded(null);}else{setMonitorExpanded(gKey);setMonitorLogFilter("ALL");setMonitorDeepSearch("");setMsgPage(1);}}}
                  style={{display:"grid",gridTemplateColumns:"1fr 70px 70px 70px 150px 24px",padding:"12px 16px",alignItems:"center",
                    background:isOpen?v("surface-2"):v("surface-0"),borderTop:`1px solid ${v("border-subtle")}`,cursor:"pointer",transition:"background .08s"}}
                  onMouseEnter={e=>{if(!isOpen)e.currentTarget.style.background=v("surface-1");}}
                  onMouseLeave={e=>{e.currentTarget.style.background=isOpen?v("surface-2"):v("surface-0");}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                    <span style={{width:7,height:7,borderRadius:"50%",flexShrink:0,
                      background:g.failed>0?v("danger"):g.completed>0?v("success"):v("text-3"),
                      boxShadow:g.failed>0?`0 0 6px ${v("danger")}40`:g.completed>0?`0 0 6px ${v("success")}40`:"none"}}/>
                    <span style={{fontWeight:600,fontSize:13,wordBreak:"break-word",lineHeight:"1.3"}}>{g.key}</span>
                  </div>
                  <span style={{fontFamily:MONO,fontWeight:600,color:(g.completed||0)>0?v("success"):v("text-3"),textAlign:"right"}} title={zahl(g.completed)}>{fmt(g.completed||0)}</span>
                  <span style={{fontFamily:MONO,fontWeight:600,color:(g.failed||0)>0?v("danger"):v("text-3"),textAlign:"right"}} title={zahl(g.failed)}>{fmt(g.failed||0)}</span>
                  <span style={{fontFamily:MONO,fontSize:11,color:v("accent"),textAlign:"right"}} title={zahl(g.total)}>{fmt(g.total||0)}</span>
                  <span style={{fontFamily:MONO,fontSize:11,color:v("text-2"),paddingLeft:16}}>{g.lastRun?fD(g.lastRun):"-"}</span>
                  <span style={{color:v("text-3"),fontSize:10,transform:isOpen?"rotate(180deg)":"none",transition:"transform .2s"}}>▾</span>
                </div>
                {isOpen&&(
                  <div style={{background:v("surface-0"),borderTop:`1px solid ${v("border-subtle")}`,animation:"fadeIn 0.15s ease"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px 4px 32px",borderBottom:`1px solid ${v("border-subtle")}`}}>
                      <span style={{fontSize:10,color:v("text-3")}}>{t("gruppen.filter")}</span>
                      {(()=>{
                        const okC=g.messages.filter(r=>r.Status==="COMPLETED").length;
                        const errAll=g.messages.filter(r=>r.Status==="FAILED"||r.Status==="ERROR");
                        const ackedC=errAll.filter(r=>isAcked(r.MessageGuid)).length;
                        const failC=errAll.length-ackedC;
                        const procC=g.messages.filter(r=>r.Status==="PROCESSING").length;
                        const otherC=g.messages.length-okC-errAll.length-procC;
                        const eskAll=g.messages.filter(r=>r.Status==="ESCALATED");
                        const eskAcked=eskAll.filter(r=>isAcked(r.MessageGuid)).length;
                        // k ist die Filter-Kennung (ALL, COMPLETED, FAILED, …) und bleibt woertlich.
                        const tabs=[{k:"ALL",l:`${t("reiter.alle")} (${g.messages.length})`},{k:"COMPLETED",l:`${t("status.ok")} (${okC})`},{k:"FAILED",l:`${t("kpi.fehler")} (${failC})`}];
                        // Eigener Reiter nur, wenn die Ansicht ESCALATED ueberhaupt zeigt - sonst
                        // waere hier eine Rubrik, die es laut Einstellung nicht gibt.
                        if(eskModus==="spalte"&&eskAll.length>0)tabs.push({k:"ESCALATED",l:`ESCALATED (${eskAll.length-eskAcked})`});
                        if(ackedC+eskAcked>0)tabs.push({k:"ACKED",l:`${t("reiter.bearbeitet")} (${ackedC+eskAcked})`});
                        if(procC>0)tabs.push({k:"PROCESSING",l:`${t("reiter.verarbeitungKurz")} (${procC})`});
                        if(otherC>0)tabs.push({k:"OTHER",l:`${t("reiter.sonstige")} (${otherC})`});
                        return tabs.map(rb=>(
                          <button key={rb.k} onClick={()=>{setMonitorLogFilter(rb.k);setMsgPage(1);}} style={{padding:"3px 10px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",
                            background:monitorLogFilter===rb.k?v("surface-2"):"transparent",color:monitorLogFilter===rb.k?v("text-1"):v("text-3"),transition:"all .12s"}}>
                            {rb.l}
                          </button>
                        ));
                      })()}
                      <input placeholder={t("gruppen.suchePlatzhalter")} value={monitorDeepSearch} onChange={e=>{setMonitorDeepSearch(e.target.value);setMsgPage(1);}}
                        style={{flex:1,minWidth:120,background:v("surface-2"),border:`1px solid ${v("border")}`,borderRadius:6,padding:"3px 8px",color:v("text-1"),fontSize:10,fontFamily:FONT,outline:"none"}}/>
                    </div>
                    {shownMsgs.length===0?<div style={{padding:16,textAlign:"center",color:v("text-3"),fontSize:12}}>{t("gruppen.keineMessages")}</div>:
                    shownMsgs.map((m,mi)=>(
                      <div key={m.MessageGuid||mi}>
                        <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 16px",borderBottom:`1px solid ${v("border-subtle")}`,fontSize:12,cursor:"pointer",background:monitorErrorDetail?.guid===m.MessageGuid?v("surface-2"):"transparent"}}
                          onClick={()=>loadMonitorLogError(m.MessageGuid)}>
                          <SB status={m.Status}/>
                          {instance.hasPipelineConcept&&(m.Status==="FAILED"||m.Status==="ERROR")&&isPipelineMsg(m)&&(()=>{
                            const rr=retryResult[m.MessageGuid];
                            return rr?.success?<span style={{fontSize:9,fontWeight:600,color:v("success"),whiteSpace:"nowrap"}}>{t("gruppen.retryOk")}</span>:
                              rr?.manualRequired?<span style={{fontSize:9,color:v("text-3"),whiteSpace:"nowrap"}} title={rr.message}>{t("gruppen.manuell")}</span>:
                              <button onClick={e=>{e.stopPropagation();triggerRetry(m);}} disabled={retryLoading===m.MessageGuid}
                                style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 6px",borderRadius:4,background:"transparent",border:`1px solid ${v("border")}`,color:v("accent"),fontSize:9,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",opacity:retryLoading===m.MessageGuid?.5:1}}>
                                {retryLoading===m.MessageGuid?"...":`↺ ${t("gruppen.retry")}`}
                              </button>;
                          })()}
                          <span style={{flex:1,fontFamily:FONT,color:v("text-2"),wordBreak:"break-word",fontSize:11}} title={m.IntegrationFlowName||""}>{flowLabel(m.IntegrationFlowName)||"-"}</span>
                          <span style={{fontFamily:FONT,color:v("text-3"),fontSize:10,whiteSpace:"nowrap"}}>{m.Sender||"-"} → {m.Receiver||"-"}</span>
                          <span style={{fontFamily:FONT,color:v("text-3"),fontSize:10,whiteSpace:"nowrap"}}>{fD(m.LogStart)}</span>
                          <span style={{fontFamily:FONT,color:v("text-3"),fontSize:10,whiteSpace:"nowrap"}}>{fDur(m.LogStart,m.LogEnd)}</span>
                          <span style={{color:v("text-3"),fontSize:10}}>▾</span>
                        </div>
                        {monitorErrorDetail?.guid===m.MessageGuid&&(
                          <div style={{padding:"12px 16px",background:v("surface-1"),borderBottom:`1px solid ${v("border")}`,animation:"fadeIn 0.15s ease"}}>
                            {monitorErrorDetail.loading?<div style={{color:v("text-3"),fontSize:12}}>{t("detail.ladeDetails")}</div>:<>
                              {(m.Status==="FAILED"||m.Status==="ERROR")&&monitorErrorDetail.error?.message&&(
                                <div style={{marginBottom:12}}>
                                  <div style={{fontSize:11,color:v("danger"),fontWeight:600,textTransform:"uppercase",marginBottom:6}}>{t("detail.fehlerdetails")}</div>
                                  <pre style={{background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,borderRadius:8,padding:10,fontSize:11,color:v("danger"),fontFamily:FONT,whiteSpace:"pre-wrap",maxHeight:120,overflow:"auto"}}>{monitorErrorDetail.error?.message}</pre>
                                </div>
                              )}
                              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                                {[["guid",t("treffer.spalteGuid"),m.MessageGuid],["corr",t("detail.correlation"),m.CorrelationId],["snd",t("detail.sender"),m.Sender],["rcv",t("detail.receiver"),m.Receiver],["flow","iFlow",flowLabel(m.IntegrationFlowName)]].map(([kk,lbl,val])=>(
                                  <div key={kk}><div style={{fontSize:10,color:v("text-3"),fontFamily:FONT}}>{lbl}</div><div style={{fontSize:11,color:v("text-2"),fontFamily:FONT,wordBreak:"break-all"}}>{val||"-"}</div></div>
                                ))}
                              </div>
                              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                                <a href={monitoringLink(toSuiteUrl(instance.tenantUrl,instance.suiteUrl),m.MessageGuid)} target="_blank" rel="noopener noreferrer" style={btnOutline}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                  {t("aktion.imCpiMonitor")}
                                </a>
                                {m.IntegrationFlowName&&<button onClick={()=>{setLocalTargetFlow(m.IntegrationFlowName);setTab("content");}} style={btnOutline}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                                  {t("aktion.iflowEinsehen")}
                                </button>}
                                {m.IntegrationFlowName&&<button onClick={()=>openDesignLink(m.IntegrationFlowName)} style={btnOutline}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  {t("aktion.iflowBearbeiten")}
                                </button>}
                              </div>
                            </>}
                          </div>
                        )}
                      </div>
                    ))}
                    {g.messages.length>msgPage*MSG_PAGE_SIZE&&(
                      <div style={{padding:"10px 16px",textAlign:"center",borderTop:`1px solid ${v("border-subtle")}`}}>
                        <button onClick={()=>setMsgPage(p=>p+1)} style={btnGhost}>
                          {t("liste.mehrLaden",{gezeigt:zahl(Math.min((msgPage+1)*MSG_PAGE_SIZE,g.messages.length)),gesamt:zahl(g.messages.length)})}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        ):(()=>{
          /* === NEU: iFlow-Accordion (Re-Skin) + Pins + Tiefen-Strip + Drawer === */
          // Message-Detail-Drawer (zweispaltig: Timeline links, Tabs rechts)
          const renderDrawer=(m,f)=>{
            const d=monitorErrorDetail;
            if(d?.loading) return <div className="iio-mon2-drawer"><div className="iio-mon2-empty">{t("detail.ladeDetailsPunkte")}</div></div>;
            const headers=d?.headers||[];
            const adapterAttrs=d?.adapterAttributes||[];
            const attachments=d?.attachments||[];
            const errorMsg=d?.error?.message||"";
            const isFail=(m.Status==="FAILED"||m.Status==="ERROR");
            const hasErr=!!errorMsg&&isFail;
            const code=mrowCodeMod(m.Status);
            const stMod=code.cls.includes("crit")?"err":code.cls.includes("warn")?"warn":code.cls.includes("ok")?"ok":"idle";
            const stColor=stMod==="err"?v("danger"):stMod==="warn"?v("warning"):stMod==="ok"?v("success"):v("text-3");
            const isPipe=instance.hasPipelineConcept&&isFail&&isPipelineMsg(m);
            const rs=runSteps[m.MessageGuid];
            // Drawer-Tabs: NUR was wirklich Inhalt hat. Leere Reiter werden nicht ausgegraut,
            // sondern gar nicht erst angeboten - ein ausgegrauter Reiter suggeriert Daten,
            // die es nicht gibt. Der Payload-Reiter entfaellt ganz: er hatte nie eigenen
            // Inhalt, sondern verwies nur auf die Attachments.
            // MPL-Schritt-Eigenschaften (RunStepProperties) - die Laufzeit-Details je Schritt
            const stepProps=(rs?.steps||[]).filter(s=>(s.properties||[]).length>0);
            const stepPropCount=stepProps.reduce((n,s)=>n+s.properties.length,0);
            // k ist die Reiter-Kennung (auch im gemerkten Zustand drawerTab) und bleibt woertlich.
            const tabs=[
              {k:"uebersicht",l:t("detail.reiterUebersicht"),c:null},
              headers.length>0&&{k:"headers",l:t("detail.reiterHeaders"),c:headers.length},
              hasErr&&{k:"fehler",l:t("detail.reiterFehler"),c:null},
              stepPropCount>0&&{k:"schritte",l:t("detail.reiterSchritte"),c:stepPropCount},
              adapterAttrs.length>0&&{k:"adapter",l:t("detail.reiterAdapter"),c:adapterAttrs.length},
              attachments.length>0&&{k:"attachments",l:t("detail.reiterAttachments"),c:attachments.length},
            ].filter(Boolean);
            const stored=drawerTab[m.MessageGuid];
            const storedEmpty=stored?!tabs.some(t=>t.k===stored):false;
            const activeTab=(!stored||storedEmpty)?(hasErr?"fehler":"uebersicht"):stored;
            const setT=(k)=>setDrawerTab(p=>({...p,[m.MessageGuid]:k}));
            const rr=retryResult[m.MessageGuid];
            const rpState=retryLoading===m.MessageGuid?"triggering":rr?(rr.success?"triggered_ok":"triggered_fail"):null;
            // Timeline-Steps aus run-steps; Fallback Runs-Ebene; sonst aus Message-Lebenszyklus
            const buildTimeline=()=>{
              if(rs?.loading) return {loading:true,steps:[]};
              // Aufeinanderfolgende Wiederholungen desselben Schritts (Splitter/Loop)
              // zu EINER Zeile verdichten - sonst flutet ein Splitter mit 120 Durchlaeufen
              // den Verlauf und das Fehler-Fenster wird unbrauchbar.
              const roh=(rs?.steps||[]);
              const verdichtet=[];
              for(const s of roh){
                const p=verdichtet[verdichtet.length-1];
                if(p&&p.s.modelStepId&&p.s.modelStepId===s.modelStepId&&stepStatusMod(p.s.status)===stepStatusMod(s.status)){
                  p.n++; p.lastIdx++; continue;
                }
                verdichtet.push({s,n:1,firstIdx:verdichtet.length,lastIdx:verdichtet.length});
              }
              const steps=verdichtet.map(({s,n})=>{
                const nm=s.stepName||null, ty=s.stepType||null;
                // Typzeile unterdruecken, wenn der Typ schon im Namen steckt
                // ("Content Modifier 1" + "Content Modifier" -> nur der Name).
                const norm=(x)=>String(x||"").toLowerCase().replace(/[^a-z0-9]/g,"");
                const typRedundant=nm&&ty&&norm(nm).includes(norm(ty));
                const dauer=s.durationMs!=null&&s.durationMs>=100
                  ? (s.durationMs>=1000?`${(s.durationMs/1000).toFixed(1)} s`:`${s.durationMs} ms`) : "";
                return {
                  ts:s.start?new Date(parseOD(s.start)||s.start).toLocaleTimeString(gebiet,{hour:"2-digit",minute:"2-digit",second:"2-digit"}):"",
                  nm:nm||ty||s.modelStepId||t("verlauf.schritt"),
                  sub:!nm?(s.modelStepId&&!ty?t("verlauf.ohneKlarnamen"):null):(typRedundant?null:ty),
                  dauer, iter:n>1?n:0, id:s.modelStepId||null, unresolved:!nm,
                  dt:s.error?String(s.error).replace(/\s+/g," ").trim().slice(0,90):"",
                  st:stepStatusMod(s.status), err:stepStatusMod(s.status)==="err",
                };
              });
              if(steps.length) return {loading:false,steps,source:"steps"};
              // Der Status-WERT (r.status) wird nur eingesetzt, nicht uebersetzt.
              const runs=(rs?.runs||[]).map(r=>({
                ts:r.start?new Date(parseOD(r.start)||r.start).toLocaleTimeString(gebiet,{hour:"2-digit",minute:"2-digit",second:"2-digit"}):"",
                nm:t("verlauf.lauf",{status:r.status||""}).trim(), dt:"", st:stepStatusMod(r.status), err:stepStatusMod(r.status)==="err",
              }));
              if(runs.length) return {loading:false,steps:runs,source:"runs"};
              // Degradation: Message-Lebenszyklus
              return {loading:false,source:"lifecycle",steps:[
                {ts:m.LogStart?new Date(parseOD(m.LogStart)||m.LogStart).toLocaleTimeString(gebiet,{hour:"2-digit",minute:"2-digit",second:"2-digit"}):"",nm:t("verlauf.empfangen"),dt:m.Sender||"",st:"ok",err:false},
                {ts:"",nm:t("verlauf.verarbeitet"),dt:fDur(m.LogStart,m.LogEnd)||"",st:isFail?"err":"ok",err:false},
                ...(isFail?[{ts:m.LogEnd?new Date(parseOD(m.LogEnd)||m.LogEnd).toLocaleTimeString(gebiet,{hour:"2-digit",minute:"2-digit",second:"2-digit"}):"",nm:t("verlauf.fehler"),dt:(errorMsg||"").slice(0,60),st:"err",err:true}]:[{ts:m.LogEnd?new Date(parseOD(m.LogEnd)||m.LogEnd).toLocaleTimeString(gebiet,{hour:"2-digit",minute:"2-digit",second:"2-digit"}):"",nm:t("verlauf.abgeschlossen"),dt:"",st:"ok",err:false}]),
              ]};
            };
            const tl=buildTimeline();
            const errIdx=tl.steps.findIndex(s=>s.err);
            // Fenster: 2 Schritte vor dem Fehler + Fehler-Schritt + 1 danach (bei Erfolg: letzte 4)
            const prominentStart=errIdx>=0?Math.max(0,errIdx-2):Math.max(0,tl.steps.length-4);
            const prominentEnd=errIdx>=0?Math.min(tl.steps.length,errIdx+2):tl.steps.length;
            const hiddenCount=prominentStart;
            const hiddenAfter=tl.steps.length-prominentEnd;
            const tlOpen=!!tlExpanded[m.MessageGuid];
            const renderStep=(s,i)=><div key={i} className={`iio-mon2-tl-step iio-mon2-tl-step--${s.st}`}>
              <div className="iio-mon2-tl-rail"><span className="iio-mon2-tl-glyph"/><span className="iio-mon2-tl-line"/></div>
              <div className="iio-mon2-tl-body">
                <div className="ts">{s.ts||"-"}{s.dauer?<span className="iio-mon2-tl-dur"> · {s.dauer}</span>:null}</div>
                <div className={"nm"+(s.unresolved?" is-id":"")} title={s.id||undefined}>{s.nm}</div>
                {s.sub&&<div className="iio-mon2-tl-sub">{s.sub}</div>}
                {s.iter>0&&<div className="iio-mon2-tl-sub">{t("verlauf.durchlaeufe",{count:s.iter})}</div>}
                {s.dt&&<div className="dt">{s.dt}</div>}</div>
            </div>;
            return <div className="iio-mon2-drawer">
              {/* Drawer-Kopf */}
              <div className="iio-mon2-dhead">
                <span className="bar" style={{color:stColor}}/>
                <span className="stlabel" style={{color:stColor}}>{codeLabel(code)}</span>
                <span className="route">{(m.Sender||m.Receiver)
                  ? <><span>{m.Sender||"-"}</span><ChevFlow variant="wave" small/><span>{m.Receiver||"-"}</span></>
                  : <span title={m.IntegrationFlowName||""}>{flowLabel(m.IntegrationFlowName)||"-"}</span>}</span>
                <span className="guid" title={m.MessageGuid}>{m.MessageGuid?.substring(0,18)}…</span>
                <div className="iio-mon2-dactions">
                  {isPipe&&<button className="btn btn--signal btn--sm" disabled={retryLoading===m.MessageGuid} onClick={()=>triggerRetry(m)}>{retryLoading===m.MessageGuid?"…":t("aktion.erneutAnstossen")}</button>}
                  {isFail&&<ErrorAnalysisMenu disabled={!aiEnabled} loading={errorAnalysis.entries[m.MessageGuid]?.loading}
                    mode={errorAnalysis.entries[m.MessageGuid]?.analysisMode} onSelect={mode=>errorAnalysis.analyze(m.MessageGuid,m.IntegrationFlowName,errorMsg,mode,i18n.language==="en"?"en":"de")}/>}
                  {/* Vorerst ausgeblendet (Logik bleibt erhalten):
                  <a className="btn btn--tertiary btn--sm" href={monitoringLink(toSuiteUrl(instance.tenantUrl,instance.suiteUrl),m.MessageGuid)} target="_blank" rel="noopener noreferrer">In Cockpit</a>
                  {m.IntegrationFlowName&&<button className="btn btn--tertiary btn--sm" onClick={()=>{setLocalTargetFlow(m.IntegrationFlowName);setTab("content");}}>iFlow einsehen</button>}
                  {m.IntegrationFlowName&&<button className="btn btn--tertiary btn--sm" onClick={()=>openDesignLink(m.IntegrationFlowName)}>Bearbeiten</button>}
                  */}
                  <button className="btn btn--tertiary btn--sm" onClick={()=>loadMonitorLogError(m.MessageGuid)} title={t("gemeinsam:aktion.schliessen")}>✕</button>
                </div>
              </div>
              <div className="iio-mon2-dgrid">
                {/* Timeline (run-steps) */}
                <div className="iio-mon2-tl">
                  <div className="iio-mon2-tl-title">{t("verlauf.titel")}{tl.source==="lifecycle"?` · ${t("verlauf.quelleLebenszyklus")}`:tl.source==="runs"?` · ${t("verlauf.quelleLaeufe")}`:""}</div>
                  {tl.loading?<div className="iio-mon2-tl-empty">{t("verlauf.ladeSchritte")}</div>:<>
                    {(hiddenCount>0||hiddenAfter>0)&&<div className={`iio-mon2-tl-collapse${tlOpen?" is-open":""}`} onClick={()=>setTlExpanded(p=>({...p,[m.MessageGuid]:!tlOpen}))}>
                      <div className="iio-mon2-tl-rail"><span className="iio-mon2-tl-glyph"/><span className="iio-mon2-tl-line"/></div>
                      <div className="lbl"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 6 15 12 9 18"/></svg>{tlOpen?t("verlauf.reduzieren"):t("verlauf.alleZeigen",{count:tl.steps.length})}</div>
                    </div>}
                    {(() => {
                      const sichtbar=tlOpen?tl.steps:tl.steps.slice(prominentStart,prominentEnd);
                      const off=tlOpen?0:prominentStart;
                      const out=[];
                      sichtbar.forEach((s,i)=>{
                        // Fehlerstelle bekommt eine eigene Zonen-Ueberschrift, damit sie
                        // sofort ins Auge faellt - ohne Icon, Rahmen oder Badge.
                        if(s.err) out.push(<div key={`h${i}`} className="iio-mon2-tl-zone is-err">{t("verlauf.fehlerstelle")}</div>);
                        out.push(renderStep(s,off+i));
                      });
                      return out;
                    })()}
                    {/* Laut Modell danach - deterministisch aus den Sequenzfluessen des BPMN.
                        Bewusst ohne Uhrzeit und Statusglyphe, damit es nicht wie ein
                        ausgefuehrter Schritt aussieht. Bei Verzweigungen ALLE Wege. */}
                    {tl.source==="steps"&&(rs?.errorStep?.modeledNext||[]).length>0&&<>
                      <div className="iio-mon2-tl-zone">{t("verlauf.lautModell")}</div>
                      {rs.errorStep.modeledNext.slice(0,3).map((n,i)=>
                        <div key={`mn${i}`} className="iio-mon2-tl-step iio-mon2-tl-next">
                          <div className="iio-mon2-tl-body"><div className="nm">{n.label}</div></div>
                        </div>)}
                      {rs.errorStep.modeledNext.length>3&&<div className="iio-mon2-tl-sub" style={{paddingLeft:18}}>{t("verlauf.weitereWege",{count:rs.errorStep.modeledNext.length-3})}</div>}
                    </>}
                    {rs?.errorStep?.anchorLabel&&<div className="iio-mon2-tl-note"><span className="bar"/>{t("verlauf.adapterAmSchritt",{schritt:rs.errorStep.anchorLabel})}</div>}
                    {tl.source==="lifecycle"&&<div className="iio-mon2-tl-note"><span className="bar"/>{t("verlauf.hinweisLebenszyklus")}</div>}
                    {tl.source==="runs"&&<div className="iio-mon2-tl-note"><span className="bar"/>{t("verlauf.hinweisNurLaeufe")}</div>}
                    {tl.source==="steps"&&rs?.resolution&&rs.resolution.total>0&&rs.resolution.named<rs.resolution.total&&
                      <div className="iio-mon2-tl-note"><span className="bar"/>{rs.contentRestricted
                        ? t("verlauf.hinweisScopeFehlt")
                        : t("verlauf.hinweisOhneNamen",{ohne:rs.resolution.total-rs.resolution.named,gesamt:rs.resolution.total})}</div>}
                  </>}
                </div>
                {/* Tabs rechts */}
                <div className="iio-mon2-pane">
                  <div className="iio-mon2-dtabs">
                    {tabs.map(rb=><button key={rb.k} className={`iio-mon2-dtab${activeTab===rb.k?" is-active":""}`} onClick={()=>setT(rb.k)}>{rb.l}{rb.c!=null&&<span className="c">· {rb.c}</span>}</button>)}
                  </div>
                  <div className="iio-mon2-dcontent">
                    {/* Ehrlichkeit: Konnte eine Quelle nicht gelesen werden, fehlt ihr Reiter -
                        das darf nicht wie "nichts vorhanden" aussehen. Einmal ausweisen. */}
                    {(d?.nichtLesbar||[]).length>0&&
                      <div className="iio-mon2-tl-note" style={{marginTop:0,marginBottom:10}}><span className="bar"/>
                        <span>{d.nichtLesbar.map((n,ni)=>
                          <span key={ni} style={{display:"block"}}>{t("detail.nichtLesbar",{bereich:n.bereich})} {n.grund||t("detail.tenantAbgelehnt")}</span>)}</span></div>}
                    {activeTab==="uebersicht"&&<>
                      <div className="iio-mon2-sech">{t("detail.identitaet")}</div>
                      <div className="iio-mon2-kv">
                        <div><div className="k">GUID</div><div className="v">{m.MessageGuid||"-"}</div></div>
                        <div><div className="k">{t("detail.correlationId")}</div><div className="v">{m.CorrelationId||"-"}</div>
                          {m.CorrelationId&&(
                            <button className="btn btn--secondary btn--sm" style={{marginTop:6}} disabled={corrDeps?.loading}
                              onClick={()=>openCorrDeps(m.CorrelationId)}
                              title={t("korrelation.ladenHilfe")}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                              {corrDeps?.loading&&corrDeps?.cid===m.CorrelationId?t("korrelation.lade"):t("korrelation.abhaengigkeitenLaden")}
                            </button>
                          )}
                        </div>
                        {/* Der Status-WERT des Tenants, unuebersetzt. */}
                        <div><div className="k">{t("pipeline.spalteStatus")}</div><div className="v">{m.Status||"-"}</div></div>
                        <div><div className="k">{t("detail.empfangen")}</div><div className="v">{m.LogStart?fD(m.LogStart):"-"}</div></div>
                      </div>
                      <div className="iio-mon2-sech">{t("detail.routing")}</div>
                      <div className="iio-mon2-kv">
                        <div><div className="k">{t("detail.sender")}</div><div className="v">{m.Sender||"-"}</div></div>
                        <div><div className="k">{t("detail.receiver")}</div><div className="v">{m.Receiver||"-"}</div></div>
                        <div><div className="k">{t("detail.reiterAdapter")}</div><div className="v">{m.AdapterMessageType||m.AdapterId||"-"}</div></div>
                        <div><div className="k">{t("treffer.spalteDauer")}</div><div className="v">{fDur(m.LogStart,m.LogEnd)||"-"}</div></div>
                      </div>
                    </>}
                    {activeTab==="headers"&&(headers.length===0?<div className="iio-mon2-empty">{t("detail.keineHeaders")}</div>:
                      <div className="iio-mon2-hdr-table">{headers.map((h,hi)=><div key={hi} className="iio-mon2-hdr-row"><div className="k">{h.Name}</div><div className="v">{h.Value}</div></div>)}</div>)}
                    {activeTab==="fehler"&&(hasErr?<>
                      <div style={{fontSize:11,color:v("text-3"),marginBottom:8}}>{t("detail.fehlerAuszug")}</div>
                      <div className="iio-mon2-errblock">{errorMsg}</div>
                      {isPipe&&(()=>{
                        const lage=rr?.queueName||m.Receiver||"JMS-Queue";
                        return <div className="iio-mon2-reprocess">
                          <div className="iio-mon2-rp-title">{t("reprocess.titel")}</div>
                          <div className="iio-mon2-rp-row lage"><span className="bar"/><span className="k">{t("reprocess.lage")}</span><span className="vv">{t("reprocess.aktuellIn",{ort:lage})}</span></div>
                          <div className="iio-mon2-rp-row last" style={{color:v("text-3")}}><span className="bar" style={{background:v("text-4")}}/><span className="k">{t("reprocess.letzter")}</span><span className="vv">{errorMsg.slice(0,80)}</span></div>
                          <div className="iio-mon2-rp-actions">
                            <button className="btn btn--signal btn--sm" disabled={rpState==="triggering"} onClick={()=>triggerRetry(m)}>{rpState==="triggering"?t("reprocess.wirdAngestossen"):t("reprocess.ausQueueAnstossen")}</button>
                          </div>
                          {rpState==="triggered_ok"&&<div className="iio-mon2-rp-feedback ok"><span className="bar"/>{t("reprocess.erfolgreich")}{rr?.message?` · ${rr.message}`:""}</div>}
                          {rpState==="triggered_fail"&&<div className="iio-mon2-rp-feedback fail"><span className="bar"/>{t("reprocess.fehlgeschlagen",{grund:rr?.message||t("reprocess.unbekannt")})}</div>}
                          {rpState==="triggering"&&<div className="iio-mon2-rp-feedback trig"><span className="bar"/>{t("reprocess.wirdAngestossen")}</div>}
                        </div>;
                      })()}
                      {!aiEnabled&&<div className="iio-mon2-ai-off"><span className="bar"/>{t("ki.nichtAktiviertLang")}</div>}
                    </>:<div className="iio-mon2-empty">{t("detail.nichtFehlgeschlagen")}</div>)}
                    {/* MPL-Schritt-Details: RunStepProperties je Schritt, in Ausfuehrungsreihenfolge.
                        Der fehlgeschlagene Schritt steht zuerst, damit man nicht suchen muss. */}
                    {activeTab==="schritte"&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
                      {[...stepProps].sort((a,b)=>(b.status==="FAILED"?1:0)-(a.status==="FAILED"?1:0)).map((s,si)=>
                        <div key={si}>
                          {/* Geprueft wird der Status-WERT, angezeigt eine Beschriftung. */}
                          <div className="iio-mon2-sech" style={{color:/FAILED|ERROR/i.test(s.status||"")?v("danger"):undefined}}>
                            {s.stepName||s.modelStepId||t("verlauf.schritt")}{s.stepType?` · ${s.stepType}`:""}
                            {/FAILED|ERROR/i.test(s.status||"")?` · ${t("verlauf.fehlgeschlagen")}`:""}
                          </div>
                          <div className="iio-mon2-hdr-table">{s.properties.map((p,pi)=>
                            <div key={pi} className="iio-mon2-hdr-row"><div className="k">{p.name}</div><div className="v">{String(p.value)}</div></div>)}</div>
                        </div>)}
                    </div>}
                    {activeTab==="adapter"&&(adapterAttrs.length===0?<div className="iio-mon2-empty">{t("detail.keineAdapterAttribute")}</div>:
                      <div className="iio-mon2-hdr-table">{adapterAttrs.map((a,ai)=><div key={ai} className="iio-mon2-hdr-row"><div className="k">{a.Name||a.Key||"-"}</div><div className="v">{a.Value||"-"}</div></div>)}</div>)}
                    {activeTab==="attachments"&&(attachments.length===0?<div className="iio-mon2-empty">{t("detail.keineAttachments")}</div>:
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>{attachments.map((att,ai)=>
                        <button key={ai} className="btn btn--tertiary" style={{justifyContent:"flex-start"}} onClick={()=>loadAttachmentContent(att.Id,att.Name)}>
                          {att.Name} <span style={{color:v("text-3"),fontSize:11,marginLeft:6}}>({att.ContentType||"-"}{att.PayloadSize?` · ${(att.PayloadSize/1024).toFixed(1)} kB`:""})</span>
                        </button>)}</div>)}
                    <ErrorAnalysisPanel key={instance.id+":"+m.MessageGuid} entry={errorAnalysis.entries[m.MessageGuid]}
                      onSend={text=>errorAnalysis.send(m.MessageGuid,text)} onRetry={()=>errorAnalysis.send(m.MessageGuid,null,true)}
                      onStop={()=>errorAnalysis.stop(m.MessageGuid)}
                      onAnalyze={()=>errorAnalysis.analyze(m.MessageGuid,m.IntegrationFlowName,errorMsg,errorAnalysis.entries[m.MessageGuid]?.analysisMode||"simple",i18n.language==="en"?"en":"de")}/>
                  </div>
                </div>
              </div>
            </div>;
          };
          // Eine iFlow-Karte (Header + Body bei Aufklappung). isPinView=true im Pin-Bereich.
          const renderFlowBody=(f)=>{
            const ef=(scopedIflows||[]).find(x=>x.name===f.name)||f;
            const okC=ef?.completed??0, failC=ef?.failed??0, retryC=ef?.retry??0, procC=ef?.processing??0;
            const otherC=ef?.other??0, totalC=ef?.total??(okC+failC+retryC+procC+otherC);
            // Eskalationen im Drilldown: eigener Reiter, sobald die Ansicht sie zeigt. Die
            // Zahlen kommen aus derselben Quelle wie die Kopfzeile der Tabelle.
            const eskC=eskModus==="aus"?0:(ef?.escalated??0);
            const eskAckC=Math.min(ackEskByFlow[f.name]||0,eskC);
            const confirmedCounts=failureCountsForFlow(ef);
            const eskOpen=confirmedCounts.escalated;
            const fehlerBasisC=failC+(eskModus==="fehler"?eskC:0);
            const ackedC=Math.min((ackFehlerByFlow[f.name]||0)+(eskModus==="fehler"?eskAckC:0),fehlerBasisC);
            const failOpen=confirmedCounts.failed;
            const acknowledged=confirmedCounts.acknowledged;
            const expectedCount=monitorLogFilter==="ACKED"?acknowledged:monitorLogFilter==="ESCALATED"?eskOpen:failOpen;
            const listUncertain=filteredRunsActive&&(!monitorFilterReload||monitorFilterReload.stale||monitorFilterReload.incomplete||monitorFilterReload.hasMore||monitorFilterReload.error||monitorFilterReload.count==null);
            const lastMsg=(monitorLogs||[])[0];
            const lastRunTs=ef?.lastRun||lastMsg?.LogStart;
            const adapter=lastMsg?.AdapterMessageType||lastMsg?.AdapterId||"-";
            // k ist die Filter-Kennung, l die Beschriftung. ESCALATED ist beides zugleich:
            // Kennung UND Anzeige - der Name des SAP-Status bleibt in beiden Sprachen stehen.
            const tabsDef=[
              {k:"FAILED",l:eskModus==="fehler"?t("kpi.fehlerMitEsk"):t("kpi.fehler"),c:failOpen},
              ...(eskModus==="spalte"&&eskC>0?[{k:"ESCALATED",l:"ESCALATED",c:eskOpen}]:[]),
              ...(acknowledged>0?[{k:"ACKED",l:t("reiter.bearbeitet"),c:acknowledged}]:[]),
              {k:"COMPLETED",l:t("kpi.erfolgreich"),c:okC},
              {k:"OTHER",l:t("reiter.andere"),c:Math.max(0,otherC+procC+retryC-(eskModus==="spalte"?eskC:0))},
              {k:"ALL",l:t("reiter.alle"),c:totalC},
            ];
            return <div className="iio-mon2-body-pad">
              {corrActive&&<div className="iio-mon2-banner" style={{marginBottom:8}}><span className="bar"/><span className="grow">{t("korrelation.gefiltertVor")} <span style={{fontFamily:MONO,fontSize:11}}>{corrSearch.cid}</span> {t("korrelation.gefiltertNach")}</span></div>}
              {(monitorLogsLoading||(monitorFilterReload?.loading&&monitorFilterReload.status===monitorLogFilter))&&<div className="iio-mon2-reload"><span/></div>}
              {/* Kontext-Anker-Zeile */}
              <div className="iio-mon2-ctx">
                {t("liste.zuletzt")} <span>{relTime(lastRunTs,t)}</span>
                {lastRunTs&&<><span className="dot">·</span> {fD(lastRunTs)}</>}
                <span className="dot">·</span> {lastMsg?.Sender||f.name} <ChevFlow variant="static" small/> {lastMsg?.Receiver||"-"}
                <span className="dot">·</span> {adapter}
              </div>
              {/* Status-Tabs */}
              <div className="iio-mon2-stabs">
                {tabsDef.map(rb=>(
                  <button key={rb.k} className={`iio-mon2-stab${monitorLogFilter===rb.k?" is-active":""}`} onClick={()=>setMonitorLogFilter(rb.k)}>
                    {rb.l}<span className="c">{fmt(rb.c)}</span>
                  </button>
                ))}
              </div>
              {/* Deep-Search */}
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
                <input className="iio-mon2-hdr-search" style={{marginBottom:0,flex:1}} placeholder={t("liste.tiefensuche")} value={monitorDeepSearch} onFocus={()=>setIflowIndexWanted(true)} onChange={e=>{setIflowIndexWanted(true);setMonitorDeepSearch(e.target.value);setMsgPage(1);}}/>
                <span style={{fontSize:11,color:v("text-3")}}>{iflowSearchActive&&iflowIndex.building?`${t("liste.treffer",{count:displayLogs.length})} · ${t("liste.indexiert",{fertig:iflowIndex.indexed,gesamt:iflowIndex.total})}`:t("liste.treffer",{count:displayLogs.length})}</span>
              </div>
              {/* Hinweise (Filter-Reload / Truncation / Deep-Search-Scope) */}
              {monitorFilterReload?.stale&&!monitorFilterReload.error&&(
                <div role="status" className="iio-mon2-banner" style={{marginBottom:8,flexWrap:"wrap"}}><span className="bar"/><span className="grow">{t("liste.seitenVeraltet")}</span><button className="btn btn--tertiary btn--sm" onClick={refreshMonitorRuns}>{t("liste.neuLaden")}</button></div>
              )}
              {monitorFilterReload?.error && monitorFilterReload.status===monitorLogFilter && (
                <div role="alert" className="iio-mon2-banner iio-mon2-banner--crit" style={{marginBottom:8,flexWrap:"wrap"}}><span className="bar"/><span className="grow">{t(`liste.seitenFehler.${monitorFilterReload.error}`,{defaultValue:t("liste.seitenFehler.MONITOR_READ_FAILED")})}</span><button className="btn btn--tertiary btn--sm" onClick={refreshMonitorRuns}>{t("liste.neuLaden")}</button></div>
              )}
              {useFilterReload && !monitorFilterReload?.error && monitorFilterReload?.incomplete && (
                <div role="status" className="iio-mon2-banner" style={{marginBottom:8}}><span className="bar"/><span className="grow">{t("liste.seitenUnvollstaendig")}</span></div>
              )}
              {iflowSearchActive && iflowIndex.runsCapped && (
                <div className="iio-mon2-banner" style={{marginBottom:8}}><span className="bar"/><span className="grow">{t("liste.sucheBegrenzt",{anzahl:zahl(iflowIndex.total)})}</span></div>
              )}
              {/* Message-Tabellen-Kopf */}
              {/* Bearbeiter-Spalte nur, wenn in der aktuellen Liste ueberhaupt etwas
                  als bearbeitet markiert ist. Bei reinen Fehlerlisten entfaellt sie ganz. */}
              <div className={`iio-mon2-msghead${zeigeBearbeiter?" has-ack":""}`}>
                <span><input type="checkbox" onChange={e=>{
                  const visible=displayLogs.slice(0,msgPage*MSG_PAGE_SIZE);
                  if(e.target.checked)setSelectedGuids(p=>{const n=new Set(p);visible.forEach(x=>n.add(x.MessageGuid));return n;});
                  else setSelectedGuids(p=>{const n=new Set(p);visible.forEach(x=>n.delete(x.MessageGuid));return n;});
                }} style={{accentColor:"var(--iio-brand-ink)",width:14,height:14}}/></span>
                <span/><span>{t("liste.spalteRoute")}</span>{zeigeBearbeiter&&<span>{t("liste.spalteBearbeiter")}</span>}<span>{t("detail.correlationId")}</span><span>{t("detail.empfangen")}</span><span className="r">{t("pipeline.spalteStatus")}</span>
              </div>
              {/* Message-Liste */}
              {(monitorLogsLoading&&!useFilterReload)?<div className="iio-mon2-empty">{t("liste.ladeDurchlaeufe")}</div>:
              (filteredRunsActive&&(!monitorFilterReload||monitorFilterReload.loading))?<div role="status" className="iio-mon2-empty">{monitorLogFilter==="ACKED"?t("liste.ladeBearbeitete"):t("liste.ladeFehler")}</div>:
              (iflowSearchActive&&iflowIndex.loading&&(iflowIndex.runs||[]).length===0)?<div className="iio-mon2-empty">{t("liste.indexWirdAufgebaut")}</div>:
              displayLogs.length===0?(
                iflowSearchActive?<div className="iio-mon2-empty">{iflowIndex.error?t("liste.sucheFehler",{grund:iflowIndex.error}):(iflowIndex.building?t("liste.nochKeineTreffer"):t("liste.keineTrefferFelder"))}</div>
                :listUncertain?<div role="status" className="iio-mon2-empty">{monitorFilterReload?.error?t("liste.seitenNichtLesbar"):t("liste.seitenNochKeine")}</div>
                :filteredRunsActive&&expectedCount>0?<div role="status" className="iio-mon2-empty">{t("liste.seitenAbweichung")}</div>
                :(monitorLogFilter==="FAILED"&&failOpen===0?<div className="iio-mon2-empty">{ackedC>0?t("liste.alleBearbeitet"):t("liste.keineFehlerZeitraum")} <button onClick={()=>setMonitorLogFilter(ackedC>0?"ACKED":"ALL")}>{ackedC>0?t("liste.bearbeiteteZeigen"):t("liste.filterZuruecksetzenFrage")}</button></div>
                :monitorLogFilter==="ACKED"?<div className="iio-mon2-empty">{t("liste.keineBearbeiteten")}</div>
                :<div className="iio-mon2-empty">{t("liste.keineInStatus")}</div>)
              ):
              displayLogs.slice(0,msgPage*MSG_PAGE_SIZE).map((m,mi)=>{
                const isOpen=monitorErrorDetail?.guid===m.MessageGuid;
                const acked=m.isAcknowledged??((m.Status==="FAILED"||m.Status==="ERROR"||m.Status==="ESCALATED")&&isAcked(m.MessageGuid));
                const codeBase=mrowCodeMod(m.Status);
                // "Bearbeitet" ist KEIN Status des Tenants, sondern eine Markierung der Webapp.
                // Sie tritt hier an die Stelle der Status-Anzeige - der Status-Wert bleibt unberührt.
                const code=acked?{cls:"info",schluessel:"bearbeitet"}:codeBase;
                const barMod=acked?"info":codeBase.cls.includes("crit")?"err":codeBase.cls.includes("warn")?"warn":codeBase.cls.includes("ok")?"ok":"idle";
                return <div key={m.MessageGuid||mi}>
                  <div className={`iio-mon2-mrow${isOpen?" is-open":""}${zeigeBearbeiter?" has-ack":""}`} onClick={()=>{if(!isOpen&&!("ApplicationMessageId" in m))hydrateRun(m.MessageGuid);loadMonitorLogError(m.MessageGuid);loadRunSteps(m.MessageGuid,m.IntegrationFlowName);}}>
                    <input type="checkbox" checked={selectedGuids.has(m.MessageGuid)} onClick={e=>e.stopPropagation()} onChange={()=>toggleSelect(m.MessageGuid)}/>
                    <span className={`iio-mon2-mbar iio-mon2-mbar--${barMod}`}/>
                    <span className="iio-mon2-route">{(m.Sender||m.Receiver)
                      ? <><span className="ep">{m.Sender||"-"}</span><ChevFlow variant="static" small/><span className="ep">{m.Receiver||"-"}</span></>
                      : <span className="ep" title={m.IntegrationFlowName||""}>{flowLabel(m.IntegrationFlowName)||"-"}</span>}</span>
                    {/* Die Spalte existiert nur, wenn ueberhaupt bearbeitete Eintraege
                        sichtbar sind - ein offener Fehler hat per Definition keinen Bearbeiter. */}
                    {zeigeBearbeiter&&<span className="iio-mon2-mc" style={acked?{color:"var(--iio-info)"}:undefined} title={acked&&acks[m.MessageGuid]?.by?`${t("liste.markiertVon",{name:acks[m.MessageGuid].by})}${acks[m.MessageGuid].at?` · ${new Date(acks[m.MessageGuid].at).toLocaleString(gebiet)}`:""}`:undefined}>
                      {acked?(m.acknowledgedBy||acks[m.MessageGuid]?.by||"-"):""}
                    </span>}
                    <span className="iio-mon2-mc" title={`${t("detail.correlationId")}: ${m.CorrelationId||"-"} · GUID: ${m.MessageGuid||"-"}${m._matchHeader?` · ${m._matchHeader}`:""}`}>
                      {(()=>{const cid=m.CorrelationId||m.MessageGuid||"-";return cid.length>18?cid.substring(0,18)+"…":cid;})()}
                    </span>
                    <span className="iio-mon2-mc">{m.LogStart?new Date(parseOD(m.LogStart)||m.LogStart).toLocaleTimeString(gebiet,{hour:"2-digit",minute:"2-digit",second:"2-digit"}):"-"}</span>
                    <span className={`iio-mon2-code ${barMod}`}>{codeLabel(code)}</span>
                  </div>
                  {isOpen&&renderDrawer(m,f)}
                </div>;
              })}
              {displayLogs.length>msgPage*MSG_PAGE_SIZE&&(
                <button className="iio-mon2-more" onClick={()=>setMsgPage(p=>p+1)}>{t("liste.mehrLaden",{gezeigt:zahl(Math.min((msgPage+1)*MSG_PAGE_SIZE,displayLogs.length)),gesamt:zahl(displayLogs.length)})}</button>
              )}
              {!iflowSearchActive&&!corrActive&&monitorFilterReload?.hasMore&&displayLogs.length<=msgPage*MSG_PAGE_SIZE&&(
                <button className="iio-mon2-more" disabled={monitorFilterReload.loadingMore||!!monitorFilterReload.error} onClick={async()=>{await loadMoreMonitorRuns();setMsgPage(p=>p+1);}}>{t(monitorFilterReload.loadingMore?"liste.ladeDurchlaeufe":"liste.naechsteSeite")}</button>
              )}
              {filteredRunsActive&&!iflowSearchActive&&monitorFilterReload?.count!=null&&(
                <div role="status" className="iio-mon2-empty">{t("liste.statusGeladen",{geladen:zahl(monitorFilterReload.logs.length),gesamt:zahl(monitorFilterReload.count)})}</div>
              )}
              {/* Gesamtzahl: die leichte Liste traegt kein $inlinecount mehr - die scope-
                  gerechte Zeile des Snapshots (ef.total) kennt die Zahl bereits. */}
              {!filteredRunsActive&&!iflowSearchActive && (monitorLogCount||ef.total||0)>monitorLogs.length&&(
                <div style={{padding:"6px 0",textAlign:"center",fontSize:10,color:v("text-3")}}>{t("liste.durchlaeufeGeladen",{geladen:zahl(monitorLogs.length),gesamt:zahl(monitorLogCount||ef.total)})}</div>
              )}
            </div>;
          };
          // Eine iFlow-Header-Zeile (im Pin-Bereich UND in der Hauptliste)
          const renderFlowHead=(f,pinned)=>{
            const isOpen=monitorExpanded===f.name;
            // Eskalationen je Modus: eigene Spalte, in die Fehlerzahl eingerechnet, oder aus.
            const eskRoh=eskModus==="aus"?0:(f.escalated||0);
            const eskAck=Math.min(ackEskByFlow[f.name]||0,eskRoh);
            const confirmedCounts=failureCountsForFlow(f);
            const eskOffen=confirmedCounts.escalated;
            const fehlerBasis=(f.failed||0)+(eskModus==="fehler"?eskRoh:0);
            const ackedC=Math.min((ackFehlerByFlow[f.name]||0)+(eskModus==="fehler"?eskAck:0),fehlerBasis);
            const failShown=confirmedCounts.failed;
            const acknowledged=confirmedCounts.acknowledged;
            // Erfolgszahl nur als Zahl darstellen, wenn sie auch gemessen wurde. Auf sehr
            // grossen Tenants kann der Tenant einzelne Flow-Counts nicht liefern - dann steht
            // hier ein Strich statt einer 0, die eine Aussage waere, die wir nicht haben.
            const okOffen=f.okQuelle==="keine";
            // undefined = Instanz im klassischen Worker, der diese Herkunft nicht liefert:
            // dann wie gemessen behandeln, statt allen Zeilen die Farbe zu nehmen.
            const okGemessen=f.okQuelle===undefined||f.okQuelle==="gemessen";
            const okTitel=okOffen?t("liste.okNichtAbrufbar")
              :!okGemessen?t("liste.okAusSpeicher",{wert:zahl(f.completed)})
              :zahl(f.completed);
            const isCrit=failShown>0;
            const ef=(scopedIflows||[]).find(x=>x.name===f.name)||f;
            const label=f.displayName||f.name;
            const hasTechId=!!(f.displayName&&f.displayName!==f.name);
            return <div key={(pinned?"pin:":"")+f.name} className={`iio-mon2-flow${isOpen?" is-open":""}${isCrit?" is-crit":""}${pinned?" iio-mon2-flow--pinned":""}`}>
              <div className="iio-mon2-flow-head" role="button" tabIndex={0} onClick={()=>{
                if(isOpen){setMonitorExpanded(null);setMonitorErrorDetail(null);return;}
                if(corrActive){setMonitorExpanded(f.name);setMonitorLogs([]);setMonitorErrorDetail(null);setMonitorLogFilter("ALL");setMonitorDeepSearch("");setMsgPage(1);return;}
                loadMonitorLogs(f.name,{failed:failShown});
              }}>
                <svg className="iio-mon2-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                <div className="iio-mon2-flow-name">
                  <div className="nm">{monitorPins.includes(f.name)&&<svg className="iio-mon2-pin-lead" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H19v-2l-3-2z"/></svg>}<span title={hasTechId?`${label} · ${t("liste.technischeId",{id:f.name})}`:label}>{label}</span></div>
                  <div className="sub">{hasTechId
                    ?<span title={t("liste.technischeIdHilfe")}>{f.name}</span>
                    :<>{label.split(/[_\-.]/).slice(0,1)[0]||label} <ChevFlow variant="static" small/> {label.split(/[_\-.]/).slice(-1)[0]||""}</>}</div>
                </div>
                <div className={`iio-mon2-stat iio-mon2-stat--first${failShown>0?" crit":""}`}><span className="val" title={eskModus==="fehler"?t("liste.fehlerDarinEsk",{fehler:zahl(failShown),esk:zahl(eskRoh)}):zahl(failShown)}>{fmt(failShown)}</span><span className="lbl">{eskModus==="fehler"?t("liste.spalteFehlerEsk"):t("kpi.fehler")}</span></div>
                {eskModus==="spalte"&&<div className={`iio-mon2-stat${eskOffen>0?" esk":""}`}><span className="val" title={t("liste.eskOffenVon",{offen:zahl(eskOffen),gesamt:zahl(eskRoh)})}>{fmt(eskOffen)}</span><span className="lbl">ESCALATED</span></div>}
                <div className={`iio-mon2-stat${acknowledged>0?" info":""}`}><span className="val" title={t("liste.bearbeitetAnzahl",{count:acknowledged})}>{fmt(acknowledged)}</span><span className="lbl">{t("reiter.bearbeitet")}</span></div>
                <div className={`iio-mon2-stat${okGemessen&&(f.completed||0)>0?" ok":""}`}><span className="val" title={okTitel}>{okOffen?"–":fmt(f.completed||0)}</span><span className="lbl">{t("status.ok")}</span></div>
                <div className="iio-mon2-last" title={ef.lastRun?new Date(parseOD(ef.lastRun)||ef.lastRun).toLocaleString(gebiet):""}>
                  {ef.lastRun?relTime(ef.lastRun,t):"-"}
                  {ef.lastRun&&<span className="abs">{new Date(parseOD(ef.lastRun)||ef.lastRun).toLocaleTimeString(gebiet,{hour:"2-digit",minute:"2-digit"})}</span>}
                </div>
                <button className={`iio-mon2-iconbtn iio-mon2-pin${monitorPins.includes(f.name)?" is-pinned":""}`} title={monitorPins.includes(f.name)?t("pins.entfernen"):t("pins.anpinnen")} onClick={e=>{e.stopPropagation();togglePin(f.name);}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H19v-2l-3-2z"/></svg>
                </button>
                <button className="iio-mon2-iconbtn" title={t("liste.menue")} onClick={e=>{e.stopPropagation();const r=e.currentTarget.getBoundingClientRect();setKebabMenu(kebabMenu?.flowName===f.name?null:{flowName:f.name,x:Math.min(r.left,window.innerWidth-210),y:r.bottom+4});}}>
                  <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                </button>
              </div>
              {isOpen&&<div className="iio-mon2-body">{renderFlowBody(f)}</div>}
            </div>;
          };
          const pinnedFlows=monitorPins.map(name=>sortedMonitorIflows.find(f=>f.name===name)).filter(Boolean);
          return <div>
            {/* Correlation-Suche: Status der Ketten-Abfrage */}
            {corrSearch&&monitorSearchMode==="iflow"&&monitorSearch.trim()===corrSearch.cid&&(
              corrSearch.loading?<div className="iio-mon2-banner" style={{marginBottom:8}}><span className="bar"/><span className="grow">{t("korrelation.sucheLaeuft")}</span></div>
              :corrSearch.error?<div className="iio-mon2-banner iio-mon2-banner--crit" style={{marginBottom:8}}><span className="bar"/><span className="grow">{t("korrelation.sucheFehler",{grund:corrSearch.error})}</span><button className="iio-mon2-link" onClick={corrReset}>{t("liste.filterZuruecksetzen")}</button></div>
              :corrSearch.flows.length===0?<div className="iio-mon2-banner" style={{marginBottom:8}}><span className="bar"/><span className="grow">{t("korrelation.nichtsGefunden")}</span><button className="iio-mon2-link" onClick={corrReset}>{t("liste.filterZuruecksetzen")}</button></div>
              :<div className="iio-mon2-banner" style={{marginBottom:8}}><span className="bar"/><span className="grow">{t("korrelation.treffer",{runs:corrSearch.count,iflows:corrSearch.flows.length})}{corrSearch.capped?` ${t("korrelation.begrenzt")}`:""}. {t("korrelation.aufklappenHinweis")}</span><button className="iio-mon2-link" onClick={corrReset}>{t("liste.filterZuruecksetzen")}</button></div>
            )}
            {/* === Z4 Pin-Bereich (nur wenn Pins vorhanden) === */}
            {monitorPins.length>0?(<>
              <div className="iio-mon2-sec-head">{t("pins.angepinnt")} <span className="cnt">· {monitorPins.length}</span>
                <span className="right"><button className="iio-mon2-link" onClick={()=>setPinsHidden(p=>!p)}>{pinsHidden?t("pins.einblenden"):t("pins.ausblenden")}</button></span>
              </div>
              {!pinsHidden&&<div className={`iio-mon2-pin-region${eskModus==="spalte"?" hat-esk":""}`}><div className="iio-mon2-list" style={{margin:0,border:"none",borderRadius:0}}>{pinnedFlows.length?pinnedFlows.map(f=>renderFlowHead(f,true)):<div className="iio-mon2-empty">{t("pins.keineAktiv")}</div>}</div></div>}
              <div className="iio-mon2-sec-head">{t("liste.alleIflows")}</div>
            </>):(!pinHintDismissed&&sortedMonitorIflows.length>0&&(
              <div className="iio-mon2-pin-hint">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H19v-2l-3-2z"/></svg>
                {t("pins.hinweis")}
                <button className="x" onClick={()=>setPinHintDismissed(true)} title={t("gemeinsam:aktion.schliessen")}>✕</button>
              </div>
            ))}
            {/* === Spalten-Header === */}
            <div className={`iio-mon2-list${eskModus==="spalte"?" hat-esk":""}`}>
              <div className="iio-mon2-cols">
                <span/><span>iFlow</span>
                <span className="r">{t("kpi.fehler")}{eskModus==="fehler"&&<i className="plus"> + ESCALATED</i>}</span>
                {eskModus==="spalte"&&<span className="r">ESCALATED</span>}
                <span className="r">{t("reiter.bearbeitet")}</span><span className="r">{t("status.ok")}</span><span className="r">{t("liste.spalteZuletzt")}</span><span/><span/>
              </div>
              {sortedMonitorIflows.length===0?<div className="iio-mon2-empty">{t("liste.keineIflows")}</div>:
              sortedMonitorIflows.map(f=>renderFlowHead(f,false))}
            </div>
          </div>;
        })()}

        </>}{/* end monitorSubTab==="monitor" */}
      </>}

      {/* === PIPELINE ASSESSMENT TAB === */}
      {tab==="assessment"&&<PipelineAssessment instance={instance}/>}

      {/* === INTEGRATION CONTENT TAB (kept mounted to preserve state across tab switches) === */}
      <div style={{display:tab==="content"?"block":"none"}}>
        <IntegrationContent instance={instance} hiddenFlows={hiddenFlows} iflows={iflows} permissions={permissions} targetFlowName={localTargetFlow} onClearTarget={()=>{setLocalTargetFlow(null);onClearTarget&&onClearTarget();}} isVisible={tab==="content"}
          onFlowSelect={(name)=>{
            const p=window.location.pathname.split("/").filter(Boolean).slice(0,3);
            const base="/"+p.join("/");
            window.history.pushState({},document.title,name?base+"/content/"+encodeURIComponent(name):base+"/content");
          }}
        />
      </div>

      {/* === MESSAGE LOGS TAB (legacy, hidden from tabs but code preserved) === */}
      {tab==="logs"&&<>
        {loading&&<div style={{display:"flex",flexDirection:"column",gap:8}}>{[...Array(4)].map((_,i)=><div key={i} className="shimmer-bg" style={{height:56,borderRadius:10}}/>)}</div>}

        {view==="overview"&&!loading&&<>
          <div style={{marginBottom:16}}>
            <h2 style={{fontFamily:FONT,fontWeight:700,fontSize:22,color:"var(--text-1)",marginBottom:4}}>{t("protokoll.titel")}</h2>
            <p style={{color:v("text-3"),fontSize:13}}>{t("protokoll.anzahl",{sichtbar:iflows.filter(f=>!hiddenFlows.includes(f.Id)).length,gesamt:iflows.length})}{hiddenFlows.length>0?` ${t("protokoll.filterAktiv")}`:""}</p>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(min(340px,100%),1fr))",gap:16}}>
            {iflows.filter(f=>!hiddenFlows.includes(f.Id)).map((f,i)=><Card key={f.Id} onClick={()=>pickFlow(f)} style={{cursor:"pointer",animation:`fadeIn 0.3s ease ${i*.04}s both`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div style={{fontFamily:FONT,fontWeight:600,fontSize:14,color:"var(--text-1)",wordBreak:"break-all"}}>{f.Name}</div><SB status={f.Status}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,fontSize:12}}>
                <div><div style={{color:v("text-3"),fontFamily:FONT,fontSize:10}}>{t("protokoll.version")}</div><div style={{color:v("text-2"),fontFamily:FONT}}>{f.Version}</div></div>
                <div><div style={{color:v("text-3"),fontFamily:FONT,fontSize:10}}>{t("protokoll.deployedBy")}</div><div style={{color:v("text-2"),fontFamily:FONT}}>{f.DeployedBy}</div></div>
              </div>
              <div style={{marginTop:12,paddingTop:10,borderTop:`1px solid ${v("border")}`,fontSize:12,color:v("text-3")}}>{t("protokoll.messageLogs")} →</div>
            </Card>)}
          </div>
        </>}

        {view==="iflow"&&sel&&!loading&&<>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}><h2 style={{fontFamily:FONT,fontWeight:700,fontSize:20,color:"var(--text-1)"}}>{sel.Name}</h2><SB status={sel.Status}/></div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:16}}>
            {[{l:t("gruppen.gesamt"),v:st.total,c:"var(--accent)"},{l:t("kpi.erfolgreich"),v:st.ok,c:"var(--success)"},{l:t("protokoll.fehlerhaft"),v:st.fail,c:"var(--danger)"},{l:t("protokoll.verarbeitung"),v:st.proc,c:"var(--accent)"},{l:t("gruppen.retry"),v:st.retry,c:"var(--danger)"}].map((s,i)=>(
              <div key={i} style={{background:v("surface-1"),border:`1px solid ${v("border")}`,borderRadius:8,padding:"14px 16px"}}>
                <div style={{fontSize:10,color:v("text-3"),fontFamily:FONT,textTransform:"uppercase"}}>{s.l}</div>
                <div style={{fontSize:26,fontWeight:700,color:s.c,fontFamily:FONT,letterSpacing:"-1px"}}>{s.v}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap",alignItems:"center",padding:"12px 16px",background:v("surface-1"),border:`1px solid ${v("border")}`,borderRadius:8}}>
            <select value={sf} onChange={e=>setSf(e.target.value)} style={{background:v("surface-2"),border:`1px solid ${v("border")}`,borderRadius:6,padding:"6px 10px",color:v("text-1"),fontSize:12,fontFamily:FONT,outline:"none"}}><option value="ALL">{t("reiter.alle")}</option><option value="COMPLETED">{t("statusfilter.completed")}</option><option value="FAILED">{t("statusfilter.failed")}</option><option value="PROCESSING">{t("statusfilter.processing")}</option><option value="RETRY">{t("gruppen.retry")}</option></select>
            <select value={gb} onChange={e=>setGb(e.target.value)} style={{background:v("surface-2"),border:`1px solid ${v("border")}`,borderRadius:6,padding:"6px 10px",color:v("text-1"),fontSize:12,fontFamily:FONT,outline:"none"}}><option value="none">{t("protokoll.ohneGruppierung")}</option><option value="status">{t("pipeline.spalteStatus")}</option><option value="sender">{t("detail.sender")}</option><option value="receiver">{t("detail.receiver")}</option></select>
            <input placeholder={t("protokoll.suchePlatzhalter")} value={search} onChange={e=>setSearch(e.target.value)} style={{flex:1,minWidth:200,background:v("surface-2"),border:`1px solid ${v("border")}`,borderRadius:6,padding:"6px 12px",color:v("text-1"),fontSize:12,fontFamily:FONT,outline:"none"}}/>
            <span style={{fontSize:12,color:v("text-3"),fontFamily:FONT}}>{fl.length}</span>
          </div>
          {Object.entries(gr).map(([g,gl])=>(
            <div key={g} style={{marginBottom:20}}>
              {gb!=="none"&&<div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}><span style={{fontFamily:FONT,fontWeight:600,fontSize:14,color:"var(--text-1)"}}>{g}</span><span style={{background:v("surface-2"),border:`1px solid ${v("border")}`,borderRadius:4,padding:"2px 8px",fontSize:11,color:v("text-3"),fontFamily:FONT}}>{gl.length}</span><div style={{flex:1,height:1,background:v("border")}}/></div>}
              <div style={{background:v("surface-1"),border:`1px solid ${v("border")}`,borderRadius:8,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr",padding:"10px 16px",background:v("surface-0"),borderBottom:`1px solid ${v("border")}`,fontSize:10,color:v("text-3"),fontFamily:FONT,textTransform:"uppercase",letterSpacing:.8}}><div>GUID</div><div>{t("pipeline.spalteStatus")}</div><div>{t("treffer.spalteStart")}</div><div>{t("treffer.spalteDauer")}</div><div>{t("detail.sender")}</div><div>{t("detail.receiver")}</div></div>
                {gl.map((l,i)=><div key={l.MessageGuid}>
                  <div className="row-hover" onClick={()=>toggle(l.MessageGuid)} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr",padding:"11px 16px",borderBottom:i<gl.length-1?`1px solid ${v("border-subtle")}`:"none",alignItems:"center"}}>
                    <div style={{fontFamily:FONT,fontSize:12,color:v("text-2"),overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.MessageGuid}</div><div><SB status={l.Status}/></div>
                    <div style={{fontFamily:FONT,fontSize:11,color:v("text-3")}}>{fD(l.LogStart)}</div><div style={{fontFamily:FONT,fontSize:11,color:v("text-2")}}>{fDur(l.LogStart,l.LogEnd)}</div>
                    <div style={{fontFamily:FONT,fontSize:11,color:v("text-2")}}>{l.Sender||"-"}</div><div style={{fontFamily:FONT,fontSize:11,color:v("text-2")}}>{l.Receiver||"-"}</div>
                  </div>
                  {exp===l.MessageGuid&&<div style={{padding:"16px 20px",background:v("surface-0"),borderBottom:`1px solid ${v("border")}`,animation:"fadeIn 0.2s ease"}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                      <div><div style={{fontSize:11,color:v("text-3"),fontFamily:FONT,marginBottom:8,textTransform:"uppercase"}}>{t("protokoll.details")}</div>
                        <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:"4px 12px",fontSize:12}}>
                          {[["guid","GUID",l.MessageGuid],["corr",t("detail.correlation"),l.CorrelationId],["lvl",t("protokoll.level"),l.LogLevel],["start",t("treffer.spalteStart"),fD(l.LogStart)],["end",t("protokoll.ende"),fD(l.LogEnd)]].map(([kk,lbl,val])=>[<span key={kk+"l"} style={{color:v("text-3"),fontFamily:FONT}}>{lbl}:</span>,<span key={kk+"v"} style={{color:v("text-1"),fontFamily:FONT,wordBreak:"break-all"}}>{val||"-"}</span>])}
                        </div>
                        <div style={{marginTop:10,display:"flex",gap:8}}>
                          <a href={monitoringLink(toSuiteUrl(instance.tenantUrl,instance.suiteUrl),l.MessageGuid)} target="_blank" rel="noopener noreferrer"
                            style={{display:"inline-flex",alignItems:"center",gap:4,padding:"5px 12px",borderRadius:6,background:"var(--accent)",color:"#fff",fontSize:11,fontWeight:600,textDecoration:"none",fontFamily:FONT}}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            {t("protokoll.imCpiMonitorOeffnen")}</a>
                        </div>
                      </div>
                      <div><div style={{fontSize:11,color:v("text-3"),fontFamily:FONT,marginBottom:8,textTransform:"uppercase"}}>{t("detail.customHeaders")}</div>
                        <div style={{background:v("surface-1"),border:`1px solid ${v("border")}`,borderRadius:8,padding:"0 12px"}}>
                          {!hdrs[l.MessageGuid]?<div style={{color:v("text-3"),fontSize:12}}>{t("protokoll.laden")}</div>:hdrs[l.MessageGuid].length===0?<div style={{color:v("text-3"),fontSize:12}}>{t("protokoll.keine")}</div>:
                          hdrs[l.MessageGuid].map(h=><div key={h.Name} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${v("border-subtle")}`,fontSize:12}}><span style={{color:v("accent"),fontFamily:FONT,fontWeight:500}}>{h.Name}</span><span style={{color:v("text-1"),fontFamily:FONT}}>{h.Value}</span></div>)}
                        </div></div>
                    </div>
                  </div>}
                </div>)}
                {gl.length===0&&<div style={{padding:24,textAlign:"center",color:v("text-3")}}>{t("protokoll.keineNachrichten")}</div>}
              </div>
            </div>
          ))}
        </>}
      </>}
      {/* Kebab-Menü (body-Overlay, umgeht overflow:hidden der Liste) */}
      {kebabMenu&&(
        <div className="iio-mon2-kebab-menu" style={{left:kebabMenu.x,top:kebabMenu.y}} onClick={e=>e.stopPropagation()}>
          <button onClick={()=>{const f=kebabMenu.flowName;setKebabMenu(null);setLocalTargetFlow(f);setTab("content");}}>{t("aktion.iflowEinsehen")}</button>
          <button onClick={()=>{const f=kebabMenu.flowName;setKebabMenu(null);openDesignLink(f);}}>{t("aktion.imCockpit")}</button>
          <button onClick={()=>{const f=kebabMenu.flowName;setKebabMenu(null);togglePin(f);}}>{monitorPins.includes(kebabMenu.flowName)?t("pins.entfernen"):t("pins.anpinnen")}</button>
        </div>
      )}
      {/* Abhängigkeiten-Popup: alle Runs, die dieselbe Correlation-ID teilen, als Ketten-Timeline.
          Natives .iio-modal (KEIN MUI-Portal-Dialog: der verliert den .iio-Scope und damit die CI). */}
      {corrDeps&&(
        <div className="iio-modal-overlay is-open" onClick={e=>{if(e.target===e.currentTarget)setCorrDeps(null);}}>
          <div className="iio iio-modal" role="dialog" aria-modal="true" style={{maxWidth:680}}>
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("korrelation.kette")}</span>
                <h2>{t("korrelation.abhaengigkeiten")}</h2>
              </div>
              <button className="iio-modal-close" onClick={()=>setCorrDeps(null)} title={t("gemeinsam:aktion.schliessen")}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </header>
            <div className="iio-modal-body">
              <div style={{fontFamily:"var(--iio-font-mono)",fontSize:11,color:"var(--iio-text-3)",wordBreak:"break-all"}}>{corrDeps.cid}</div>
              <div style={{fontSize:12,color:"var(--iio-text-3)"}}>{t("korrelation.reihenfolge")}</div>
              {corrDeps.loading?<div style={{padding:"24px 0",textAlign:"center",color:"var(--iio-text-3)",fontSize:13}}>{t("korrelation.ladeKette")}</div>
              :corrDeps.error?<div style={{padding:"24px 0",textAlign:"center",color:"var(--iio-critical)",fontSize:13}}>{t("meldung.fehlerMit",{text:corrDeps.error})}</div>
              :(()=>{
                const flows=corrDeps.data?.iflows||[];
                const nameMap={};flows.forEach(x=>{nameMap[x.name]=x.displayName||x.name;});
                const chain=flows.flatMap(x=>x.messages||[]).sort((a,b)=>(parseOD(a.LogStart)||0)-(parseOD(b.LogStart)||0));
                if(chain.length===0)return <div style={{padding:"24px 0",textAlign:"center",color:"var(--iio-text-3)",fontSize:13}}>{t("korrelation.keineRuns")}</div>;
                const stCol=(s)=>s==="COMPLETED"?"var(--iio-success)":(s==="FAILED"||s==="ERROR")?"var(--iio-critical)":(s==="RETRY"||s==="ESCALATED")?"var(--iio-warning)":"var(--iio-text-3)";
                return <div>
                  {chain.map((m,mi)=>(
                    <div key={m.MessageGuid||mi} style={{display:"flex",gap:14}}>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"center",width:14,flexShrink:0}}>
                        <span style={{width:2,height:14,background:stCol(m.Status),marginTop:4,flexShrink:0}}/>
                        {mi<chain.length-1&&<span style={{flex:1,width:1,background:"var(--iio-border)",minHeight:16}}/>}
                      </div>
                      <div style={{paddingBottom:16,minWidth:0,flex:1}}>
                        <div style={{display:"flex",alignItems:"baseline",gap:10,flexWrap:"wrap"}}>
                          <span style={{fontFamily:"var(--iio-font-mono)",fontSize:10,fontWeight:600,letterSpacing:".1em",color:"var(--iio-text-3)"}}>{String(mi+1).padStart(2,"0")}</span>
                          <span style={{fontWeight:600,fontSize:13,color:"var(--iio-text-1)",wordBreak:"break-word"}}>{nameMap[m.IntegrationFlowName]||flowLabel(m.IntegrationFlowName)||"?"}</span>
                          <span style={{fontFamily:"var(--iio-font-mono)",fontSize:10,fontWeight:600,letterSpacing:".08em",color:stCol(m.Status)}}>{m.Status}</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:"var(--iio-text-3)",marginTop:3,flexWrap:"wrap"}}>
                          <span>{m.Sender||"-"}</span>
                          <ChevFlow variant="static" small/>
                          <span>{m.Receiver||"-"}</span>
                          <span style={{color:"var(--iio-text-4)"}}>·</span>
                          <span>{fD(m.LogStart)}</span>
                          <span style={{color:"var(--iio-text-4)"}}>·</span>
                          <span>{t("treffer.spalteDauer")} {fDur(m.LogStart,m.LogEnd)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>;
              })()}
            </div>
            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left">
                {!corrDeps.loading&&!corrDeps.error&&corrDeps.data&&(
                  <span style={{fontSize:12,color:"var(--iio-text-3)"}}>{t("korrelation.fuss",{runs:corrDeps.data.count,iflows:(corrDeps.data.iflows||[]).length,stunden:corrDeps.data.hours})}{corrDeps.data.capped?` · ${t("korrelation.auf1000")}`:""}</span>
                )}
              </div>
              <div className="iio-modal-foot-right">
                <button className="iio-btn iio-btn--secondary" onClick={()=>setCorrDeps(null)}>{t("gemeinsam:aktion.schliessen")}</button>
                <button className="iio-btn iio-btn--signal" disabled={corrDeps.loading||!!corrDeps.error||!(corrDeps.data?.count>0)} onClick={()=>{setCorrDeps(null);setMonitorSearchMode("iflow");setMonitorSearch(corrDeps.cid);setMonitorExpanded(null);}}>{t("korrelation.imMonitorFiltern")}</button>
              </div>
            </footer>
          </div>
        </div>
      )}
      {/* Inhalts-Betrachter (Payload, Header, Attachments).
          Natives .iio-modal, KEIN MUI-Dialog: der rendert per Portal ausserhalb des
          .iio-Scopes, verliert damit die Farbgebung und stellt Knoepfe nackt dar. Zuvor
          steckte zudem eine eigene Box IM Dialog-Papier - daher der doppelte Rahmen. */}
      {attachmentModal&&(()=>{
        const inhalt=attachmentModal.content||"";
        const typ=erkenneTyp(inhalt);
        const zeigeOriginal=attachmentModal.roh===true;
        const darstellung=zeigeOriginal?inhalt:formatiere(inhalt,typ);
        const formatierbar=typ.id==="xml"||typ.id==="json"||typ.id==="edifact";
        return (
        <div className="iio-modal-overlay is-open" onClick={e=>{if(e.target===e.currentTarget)setAttachmentModal(null);}}>
          <div className="iio iio-modal iio-payload-modal" role="dialog" aria-modal="true">
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{typ.label}</span>
                <h2>{attachmentModal.name}</h2>
              </div>
              <button className="iio-modal-close" onClick={()=>setAttachmentModal(null)} title={t("gemeinsam:aktion.schliessen")}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </header>
            <div className="iio-payload-bar">
              <span className="pl-meta">{t("inhalt.zeichen",{count:inhalt.length,formatiert:zahl(inhalt.length)})} · {t("inhalt.zeilen",{count:darstellung.split("\n").length,formatiert:zahl(darstellung.split("\n").length)})}</span>
              <div className="pl-actions">
                {formatierbar&&(
                  <div className="pl-seg" role="group" aria-label={t("inhalt.darstellung")}>
                    <button className={!zeigeOriginal?"active":""} onClick={()=>setAttachmentModal(m=>({...m,roh:false}))}>{t("inhalt.formatiert")}</button>
                    <button className={zeigeOriginal?"active":""} onClick={()=>setAttachmentModal(m=>({...m,roh:true}))}>{t("inhalt.original")}</button>
                  </div>
                )}
                <button className="btn btn--sm" onClick={()=>navigator.clipboard.writeText(inhalt)}>{t("inhalt.kopieren")}</button>
              </div>
            </div>
            <pre className="iio-payload-body" dangerouslySetInnerHTML={{__html:faerbe(darstellung,typ)}}/>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default Dashboard;
